import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { collection, doc, getDoc, getDocs, onSnapshot, query, where, writeBatch, serverTimestamp, runTransaction, orderBy, limit, Timestamp } from "firebase/firestore";
import { db } from "../firebaseConfig";
import { getAuth } from "firebase/auth";
import { useAuth } from "../auth/AuthProvider";
import { normalizeClaims } from "../auth/landing";
import PhotoEvidenceReview from "./PhotoEvidenceReview";
import PayrollOperationalVerificationQueue from "./PayrollOperationalVerificationQueue";

const VIEWS = [
  { id: "today", label: "Today" },
  { id: "builder", label: "Schedule Builder" },
  { id: "exceptions", label: "Exceptions" },
  { id: "leave", label: "Leave" },
  { id: "history", label: "History" },
  { id: "attendance", label: "Attendance Photos" },
];

const card = {
  borderWidth: "1px",
  borderStyle: "solid",
  borderColor: "rgba(96,165,250,.20)",
  background: "linear-gradient(145deg, rgba(15,37,65,.92), rgba(8,20,38,.96))",
  boxShadow: "0 18px 50px rgba(0,0,0,.18)",
};

const muted = { color: "rgba(226,232,240,.64)" };

function Pill({ children, tone = "blue" }) {
  const tones = {
    blue: ["rgba(56,189,248,.11)", "rgba(56,189,248,.32)", "#7dd3fc"],
    green: ["rgba(16,185,129,.11)", "rgba(16,185,129,.32)", "#6ee7b7"],
    amber: ["rgba(245,158,11,.10)", "rgba(245,158,11,.32)", "#fbbf24"],
    rose: ["rgba(244,63,94,.10)", "rgba(244,63,94,.32)", "#fda4af"],
    slate: ["rgba(148,163,184,.08)", "rgba(148,163,184,.22)", "#cbd5e1"],
  };
  const [background, borderColor, color] = tones[tone] || tones.blue;
  return (
    <span className="inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold" style={{ background, borderColor, color }}>
      {children}
    </span>
  );
}

function EmptyState({ title, body, badge }) {
  return (
    <div className="rounded-2xl border p-5 md:p-6" style={card}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-bold text-white">{title}</h3>
          <p className="mt-2 max-w-2xl text-sm" style={muted}>{body}</p>
        </div>
        {badge ? <Pill tone="amber">{badge}</Pill> : null}
      </div>
    </div>
  );
}

const pad = (v) => String(v).padStart(2, "0");
const isoDate = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

function monthDays(monthKey) {
  const [year, month] = String(monthKey).split("-").map(Number);
  if (!year || !month) return [];
  const count = new Date(year, month, 0).getDate();
  return Array.from({ length: count }, (_, index) => new Date(year, month - 1, index + 1, 12, 0, 0));
}

function formatMonth(monthKey) {
  const [year, month] = String(monthKey).split("-").map(Number);
  return new Date(year, month - 1, 1).toLocaleDateString("en-ZA", { month: "long", year: "numeric" });
}

function monthKeyFor(date = new Date()) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}`;
}

function addMonthsToKey(monthKey, offset = 1) {
  const [year, month] = String(monthKey).split("-").map(Number);
  const date = new Date(year, (month || 1) - 1 + offset, 1);
  return monthKeyFor(date);
}

function approvedOvertimeNote(before, after) {
  const beforeMatch = String(before || "").match(/^(\d{2}):(\d{2})-(\d{2}):(\d{2})$/);
  const afterMatch = String(after || "").match(/^(\d{2}):(\d{2})-(\d{2}):(\d{2})$/);
  if (!beforeMatch || !afterMatch) return "Approved overtime";

  const scheduledStart = Number(beforeMatch[1]) * 60 + Number(beforeMatch[2]);
  let scheduledEnd = Number(beforeMatch[3]) * 60 + Number(beforeMatch[4]);
  const actualStart = Number(afterMatch[1]) * 60 + Number(afterMatch[2]);
  let actualEnd = Number(afterMatch[3]) * 60 + Number(afterMatch[4]);

  if (scheduledEnd <= scheduledStart) scheduledEnd += 1440;
  if (actualEnd <= actualStart) actualEnd += 1440;

  const overtimeMinutes = Math.max(0, actualEnd - scheduledEnd);
  if (!overtimeMinutes) return "Approved overtime";

  const h = Math.floor(overtimeMinutes / 60);
  const m = overtimeMinutes % 60;
  const duration = [h ? `${h}H` : "", m ? `${m}M` : ""].filter(Boolean).join(" ");
  return `Approved overtime · ${duration}`;
}

function shiftHours(shift) {
  if (!shift || shift === "OFF") return 0;
  const match = String(shift).match(/^(\d{2}):(\d{2})-(\d{2}):(\d{2})$/);
  if (!match) return 0;
  const start = Number(match[1]) * 60 + Number(match[2]);
  let end = Number(match[3]) * 60 + Number(match[4]);
  if (end < start) end += 1440;
  return Math.max(0, (end - start) / 60);
}


const DEFAULT_ATTENDANCE_TYPES = [
  { id: "annual-leave", label: "Annual Leave", paid: true, allocateHours: true, defaultHours: 8, requiresApproval: true, showOnSchedule: true },
  { id: "sick-leave", label: "Sick Leave", paid: true, allocateHours: true, defaultHours: 8, requiresApproval: true, showOnSchedule: true },
  { id: "family-responsibility-leave", label: "Family Responsibility Leave", paid: true, allocateHours: true, defaultHours: 8, requiresApproval: true, showOnSchedule: true },
  { id: "birthday-leave", label: "Birthday Leave", paid: true, allocateHours: true, defaultHours: 8, requiresApproval: false, showOnSchedule: true },
  { id: "paid-leave", label: "Paid Leave", paid: true, allocateHours: true, defaultHours: 8, requiresApproval: true, showOnSchedule: true },
  { id: "unpaid-leave", label: "Unpaid Leave", paid: false, allocateHours: false, defaultHours: 0, requiresApproval: true, showOnSchedule: true },
  { id: "training", label: "Training", paid: true, allocateHours: true, defaultHours: 8, requiresApproval: false, showOnSchedule: true },
];


const LEAVE_TYPE_LABELS = {
  annual: "Annual Leave",
  sick: "Sick Leave",
  family_responsibility: "Family Responsibility Leave",
  unpaid: "Unpaid Leave",
  other: "Other / Manual",
};

const leaveTypeLabel = (value) => LEAVE_TYPE_LABELS[String(value || "").toLowerCase()] || "Leave";

// LR8 — Leave Balance ledger helpers. Current balance lives in one compact staff summary
// document; detailed transactions are only read when Admin explicitly opens the ledger.
const isLeaveAttendanceType = (row) => /leave/i.test(String(row?.id || "")) || /leave/i.test(String(row?.label || ""));
const numericDays = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? Math.round(n * 100) / 100 : 0;
};
const formatLeaveDays = (value) => {
  const n = numericDays(value);
  return `${Number.isInteger(n) ? n.toFixed(0) : n.toFixed(2).replace(/0$/, "")} day${Math.abs(n) === 1 ? "" : "s"}`;
};

// LR9.1 — Leave balance engine. Keep each source bucket explicit so Admin and later
// Staff Dashboard can explain where entitlement came from without replaying the ledger.
// available = opening + accrued + manual adjustments - booked - used.
// Historical ledger rows remain the audit trail and are not replayed on every screen load.
const normaliseLeaveBalanceEntry = (raw = {}) => {
  const openingBalanceDays = numericDays(raw.openingBalanceDays);
  const accruedDays = numericDays(raw.accruedDays);
  const manualAdjustmentDays = numericDays(raw.manualAdjustmentDays);
  const bookedDays = Math.max(0, numericDays(raw.bookedDays));
  const usedDays = Math.max(0, numericDays(raw.usedDays));
  const creditedDays = numericDays(openingBalanceDays + accruedDays + manualAdjustmentDays);
  const availableDays = numericDays(creditedDays - bookedDays - usedDays);
  return {
    ...raw,
    openingBalanceDays,
    accruedDays,
    manualAdjustmentDays,
    creditedDays,
    bookedDays,
    usedDays,
    availableDays,
  };
};

const leaveTsDate = (value) =>
  value?.toDate
    ? value.toDate()
    : value?.seconds
    ? new Date(value.seconds * 1000)
    : value
    ? new Date(value)
    : null;

const leaveDateKeys = (startDate, endDate) => {
  if (!startDate) return [];
  const start = new Date(`${startDate}T12:00:00`);
  const end = new Date(`${endDate || startDate}T12:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return [];
  const keys = [];
  const cursor = new Date(start);
  while (cursor <= end && keys.length < 370) {
    keys.push(`${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, "0")}-${String(cursor.getDate()).padStart(2, "0")}`);
    cursor.setDate(cursor.getDate() + 1);
  }
  return keys;
};

const attendanceTypeForLeave = (leaveType, attendanceTypes = []) => {
  const key = String(leaveType || "").toLowerCase();
  const wantedId =
    key === "annual" ? "annual-leave" :
    key === "sick" ? "sick-leave" :
    key === "family_responsibility" ? "family-responsibility-leave" :
    key === "unpaid" ? "unpaid-leave" :
    "paid-leave";

  const byId = attendanceTypes.find((row) => row.id === wantedId);
  if (byId) return byId;

  const wantedLabel = leaveTypeLabel(key).toLowerCase();
  const byLabel = attendanceTypes.find((row) => String(row.label || "").trim().toLowerCase() === wantedLabel);
  if (byLabel) return byLabel;

  if (key === "unpaid") {
    return attendanceTypes.find((row) => row.paid === false) || {
      id: "unpaid-leave", label: "Unpaid Leave", paid: false, allocateHours: false, defaultHours: 0, requiresApproval: true
    };
  }

  return attendanceTypes.find((row) => row.id === "paid-leave") ||
    attendanceTypes.find((row) => row.paid !== false && row.allocateHours === true) || {
      id: wantedId, label: leaveTypeLabel(key), paid: true, allocateHours: true, defaultHours: 8, requiresApproval: true
    };
};

const ATTENDANCE_PREFIX = "ATTENDANCE::";
const attendanceValue = (id, hours = null) => {
  const base = `${ATTENDANCE_PREFIX}${id}`;
  if (hours === null || hours === undefined || hours === "") return base;
  const numericHours = Math.max(0, Number(hours) || 0);
  return `${base}::${numericHours}`;
};
const attendancePartsFromValue = (value) => {
  const raw = String(value || "");
  if (!raw.startsWith(ATTENDANCE_PREFIX)) return { id: "", hours: null };
  const encoded = raw.slice(ATTENDANCE_PREFIX.length);
  const [id = "", rawHours] = encoded.split("::");
  const parsedHours = rawHours === undefined || rawHours === "" ? null : Number(rawHours);
  return { id, hours: Number.isFinite(parsedHours) && parsedHours >= 0 ? parsedHours : null };
};
const attendanceIdFromValue = (value) => attendancePartsFromValue(value).id;
const attendanceHoursFromValue = (value, attendanceTypes = []) => {
  const { id, hours } = attendancePartsFromValue(value);
  if (!id) return 0;
  const type = attendanceTypes.find((row) => row.id === id);
  if (!type?.allocateHours) return 0;
  return hours !== null ? Number(hours) : Number(type.defaultHours || 0);
};

function scheduleCellHours(value, attendanceTypes = [], lunchBreakMinutes = 0) {
  const attendanceId = attendanceIdFromValue(value);
  if (attendanceId) {
    return attendanceHoursFromValue(value, attendanceTypes);
  }

  const grossHours = shiftHours(value);
  if (!grossHours) return 0;
  const lunchHours = Math.max(0, Number(lunchBreakMinutes) || 0) / 60;
  return Math.max(0, grossHours - lunchHours);
}

function scheduledLunchHours(value, lunchBreakMinutes = 0) {
  if (!value || value === "OFF" || attendanceIdFromValue(value)) return 0;
  if (!shiftHours(value)) return 0;
  return Math.max(0, Number(lunchBreakMinutes) || 0) / 60;
}

function formatScheduleHours(value) {
  const hours = Math.max(0, Number(value) || 0);
  const rounded = Math.round(hours * 100) / 100;
  return `${Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(2).replace(/0$/, "")}h`;
}

function formatLunchBreak(value) {
  const totalMinutes = Math.round(Math.max(0, Number(value) || 0) * 60);
  if (!totalMinutes) return "0h";
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (!minutes) return `${hours}h`;
  return `${hours}:${String(minutes).padStart(2, "0")}`;
}


function firestoreSeconds(value) {
  if (!value) return null;
  if (typeof value === "number") return value > 1e12 ? Math.floor(value / 1000) : Math.floor(value);
  if (typeof value?.seconds === "number") return value.seconds;
  if (typeof value?.toDate === "function") return Math.floor(value.toDate().getTime() / 1000);
  if (value instanceof Date) return Math.floor(value.getTime() / 1000);
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : Math.floor(parsed.getTime() / 1000);
}

function localTodayKey() {
  const now = new Date();
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

function schedulePeriodRange(period = "today", nowValue = new Date()) {
  const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const addDays = (date, days) => { const next = new Date(date); next.setDate(next.getDate() + days); return next; };
  const startOfWeek = (date) => { const day = date.getDay(); return startOfDay(addDays(date, day === 0 ? -6 : 1 - day)); };
  const now = new Date(nowValue);
  const todayStart = startOfDay(now);
  const tomorrowStart = addDays(todayStart, 1);
  const thisWeekStart = startOfWeek(now);
  const nextWeekStart = addDays(thisWeekStart, 7);
  const lastWeekStart = addDays(thisWeekStart, -7);
  const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const nextMonthStart = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return period === "this-week" ? [thisWeekStart, nextWeekStart]
    : period === "last-week" ? [lastWeekStart, thisWeekStart]
    : period === "this-month" ? [thisMonthStart, nextMonthStart]
    : period === "last-month" ? [lastMonthStart, thisMonthStart]
    : [todayStart, tomorrowStart];
}

function secondsForLocalDateTime(dateKey, timeValue) {
  if (!dateKey || !timeValue) return null;
  const [year, month, day] = String(dateKey).split("-").map(Number);
  const [hours, minutes] = String(timeValue).split(":").map(Number);
  if (!year || !month || !day || Number.isNaN(hours) || Number.isNaN(minutes)) return null;
  return Math.floor(new Date(year, month - 1, day, hours, minutes, 0, 0).getTime() / 1000);
}

function compactDuration(totalMinutes) {
  const mins = Math.max(0, Math.round(Number(totalMinutes) || 0));
  const days = Math.floor(mins / 1440);
  const hours = Math.floor((mins % 1440) / 60);
  const minutes = mins % 60;
  const parts = [];
  if (days) parts.push(`${days}D`);
  if (hours) parts.push(`${hours}H`);
  if (minutes || !parts.length) parts.push(`${minutes}M`);
  return parts.join(" ");
}

function formatClock(seconds) {
  if (!seconds) return "—";
  return new Date(seconds * 1000).toLocaleTimeString("en-ZA", { hour: "2-digit", minute: "2-digit", hour12: false });
}

function ScheduleBuilderModal({
  open,
  onClose,
  stores,
  staff,
  roles,
  publicHolidays,
  attendanceTypes,
  lunchBreakMinutes = 60,
  initialStoreId,
  initialMonthKey,
  readOnly = false,
  orgId,
  actor,
  onSchedulePublished,
}) {
  const now = new Date();
  const [storeId, setStoreId] = useState(initialStoreId || "");
  const [monthKey, setMonthKey] = useState(initialMonthKey || monthKeyFor(now));
  const [draft, setDraft] = useState({});
  const [editing, setEditing] = useState(null);
  const [customStart, setCustomStart] = useState("08:00");
  const [customEnd, setCustomEnd] = useState("17:00");
  const [pendingAttendanceId, setPendingAttendanceId] = useState("");
  const [pendingAttendanceHours, setPendingAttendanceHours] = useState("");
  const [dirty, setDirty] = useState(false);
  const [loadedPublished, setLoadedPublished] = useState({});
  const [loadedDraft, setLoadedDraft] = useState({});
  const [dirtyKeys, setDirtyKeys] = useState(new Set());
  const [publishableKeys, setPublishableKeys] = useState(new Set());
  const draftRef = useRef({});
  const dirtyKeysRef = useRef(new Set());
  const autoSaveTimerRef = useRef(null);
  const [saveState, setSaveState] = useState("idle");
  const [saveError, setSaveError] = useState("");
  const [revisionConfirmOpen, setRevisionConfirmOpen] = useState(false);
  const [revisionReason, setRevisionReason] = useState("");
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
  const [recoverState, setRecoverState] = useState("idle");
  const [pdfExportState, setPdfExportState] = useState("idle");
  const [pdfExportError, setPdfExportError] = useState("");
  const [mobileStaffId, setMobileStaffId] = useState("");
  const [rulesByStaff, setRulesByStaff] = useState({});
  const [rulesModalPerson, setRulesModalPerson] = useState(null);
  const [ruleSaving, setRuleSaving] = useState(false);
  const [ruleSaveError, setRuleSaveError] = useState("");
  const [ruleScope, setRuleScope] = useState("whole_month");
  const [ruleWeek, setRuleWeek] = useState(1);
  const [ruleDate, setRuleDate] = useState("");
  const [ruleDays, setRuleDays] = useState(["mon", "tue", "wed", "thu", "fri", "sat", "sun"]);
  const [ruleAction, setRuleAction] = useState("shift");
  const [ruleShiftMode, setRuleShiftMode] = useState("preset");
  const [rulePreset, setRulePreset] = useState("08:00-17:00");
  const [ruleCustomStart, setRuleCustomStart] = useState("08:00");
  const [ruleCustomEnd, setRuleCustomEnd] = useState("17:00");
  const [editingRuleId, setEditingRuleId] = useState("");
  const [rulePreview, setRulePreview] = useState(null);
  const [ruleApplyMode, setRuleApplyMode] = useState("empty_only");
  const [ruleUndoState, setRuleUndoState] = useState("idle");
  const [ruleCopyState, setRuleCopyState] = useState("idle");
  const [ruleCopyMessage, setRuleCopyMessage] = useState("");
  const [ruleCopyConfirmOpen, setRuleCopyConfirmOpen] = useState(false);
  const [ruleDeleteConfirm, setRuleDeleteConfirm] = useState(null);
  const [ruleUndoConfirmOpen, setRuleUndoConfirmOpen] = useState(false);
  const desktopScheduleScrollRef = useRef(null);
  const mobileScheduleScrollRef = useRef(null);
  const todayDateKey = isoDate(new Date());
  const todayMonthKey = todayDateKey.slice(0, 7);

  useEffect(() => {
    if (!open || monthKey !== todayMonthKey) return undefined;

    const timer = setTimeout(() => {
      const scrollTodayIntoView = (container) => {
        if (!container) return;
        const target = container.querySelector(`[data-schedule-date="${todayDateKey}"]`);
        if (!target) return;

        const targetLeft = target.offsetLeft;
        const targetWidth = target.offsetWidth;
        const centeredLeft = targetLeft - ((container.clientWidth - targetWidth) / 2);
        const maxLeft = Math.max(0, container.scrollWidth - container.clientWidth);
        container.scrollLeft = Math.max(0, Math.min(maxLeft, centeredLeft));
      };

      scrollTodayIntoView(desktopScheduleScrollRef.current);
      scrollTodayIntoView(mobileScheduleScrollRef.current);
    }, 120);

    return () => clearTimeout(timer);
  }, [open, monthKey, todayDateKey, todayMonthKey, storeId]);

  useEffect(() => {
    if (!storeId && stores[0]?.id) setStoreId(stores[0].id);
  }, [stores, storeId]);

  useEffect(() => {
    if (initialStoreId) setStoreId(initialStoreId);
  }, [initialStoreId]);

  useEffect(() => {
    if (open && initialMonthKey) setMonthKey(initialMonthKey);
  }, [open, initialMonthKey]);

  useEffect(() => {
    // C7.4 — The builder component stays mounted even when its modal is closed.
    // Keep schedule-entry reads/listeners completely asleep until the workspace is visible.
    if (!open || !orgId || !storeId || !monthKey) return undefined;

    setSaveError("");
    const q = query(
      collection(db, "orgs", orgId, "neo_schedule_entries"),
      where("storeId", "==", storeId),
      where("monthKey", "==", monthKey)
    );

    return onSnapshot(
      q,
      (snap) => {
        const nextDraft = {};
        const nextPublished = {};
        let latestSaved = null;

        snap.docs.forEach((scheduleDoc) => {
          const data = scheduleDoc.data() || {};
          const key = `${storeId}__${data.staffId || ""}__${data.date || ""}`;
          if (!data.staffId || !data.date) return;

          const publishedShift = String(data.publishedShift || "");
          const draftShift =
            data.draftShift !== undefined && data.draftShift !== null
              ? String(data.draftShift)
              : publishedShift;

          nextDraft[key] = draftShift;
          nextPublished[key] = publishedShift;

          const ts = data.draftUpdatedAt || data.publishedAt || data.updatedAt;
          const millis = ts?.toMillis ? ts.toMillis() : 0;
          if (millis && (!latestSaved || millis > latestSaved)) latestSaved = millis;
        });

        // Do not destroy unsaved local edits when the snapshot refreshes.
        setDraft((current) => {
          const pendingKeys = dirtyKeysRef.current;
          if (!pendingKeys.size) {
            draftRef.current = nextDraft;
            return nextDraft;
          }
          const merged = { ...nextDraft };
          pendingKeys.forEach((key) => {
            if (Object.prototype.hasOwnProperty.call(current, key)) merged[key] = current[key];
          });
          draftRef.current = merged;
          return merged;
        });
        setLoadedDraft(nextDraft);
        setLoadedPublished(nextPublished);

        // Drafts created by Schedule Rules are autosaved to Firestore. After a
        // reload those cells can still differ from the published roster even
        // though there are no in-memory dirty keys. Reconstruct the publishable
        // set from the persisted draft-vs-published difference so OFF days (and
        // any other rule-applied changes) cannot silently disappear from PDF or
        // remain stuck as an unpublished draft.
        if (!dirtyKeysRef.current.size) {
          const persistedPublishable = new Set();
          Object.keys(nextDraft).forEach((key) => {
            if (String(nextDraft[key] || "") !== String(nextPublished[key] || "")) {
              persistedPublishable.add(key);
            }
          });
          setPublishableKeys(persistedPublishable);
          setDirty(persistedPublishable.size > 0);
          if (persistedPublishable.size > 0) setSaveState("idle");
        }
        if (latestSaved) setLastSavedAt(latestSaved);
      },
      (error) => {
        setSaveError(error?.message || "Could not load the saved schedule.");
      }
    );
  }, [open, orgId, storeId, monthKey]);

  useEffect(() => {
    // C7.4 — Staff rules are builder-only data. Do not subscribe while the modal is closed.
    if (!open || !orgId || !storeId || !monthKey) return undefined;

    const rulesQuery = query(
      collection(db, "orgs", orgId, "neo_schedule_staff_rules"),
      where("storeId", "==", storeId),
      where("monthKey", "==", monthKey)
    );

    return onSnapshot(
      rulesQuery,
      (snap) => {
        const next = {};
        snap.docs.forEach((ruleDoc) => {
          const data = ruleDoc.data() || {};
          if (!data.staffId) return;
          next[data.staffId] = Array.isArray(data.rules) ? data.rules : [];
        });
        setRulesByStaff(next);
      },
      (error) => setRuleSaveError(error?.message || "Could not load staff schedule rules.")
    );
  }, [open, orgId, storeId, monthKey]);

  const defaultPresets = [
    ["08:00-17:00", "Morning"],
    ["08:00-16:00", "Day"],
    ["08:00-15:00", "Sunday AM"],
    ["14:00-22:00", "Late"],
    ["14:00-23:00", "Late Fri/Sat"],
    ["14:00-21:00", "Sunday PM"],
    ["OFF", "Off day"],
  ];

  const [presets, setPresets] = useState(defaultPresets);
  const [newPresetLabel, setNewPresetLabel] = useState("");
  const [newPresetStart, setNewPresetStart] = useState("09:00");
  const [newPresetEnd, setNewPresetEnd] = useState("17:00");

  useEffect(() => {
    try {
      const key = `neo_schedule_shift_presets_${storeId || "default"}`;
      const saved = JSON.parse(localStorage.getItem(key) || "null");
      setPresets(Array.isArray(saved) && saved.length ? saved : defaultPresets);
    } catch {
      setPresets(defaultPresets);
    }
  }, [storeId]);

  const persistPresets = (next) => {
    setPresets(next);
    try {
      const key = `neo_schedule_shift_presets_${storeId || "default"}`;
      localStorage.setItem(key, JSON.stringify(next));
    } catch {}
  };

  const addPreset = () => {
    if (!newPresetStart || !newPresetEnd) return;
    const value = `${newPresetStart}-${newPresetEnd}`;
    if (presets.some(([existing]) => existing === value)) return;

    const next = [
      ...presets.filter(([existing]) => existing !== "OFF"),
      [value, newPresetLabel.trim() || "Custom"],
      ["OFF", "Off day"],
    ];
    persistPresets(next);
    setNewPresetLabel("");
  };

  const removePreset = (value) => {
    if (value === "OFF") return;
    persistPresets(presets.filter(([existing]) => existing !== value));
  };

  useEffect(() => {
    if (!open) return;
    requestAnimationFrame(() => {
      try {
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      } catch (e) {}
    });
  }, [open]);

  if (!open) return null;

  const roleById = Object.fromEntries((roles || []).map((role) => [role.id, role]));
  const selectedStore = stores.find((store) => store.id === storeId);
  const storeStaff = staff
    .filter((person) => person.storeId === storeId && !person.archived && !person.isArchived)
    .sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
  const mobileStaff =
    storeStaff.find((person) => person.id === mobileStaffId) || storeStaff[0] || null;
  const days = monthDays(monthKey);
  const holidaysThisMonth = new Set(
    Array.from(publicHolidays).filter((dateKey) => String(dateKey).startsWith(`${monthKey}-`))
  );

  const getShift = (staffId, dateKey) => draft[`${storeId}__${staffId}__${dateKey}`] || "";
  const setShift = (staffId, dateKey, value) => {
    if (readOnly) return;
    const key = `${storeId}__${staffId}__${dateKey}`;
    setDraft((current) => {
      const next = { ...current, [key]: value };
      draftRef.current = next;
      return next;
    });
    setDirtyKeys((current) => {
      const next = new Set(current);
      next.add(key);
      dirtyKeysRef.current = next;
      return next;
    });
    setPublishableKeys((current) => {
      const next = new Set(current);
      next.add(key);
      return next;
    });
    setDirty(true);
    setSaveState("idle");
    setSaveError("");
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      saveDraft();
    }, 450);
  };

  const entryRefFor = (staffId, dateKey) =>
    doc(db, "orgs", orgId, "neo_schedule_entries", `${storeId}__${staffId}__${dateKey}`);

  const buildEntryPayload = (staffId, dateKey, shiftValue) => {
    const person = staff.find((item) => item.id === staffId) || {};
    const role = roles.find((item) => item.id === person.roleId) || {};
    const store = stores.find((item) => item.id === storeId) || {};
    const attendanceTypeId = attendanceIdFromValue(shiftValue);
    const attendanceType = attendanceTypes.find((item) => item.id === attendanceTypeId) || null;
    const isAttendance = !!attendanceTypeId;
    const off = shiftValue === "OFF" || (!shiftValue && !isAttendance);
    const [startTime = "", endTime = ""] =
      !off && !isAttendance && String(shiftValue).includes("-") ? String(shiftValue).split("-") : ["", ""];

    return {
      orgId,
      storeId,
      storeName: store.name || "",
      staffId,
      staffName: person.name || "",
      roleId: person.roleId || "",
      roleName: role.name || "",
      date: dateKey,
      monthKey,
      shift: shiftValue || "",
      off,
      startTime,
      endTime,
      scheduledGrossHours: !off && !isAttendance ? shiftHours(shiftValue) : 0,
      lunchBreakMinutes: !off && !isAttendance ? Math.max(0, Number(lunchBreakMinutes) || 0) : 0,
      scheduledNetHours: scheduleCellHours(shiftValue, attendanceTypes, lunchBreakMinutes),
      isAttendance,
      attendanceTypeId: attendanceType?.id || "",
      attendanceLabel: attendanceType?.label || "",
      attendancePaid: attendanceType ? attendanceType.paid !== false : false,
      attendanceAllocateHours: attendanceType?.allocateHours === true,
      attendanceHours: attendanceType?.allocateHours ? attendanceHoursFromValue(shiftValue, attendanceTypes) : 0,
      attendanceRequiresApproval: attendanceType?.requiresApproval === true,
      source: "schedule-builder",
      updatedAt: serverTimestamp(),
      updatedByUid: actor?.uid || "",
      updatedByEmail: actor?.email || "",
      updatedByName: actor?.name || "",
    };
  };

  const saveDraft = async () => {
    if (!orgId || !storeId || !dirtyKeysRef.current.size) return;
    const keysToSave = Array.from(dirtyKeysRef.current);
    const valuesToSave = Object.fromEntries(keysToSave.map((key) => [key, draftRef.current[key] || ""]));
    setSaveState("saving");
    setSaveError("");

    try {
      const batch = writeBatch(db);
      keysToSave.forEach((key) => {
        const parts = key.split("__");
        const staffId = parts[1];
        const dateKey = parts[2];
        const shiftValue = valuesToSave[key] || "";
        batch.set(
          entryRefFor(staffId, dateKey),
          {
            ...buildEntryPayload(staffId, dateKey, shiftValue),
            draftShift: shiftValue,
            draftSavedAt: serverTimestamp(),
            draftUpdatedAt: serverTimestamp(),
            draftStatus: "draft",
          },
          { merge: true }
        );
      });

      const monthRef = doc(db, "orgs", orgId, "neo_schedule_months", `${storeId}__${monthKey}`);
      batch.set(
        monthRef,
        {
          orgId,
          storeId,
          storeName: selectedStore?.name || "",
          monthKey,
          status: "draft",
          draftUpdatedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          updatedByUid: actor?.uid || "",
          updatedByEmail: actor?.email || "",
          updatedByName: actor?.name || "",
        },
        { merge: true }
      );

      await batch.commit();
      setDirtyKeys((current) => {
        const next = new Set(current);
        keysToSave.forEach((key) => {
          if ((draftRef.current[key] || "") === valuesToSave[key]) next.delete(key);
        });
        dirtyKeysRef.current = next;
        setDirty(next.size > 0);
        return next;
      });
      setSaveState("saved");
      setLastSavedAt(Date.now());
    } catch (error) {
      setSaveState("error");
      setSaveError(error?.message || "Could not auto-save schedule draft.");
    }
  };


  const clearSchedule = async () => {
    if (readOnly || !orgId || !storeId) return;
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    const snapshot = [];
    storeStaff.forEach((person) => {
      days.forEach((day) => {
        const dateKey = isoDate(day);
        const key = `${storeId}__${person.id}__${dateKey}`;
        const value = draft[key] || "";
        if (!value) return;
        snapshot.push({
          staffId: person.id,
          staffName: person.name || "",
          date: dateKey,
          value,
        });
      });
    });

    if (!snapshot.length) {
      setClearConfirmOpen(false);
      return;
    }

    setSaveState("saving");
    setSaveError("");
    try {
      const batch = writeBatch(db);
      snapshot.forEach((row) => {
        batch.set(
          entryRefFor(row.staffId, row.date),
          {
            ...buildEntryPayload(row.staffId, row.date, ""),
            draftShift: "",
            draftSavedAt: serverTimestamp(),
            draftUpdatedAt: serverTimestamp(),
            draftStatus: "draft",
          },
          { merge: true }
        );
      });

      const monthRef = doc(db, "orgs", orgId, "neo_schedule_months", `${storeId}__${monthKey}`);
      batch.set(monthRef, {
        orgId,
        storeId,
        storeName: selectedStore?.name || "",
        monthKey,
        status: "draft",
        draftUpdatedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        updatedByUid: actor?.uid || "",
        updatedByEmail: actor?.email || "",
        updatedByName: actor?.name || "",
      }, { merge: true });

      const historyRef = doc(collection(db, "orgs", orgId, "neo_schedule_history"));
      batch.set(historyRef, {
        orgId,
        storeId,
        storeName: selectedStore?.name || "",
        monthKey,
        action: "clear_schedule",
        changeCount: snapshot.length,
        changes: snapshot.map((row) => ({
          staffId: row.staffId,
          staffName: row.staffName,
          date: row.date,
          before: row.value,
          after: "",
          reason: "Schedule cleared",
        })),
        recoverableDraft: snapshot,
        createdAt: serverTimestamp(),
        createdByUid: actor?.uid || "",
        createdByEmail: actor?.email || "",
        createdByName: actor?.name || "",
      });

      await batch.commit();
      const nextDraft = { ...draft };
      const nextDirty = new Set();
      const nextPublishable = new Set(publishableKeys);
      snapshot.forEach((row) => {
        const key = `${storeId}__${row.staffId}__${row.date}`;
        nextDraft[key] = "";
        nextPublishable.add(key);
      });
      draftRef.current = nextDraft;
      setDraft(nextDraft);
      setLoadedDraft(nextDraft);
      dirtyKeysRef.current = nextDirty;
      setDirtyKeys(nextDirty);
      setPublishableKeys(nextPublishable);
      setDirty(false);
      setSaveState("saved");
      setLastSavedAt(Date.now());
      setClearConfirmOpen(false);
    } catch (error) {
      setSaveState("error");
      setSaveError(error?.message || "Could not clear the schedule.");
    }
  };

  const recoverLastClearedDraft = async () => {
    if (readOnly || !orgId || !storeId) return;
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    setRecoverState("loading");
    setSaveError("");
    try {
      const snap = await getDocs(query(
        collection(db, "orgs", orgId, "neo_schedule_history"),
        where("action", "==", "clear_schedule")
      ));
      const candidates = snap.docs
        .map((d) => ({ id: d.id, ...(d.data() || {}) }))
        .filter((row) => row.action === "clear_schedule" && row.storeId === storeId && row.monthKey === monthKey && Array.isArray(row.recoverableDraft) && row.recoverableDraft.length)
        .sort((a, b) => {
          const am = a.createdAt?.toMillis?.() || (a.createdAt?.seconds ? a.createdAt.seconds * 1000 : 0);
          const bm = b.createdAt?.toMillis?.() || (b.createdAt?.seconds ? b.createdAt.seconds * 1000 : 0);
          return bm - am;
        });
      const latest = candidates[0];
      if (!latest) {
        setRecoverState("empty");
        setSaveError("No cleared draft is available to recover for this schedule.");
        return;
      }

      const batch = writeBatch(db);
      const nextDraft = { ...draft };
      const nextPublishable = new Set(publishableKeys);
      latest.recoverableDraft.forEach((row) => {
        const key = `${storeId}__${row.staffId}__${row.date}`;
        const value = String(row.value || "");
        nextDraft[key] = value;
        nextPublishable.add(key);
        batch.set(
          entryRefFor(row.staffId, row.date),
          {
            ...buildEntryPayload(row.staffId, row.date, value),
            draftShift: value,
            draftSavedAt: serverTimestamp(),
            draftUpdatedAt: serverTimestamp(),
            draftStatus: "draft",
          },
          { merge: true }
        );
      });

      const historyRef = doc(collection(db, "orgs", orgId, "neo_schedule_history"));
      batch.set(historyRef, {
        orgId,
        storeId,
        storeName: selectedStore?.name || "",
        monthKey,
        action: "recover_cleared_schedule",
        changeCount: latest.recoverableDraft.length,
        changes: latest.recoverableDraft.map((row) => ({
          staffId: row.staffId,
          staffName: row.staffName || "",
          date: row.date,
          before: "",
          after: String(row.value || ""),
          reason: "Recovered cleared draft",
        })),
        recoveredFromHistoryId: latest.id,
        createdAt: serverTimestamp(),
        createdByUid: actor?.uid || "",
        createdByEmail: actor?.email || "",
        createdByName: actor?.name || "",
      });

      await batch.commit();
      draftRef.current = nextDraft;
      setDraft(nextDraft);
      setLoadedDraft(nextDraft);
      dirtyKeysRef.current = new Set();
      setDirtyKeys(new Set());
      setPublishableKeys(nextPublishable);
      setDirty(false);
      setSaveState("saved");
      setRecoverState("recovered");
      setLastSavedAt(Date.now());
    } catch (error) {
      setRecoverState("error");
      setSaveError(error?.message || "Could not recover the cleared draft.");
    }
  };

  const publishChanges = async (reason = "") => {
    if (!orgId || !storeId || !publishableKeys.size) return;
    const revisionReasonText = String(reason || "").trim();
    setSaveState("publishing");
    setSaveError("");

    try {
      const batch = writeBatch(db);
      const changedEntries = [];

      publishableKeys.forEach((key) => {
        const parts = key.split("__");
        const staffId = parts[1];
        const dateKey = parts[2];
        const shiftValue = draft[key] || "";
        const previousPublished = loadedPublished[key] || "";

        const person = staff.find((row) => row.id === staffId) || {};
        const isRevision = !!previousPublished && previousPublished !== shiftValue;

        changedEntries.push({
          staffId,
          staffName: person.name || "",
          date: dateKey,
          before: previousPublished,
          after: shiftValue,
          originalSchedule: previousPublished,
          revisedSchedule: shiftValue,
          revision: isRevision,
          reason: isRevision ? revisionReasonText : "",
          changedByUid: actor?.uid || "",
          changedByEmail: actor?.email || "",
          changedByName: actor?.name || "",
        });

        batch.set(
          entryRefFor(staffId, dateKey),
          {
            ...buildEntryPayload(staffId, dateKey, shiftValue),
            draftShift: shiftValue,
            publishedShift: shiftValue,
            draftStatus: "published",
            published: true,
            publishedAt: serverTimestamp(),
            publishedByUid: actor?.uid || "",
            publishedByEmail: actor?.email || "",
            publishedByName: actor?.name || "",
            previousPublishedShift: previousPublished,
            revisionReason: previousPublished && previousPublished !== shiftValue ? revisionReasonText : "",
            revisedAt: previousPublished && previousPublished !== shiftValue ? serverTimestamp() : null,
            revisedByUid: previousPublished && previousPublished !== shiftValue ? (actor?.uid || "") : "",
            revisedByEmail: previousPublished && previousPublished !== shiftValue ? (actor?.email || "") : "",
            revisedByName: previousPublished && previousPublished !== shiftValue ? (actor?.name || "") : "",
          },
          { merge: true }
        );
      });

      const monthRef = doc(db, "orgs", orgId, "neo_schedule_months", `${storeId}__${monthKey}`);
      batch.set(
        monthRef,
        {
          orgId,
          storeId,
          storeName: selectedStore?.name || "",
          monthKey,
          status: "published",
          publishedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          publishedByUid: actor?.uid || "",
          publishedByEmail: actor?.email || "",
          publishedByName: actor?.name || "",
        },
        { merge: true }
      );

      const historyRef = doc(collection(db, "orgs", orgId, "neo_schedule_history"));
      batch.set(historyRef, {
        orgId,
        storeId,
        storeName: selectedStore?.name || "",
        monthKey,
        action: changedEntries.some((row) => row.revision) ? "revise_published_schedule" : "publish_changes",
        changeCount: changedEntries.length,
        changes: changedEntries,
        revisionReason: revisionReasonText,
        createdAt: serverTimestamp(),
        createdByUid: actor?.uid || "",
        createdByEmail: actor?.email || "",
        createdByName: actor?.name || "",
      });

      await batch.commit();

      const nextPublished = { ...loadedPublished };
      publishableKeys.forEach((key) => {
        nextPublished[key] = draft[key] || "";
      });
      setLoadedPublished(nextPublished);
      setDirtyKeys(new Set());
      setPublishableKeys(new Set());
      setDirty(false);
      setSaveState("published");
      setLastSavedAt(Date.now());
      setRevisionConfirmOpen(false);
      setRevisionReason("");

      // ScheduleBuilderModal owns loadedPublished, while the historical
      // exception loader lives in the parent Schedule Intelligence view.
      // Notify the parent only after a successful publish so unresolved
      // exceptions are reloaded against the newly published roster.
      if (typeof onSchedulePublished === "function") {
        onSchedulePublished();
      }
    } catch (error) {
      setSaveState("error");
      setSaveError(error?.message || "Could not publish schedule changes.");
    }
  };


  const requestPublishChanges = () => {
    if (!publishableKeys.size || saveState === "saving" || saveState === "publishing") return;
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);

    const revisedPublishedKeys = Array.from(publishableKeys).filter((key) => {
      const before = loadedPublished[key] || "";
      const after = draft[key] || "";
      return !!before && before !== after;
    });

    if (revisedPublishedKeys.length) {
      setRevisionReason("");
      setRevisionConfirmOpen(true);
      return;
    }

    publishChanges("");
  };

  const publishedValueFor = (staffId, dateKey) =>
    loadedPublished[`${storeId}__${staffId}__${dateKey}`] || "";

  const pdfCellLabel = (value) => {
    if (!value) return "";
    if (value === "OFF") return "OFF";

    const attendanceId = attendanceIdFromValue(value);
    if (attendanceId) {
      const type = attendanceTypes.find((row) => row.id === attendanceId);
      const label = String(type?.label || "Attendance");
      const words = label.split(/\s+/).filter(Boolean);
      if (words.length === 1) return words[0].slice(0, 8).toUpperCase();
      return words.map((word) => word.slice(0, 1)).join("").slice(0, 5).toUpperCase();
    }

    const match = String(value).match(/^(\d{2}):(\d{2})-(\d{2}):(\d{2})$/);
    if (match) return `${match[1]}:${match[2]}\n${match[3]}:${match[4]}`;
    return String(value).slice(0, 10);
  };

  const exportPublishedSchedulePdf = async ({ printAfter = false } = {}) => {
    if (!selectedStore || !monthKey) return;

    // PDF/Print are published-roster exports. Rules update the draft first.
    // Do not silently export the previous published roster while rule-generated
    // OFF days (or any other schedule edits) are still waiting to be published.
    if (publishableKeys.size > 0) {
      setPdfExportState("error");
      setPdfExportError(
        `There ${publishableKeys.size === 1 ? "is" : "are"} ${publishableKeys.size} unpublished schedule change${publishableKeys.size === 1 ? "" : "s"}. Publish Changes first, then export the PDF.`
      );
      return;
    }

    const printWindow = printAfter ? window.open("", "_blank") : null;
    if (printAfter && !printWindow) {
      setPdfExportError("Could not open the print preview. Please allow pop-ups for Neo and try again.");
      return;
    }
    if (printWindow) {
      printWindow.document.write(
        '<!doctype html><html><head><title>Preparing schedule…</title></head>' +
        '<body style="font-family:Arial,sans-serif;padding:24px;background:#fff;color:#111">' +
        '<h3>Preparing schedule for print…</h3><p>Please wait.</p></body></html>'
      );
      printWindow.document.close();
    }

    const hasPublishedSchedule = staff.some((person) =>
      days.some((day) => !!publishedValueFor(person.id, isoDate(day)))
    );

    if (!hasPublishedSchedule) {
      if (printWindow) printWindow.close();
      setPdfExportError("Publish at least one schedule entry before exporting the roster.");
      return;
    }

    setPdfExportState(printAfter ? "printing" : "exporting");
    setPdfExportError("");

    try {
      const pdf = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
        compress: true,
      });

      const monthLabel = formatMonth(monthKey);
      const storeLabel = selectedStore.name || "Store";
      const generatedLabel = new Date().toLocaleString("en-ZA", {
        dateStyle: "medium",
        timeStyle: "short",
      });

      // PDF must follow the exact same staff ordering as the Schedule Builder.
      // `storeStaff` is the builder's ordered list; using the raw `staff` prop here
      // allowed Firestore/input order to leak into the exported roster.
      const visibleStaff = storeStaff.filter((person) =>
        days.some((day) => !!publishedValueFor(person.id, isoDate(day)))
      );
      const rosterStaff = visibleStaff.length ? visibleStaff : storeStaff;

      const chunks = [];
      for (let index = 0; index < days.length; index += 16) {
        chunks.push(days.slice(index, index + 16));
      }

      const pdfAttendancePalette = [
        { fill: [226, 232, 255], text: [49, 46, 129] },
        { fill: [224, 242, 254], text: [12, 74, 110] },
        { fill: [220, 252, 231], text: [22, 101, 52] },
        { fill: [254, 249, 195], text: [113, 63, 18] },
        { fill: [255, 228, 230], text: [159, 18, 57] },
        { fill: [243, 232, 255], text: [107, 33, 168] },
        { fill: [204, 251, 241], text: [17, 94, 89] },
        { fill: [255, 237, 213], text: [154, 52, 18] },
      ];

      const attendancePdfStyle = (attendanceId) => {
        const index = Math.max(
          0,
          attendanceTypes.findIndex((row) => row.id === attendanceId)
        );
        return pdfAttendancePalette[index % pdfAttendancePalette.length];
      };

      const usedAttendanceIds = new Set();
      let hasOffInPdf = false;

      rosterStaff.forEach((person) => {
        days.forEach((day) => {
          const value = publishedValueFor(person.id, isoDate(day));
          if (value === "OFF") hasOffInPdf = true;
          const attendanceId = attendanceIdFromValue(value);
          if (attendanceId) usedAttendanceIds.add(attendanceId);
        });
      });

      const hasPublicHolidayInPdf = days.some((day) => publicHolidays.has(isoDate(day)));
      const hasSundayInPdf = days.some((day) => day.getDay() === 0);

      const pdfLegendItems = [];
      if (hasSundayInPdf) {
        pdfLegendItems.push({
          label: "Sunday",
          fill: [58, 44, 32],
          text: [255, 225, 157],
        });
      }
      if (hasPublicHolidayInPdf) {
        pdfLegendItems.push({
          label: "Public Holiday",
          fill: [86, 27, 57],
          text: [255, 232, 240],
        });
      }
      if (hasOffInPdf) {
        pdfLegendItems.push({
          label: "OFF",
          fill: [255, 248, 230],
          text: [110, 72, 0],
        });
      }

      attendanceTypes.forEach((type) => {
        if (!usedAttendanceIds.has(type.id)) return;
        const style = attendancePdfStyle(type.id);
        pdfLegendItems.push({
          label: type.label || "Attendance",
          fill: style.fill,
          text: style.text,
        });
      });

      const drawPdfLegend = (startY = 27) => {
        if (!pdfLegendItems.length) return startY;

        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(6.5);
        pdf.setTextColor(70, 70, 70);
        pdf.text("Legend", 12, startY + 2.4);

        let x = 25;
        let y = startY;
        const rightEdge = 285;

        pdfLegendItems.forEach((item) => {
          pdf.setFont("helvetica", "normal");
          pdf.setFontSize(6.5);
          const labelWidth = pdf.getTextWidth(item.label);
          const itemWidth = 5 + labelWidth + 6;

          if (x + itemWidth > rightEdge) {
            x = 12;
            y += 6;
          }

          pdf.setFillColor(...item.fill);
          pdf.setDrawColor(190, 190, 190);
          pdf.roundedRect(x, y, 4, 3.6, 0.5, 0.5, "FD");

          pdf.setTextColor(55, 55, 55);
          pdf.text(item.label, x + 5.5, y + 2.7);
          x += itemWidth;
        });

        return y + 5;
      };

      const drawSchedulePageHeader = () => {
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(16);
        pdf.setTextColor(20, 20, 20);
        pdf.text(`${storeLabel} - Staff Schedule`, 12, 12);

        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(9);
        pdf.text(`${monthLabel} | Published roster`, 12, 18);
        pdf.setFontSize(7.5);
        pdf.text(`Generated ${generatedLabel}`, 12, 23);

        const legendBottomY = drawPdfLegend(27);
        return legendBottomY + 3;
      };

      let currentPageTableTop = drawSchedulePageHeader();

      chunks.forEach((chunk, chunkIndex) => {
        const pageHeight = pdf.internal.pageSize.getHeight();
        const footerY = pageHeight - 9;
        const footerReserve = 17;
        const tableGap = 9;
        const estimatedRowHeight = 10.5;
        const estimatedTableHeight = 11 + (rosterStaff.length * estimatedRowHeight);

        let tableStartY = currentPageTableTop;

        if (chunkIndex > 0) {
          const previousFinalY = pdf.lastAutoTable?.finalY || currentPageTableTop;
          const candidateStartY = previousFinalY + tableGap;

          if (candidateStartY + estimatedTableHeight <= pageHeight - footerReserve) {
            tableStartY = candidateStartY;
          } else {
            pdf.addPage("a4", "landscape");
            currentPageTableTop = drawSchedulePageHeader();
            tableStartY = currentPageTableTop;
          }
        }

        const startDay = chunk[0]?.getDate();
        const endDay = chunk[chunk.length - 1]?.getDate();

        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(8);
        pdf.setTextColor(30, 30, 30);
        pdf.text(`Days ${startDay}-${endDay}`, 285, tableStartY - 3, { align: "right" });

        const head = [
          ["Staff", ...chunk.map((day) => {
            const dow = day.toLocaleDateString("en-ZA", { weekday: "short" }).slice(0, 2).toUpperCase();
            return `${dow}\n${day.getDate()}`;
          }), "Lunch", "Hours"],
        ];

        const body = rosterStaff.map((person) => {
          const role = roles.find((item) => item.id === person.roleId);
          const staffLabel = role?.name
            ? `${person.name}\n${role.name}`
            : person.name;

          const publishedLunchHours = chunk.reduce(
            (sum, day) =>
              sum + scheduledLunchHours(publishedValueFor(person.id, isoDate(day)), lunchBreakMinutes),
            0
          );
          const publishedNetHours = chunk.reduce(
            (sum, day) =>
              sum +
              scheduleCellHours(
                publishedValueFor(person.id, isoDate(day)),
                attendanceTypes,
                lunchBreakMinutes
              ),
            0
          );

          return [
            staffLabel,
            ...chunk.map((day) => pdfCellLabel(publishedValueFor(person.id, isoDate(day)))),
            formatLunchBreak(publishedLunchHours),
            formatScheduleHours(publishedNetHours),
          ];
        });

        autoTable(pdf, {
          startY: tableStartY,
          head,
          body,
          theme: "grid",
          styles: {
            font: "helvetica",
            fontSize: 6.5,
            cellPadding: 1.15,
            halign: "center",
            valign: "middle",
            lineWidth: 0.12,
            overflow: "linebreak",
            minCellHeight: 9,
          },
          headStyles: {
            fillColor: [12, 28, 49],
            textColor: [255, 255, 255],
            fontStyle: "bold",
            fontSize: 6.5,
          },
          columnStyles: {
            0: {
              halign: "left",
              fontStyle: "bold",
              cellWidth: 33,
              fontSize: 7,
            },
          },
          didParseCell: (data) => {
            if (
              data.section === "head" &&
              data.column.index > 0 &&
              data.column.index <= chunk.length
            ) {
              const day = chunk[data.column.index - 1];
              if (!day) return;
              const dateKey = isoDate(day);
              const isSunday = day.getDay() === 0;
              const isHoliday = publicHolidays.has(dateKey);
              if (isHoliday) {
                data.cell.styles.fillColor = [86, 27, 57];
                data.cell.styles.textColor = [255, 232, 240];
              } else if (isSunday) {
                data.cell.styles.fillColor = [58, 44, 32];
                data.cell.styles.textColor = [255, 225, 157];
              }
            }

            if (
              data.section === "body" &&
              data.column.index > 0 &&
              data.column.index <= chunk.length
            ) {
              const raw = String(data.cell.raw || "");
              const person = rosterStaff[data.row.index];
              const day = chunk[data.column.index - 1];
              const originalValue =
                person && day
                  ? publishedValueFor(person.id, isoDate(day))
                  : "";
              const attendanceId = attendanceIdFromValue(originalValue);

              if (originalValue === "OFF" || raw === "OFF") {
                data.cell.styles.fillColor = [255, 248, 230];
                data.cell.styles.textColor = [110, 72, 0];
                data.cell.styles.fontStyle = "bold";
              } else if (attendanceId) {
                const attendanceStyle = attendancePdfStyle(attendanceId);
                data.cell.styles.fillColor = attendanceStyle.fill;
                data.cell.styles.textColor = attendanceStyle.text;
                data.cell.styles.fontStyle = "bold";
              }
            }
          },
          margin: { left: 12, right: 12, bottom: 17 },
        });
      });

      const totalSchedulePages = pdf.getNumberOfPages();
      for (let pageNumber = 1; pageNumber <= totalSchedulePages; pageNumber += 1) {
        pdf.setPage(pageNumber);
        const pageHeight = pdf.internal.pageSize.getHeight();
        const legendY = pageHeight - 9;

        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(6.5);
        pdf.setTextColor(70, 70, 70);
        pdf.text(
          "MatrixOS Neo · Published staff schedule",
          12,
          legendY
        );
        pdf.text(
          `Page ${pageNumber} of ${totalSchedulePages}`,
          285,
          legendY,
          { align: "right" }
        );
      }

      const safeStore = storeLabel.replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "");
      const fileName = `Neo-Schedule-${safeStore}-${monthKey}.pdf`;

      if (printAfter) {
        const blobUrl = pdf.output("bloburl");
        if (!printWindow) throw new Error("Print preview window is unavailable.");
        printWindow.location.replace(blobUrl);
        printWindow.focus();
      } else {
        pdf.save(fileName);
      }

      setPdfExportState("done");
      setTimeout(() => setPdfExportState("idle"), 1800);
    } catch (error) {
      try {
        if (printWindow && !printWindow.closed && printWindow.location?.href === "about:blank") {
          printWindow.close();
        }
      } catch (e) {}
      console.error("Schedule PDF export failed", error);
      setPdfExportState("error");
      setPdfExportError(error?.message || "Could not generate the schedule PDF.");
    }
  };

  const weekdayOptions = [
    ["mon", "Mon"], ["tue", "Tue"], ["wed", "Wed"], ["thu", "Thu"],
    ["fri", "Fri"], ["sat", "Sat"], ["sun", "Sun"],
  ];

  const openRulesModal = (person) => {
    if (readOnly || !person) return;
    setRulesModalPerson(person);
    setRuleSaveError("");
    setRuleScope("whole_month");
    setRuleWeek(1);
    setRuleDate(`${monthKey}-01`);
    setRuleDays(["mon", "tue", "wed", "thu", "fri", "sat", "sun"]);
    setRuleAction("shift");
    setRuleShiftMode("preset");
    setRulePreset(presets.find(([value]) => value !== "OFF")?.[0] || "08:00-17:00");
    setRuleCustomStart("08:00");
    setRuleCustomEnd("17:00");
    setEditingRuleId("");
    setRulePreview(null);
    setRuleApplyMode("empty_only");
    setRuleUndoState("idle");
    setRuleCopyState("idle");
    setRuleCopyMessage("");
  };

  const resetRuleEditor = () => {
    setEditingRuleId("");
    setRuleSaveError("");
    setRuleScope("whole_month");
    setRuleWeek(1);
    setRuleDate(`${monthKey}-01`);
    setRuleDays(["mon", "tue", "wed", "thu", "fri", "sat", "sun"]);
    setRuleAction("shift");
    setRuleShiftMode("preset");
    setRulePreset(presets.find(([value]) => value !== "OFF")?.[0] || "08:00-17:00");
    setRuleCustomStart("08:00");
    setRuleCustomEnd("17:00");
  };

  const editStaffRule = (rule) => {
    if (!rule) return;
    setEditingRuleId(rule.id);
    setRuleSaveError("");
    setRulePreview(null);
    setRuleScope(rule.scope || "whole_month");
    setRuleWeek(Number(rule.weekNumber || 1));
    setRuleDate(rule.date || `${monthKey}-01`);
    setRuleDays(Array.isArray(rule.days) && rule.days.length ? rule.days : ["mon", "tue", "wed", "thu", "fri", "sat", "sun"]);
    setRuleAction(rule.action || "shift");
    const value = String(rule.shiftValue || "");
    const presetExists = presets.some(([presetValue]) => presetValue === value);
    setRuleShiftMode(presetExists ? "preset" : "custom");
    if (presetExists) {
      setRulePreset(value);
    } else if (value.includes("-")) {
      const [start = "08:00", end = "17:00"] = value.split("-");
      setRuleCustomStart(start);
      setRuleCustomEnd(end);
    }
  };

  const persistStaffRules = async (person, nextRules, historyAction = "rules_updated", historyExtra = {}) => {
    if (!orgId || !storeId || !monthKey || !person?.id) return false;
    setRuleSaving(true);
    setRuleSaveError("");
    try {
      const batch = writeBatch(db);
      const ruleRef = doc(db, "orgs", orgId, "neo_schedule_staff_rules", `${storeId}__${monthKey}__${person.id}`);
      batch.set(ruleRef, {
        orgId,
        storeId,
        storeName: selectedStore?.name || "",
        monthKey,
        staffId: person.id,
        staffName: person.name || "",
        roleId: person.roleId || "",
        roleName: roleById[person.roleId]?.name || "",
        rules: nextRules,
        ruleCount: nextRules.length,
        status: nextRules.length ? "configured" : "empty",
        updatedAt: serverTimestamp(),
        updatedByUid: actor?.uid || "",
        updatedByEmail: actor?.email || "",
        updatedByName: actor?.name || "",
      }, { merge: true });

      const historyRef = doc(collection(db, "orgs", orgId, "neo_schedule_history"));
      batch.set(historyRef, {
        orgId, storeId, storeName: selectedStore?.name || "", monthKey,
        type: historyAction,
        staffId: person.id, staffName: person.name || "",
        ruleCount: nextRules.length,
        rules: nextRules,
        ...historyExtra,
        createdAt: serverTimestamp(),
        createdByUid: actor?.uid || "",
        createdByEmail: actor?.email || "",
        createdByName: actor?.name || "",
      });
      await batch.commit();
      setRulesByStaff((current) => ({ ...current, [person.id]: nextRules }));
      return true;
    } catch (error) {
      setRuleSaveError(error?.message || "Could not save staff schedule rules.");
      return false;
    } finally {
      setRuleSaving(false);
    }
  };

  const addStaffRule = async () => {
    const person = rulesModalPerson;
    if (!person) return;
    const daysForRule = ruleScope === "specific_date" ? [] : ruleDays;
    if (ruleScope !== "specific_date" && !daysForRule.length) {
      setRuleSaveError("Choose at least one day for this rule.");
      return;
    }
    if (ruleScope === "specific_date" && (!ruleDate || !ruleDate.startsWith(`${monthKey}-`))) {
      setRuleSaveError(`Choose a date inside ${formatMonth(monthKey)}.`);
      return;
    }

    const shiftValue = ruleAction === "off"
      ? "OFF"
      : ruleShiftMode === "custom"
      ? `${ruleCustomStart}-${ruleCustomEnd}`
      : rulePreset;
    if (ruleAction === "shift" && !shiftValue) {
      setRuleSaveError("Choose a shift for this rule.");
      return;
    }

    const currentRules = rulesByStaff[person.id] || [];
    const nowMs = Date.now();
    if (editingRuleId) {
      const nextRules = currentRules.map((rule) => rule.id === editingRuleId ? {
        ...rule,
        scope: ruleScope,
        weekNumber: ruleScope === "week" ? Number(ruleWeek) : null,
        date: ruleScope === "specific_date" ? ruleDate : "",
        days: daysForRule,
        action: ruleAction,
        shiftValue,
        enabled: true,
        updatedAtMs: nowMs,
      } : rule);
      await persistStaffRules(person, nextRules, "rule_edited");
      resetRuleEditor();
      setRulePreview(null);
      return;
    }

    const nextRule = {
      id: `rule_${nowMs}_${Math.random().toString(36).slice(2, 8)}`,
      scope: ruleScope,
      weekNumber: ruleScope === "week" ? Number(ruleWeek) : null,
      date: ruleScope === "specific_date" ? ruleDate : "",
      days: daysForRule,
      action: ruleAction,
      shiftValue,
      source: "manager_rule",
      enabled: true,
      createdAtMs: nowMs,
      updatedAtMs: nowMs,
    };
    await persistStaffRules(person, [...currentRules, nextRule], "rule_added");
    resetRuleEditor();
    setRulePreview(null);
  };

  const removeStaffRule = async (ruleId, confirmed = false) => {
    const person = rulesModalPerson;
    if (!person) return;
    const rule = (rulesByStaff[person.id] || []).find((item) => item.id === ruleId);
    const label = rule ? describeRule(rule).actionLabel : "this rule";
    if (!confirmed) {
      setRuleDeleteConfirm({ ruleId, label });
      return;
    }
    setRuleDeleteConfirm(null);
    const nextRules = (rulesByStaff[person.id] || []).filter((item) => item.id !== ruleId);
    await persistStaffRules(person, nextRules, "rule_removed");
    if (editingRuleId === ruleId) resetRuleEditor();
    setRulePreview(null);
  };

  const requestCopyPreviousMonthRules = () => {
    const person = rulesModalPerson;
    if (!person) return;
    const currentRules = rulesByStaff[person.id] || [];
    if (currentRules.length) {
      setRuleCopyConfirmOpen(true);
      return;
    }
    copyPreviousMonthRules();
  };

  const copyPreviousMonthRules = async () => {
    const person = rulesModalPerson;
    if (!person || !orgId || !storeId || !monthKey) return;

    setRuleCopyConfirmOpen(false);
    const sourceMonthKey = addMonthsToKey(monthKey, -1);
    const currentRules = rulesByStaff[person.id] || [];

    setRuleCopyState("loading");
    setRuleCopyMessage("");
    setRuleSaveError("");
    try {
      const sourceRef = doc(db, "orgs", orgId, "neo_schedule_staff_rules", `${storeId}__${sourceMonthKey}__${person.id}`);
      const sourceSnap = await getDoc(sourceRef);
      const sourceRules = sourceSnap.exists() && Array.isArray(sourceSnap.data()?.rules) ? sourceSnap.data().rules : [];

      if (!sourceRules.length) {
        setRuleCopyState("idle");
        setRuleCopyMessage(`No rules found for ${formatMonth(sourceMonthKey)}.`);
        return;
      }

      // Specific-date rules are deliberate one-off exceptions, so they do not roll into a new month.
      const reusableRules = sourceRules.filter((rule) => rule?.scope !== "specific_date" && rule?.enabled !== false);
      const skippedSpecificDates = sourceRules.filter((rule) => rule?.scope === "specific_date").length;

      const signature = (rule) => JSON.stringify({
        scope: rule?.scope || "whole_month",
        weekNumber: rule?.scope === "week" ? Number(rule?.weekNumber || 1) : null,
        days: [...(Array.isArray(rule?.days) ? rule.days : [])].sort(),
        action: rule?.action || "shift",
        shiftValue: String(rule?.shiftValue || ""),
      });

      const existingSignatures = new Set(currentRules.map(signature));
      const nowMs = Date.now();
      const imported = [];
      reusableRules.forEach((rule, index) => {
        if (existingSignatures.has(signature(rule))) return;
        imported.push({
          ...rule,
          id: `rule_${nowMs + index}_${Math.random().toString(36).slice(2, 8)}`,
          source: "copied_previous_month",
          copiedFromMonthKey: sourceMonthKey,
          createdAtMs: nowMs + index,
          updatedAtMs: nowMs + index,
        });
        existingSignatures.add(signature(rule));
      });

      if (!imported.length) {
        setRuleCopyState("done");
        setRuleCopyMessage(
          `Nothing new to copy from ${formatMonth(sourceMonthKey)}${skippedSpecificDates ? ` · ${skippedSpecificDates} specific-date rule${skippedSpecificDates === 1 ? "" : "s"} skipped` : ""}.`
        );
        return;
      }

      const nextRules = [...currentRules, ...imported];
      const saved = await persistStaffRules(person, nextRules, "rules_copied_from_previous_month", {
        sourceMonthKey,
        sourceMonthLabel: formatMonth(sourceMonthKey),
        copiedRuleCount: imported.length,
        skippedSpecificDateRuleCount: skippedSpecificDates,
      });

      if (!saved) {
        setRuleCopyState("error");
        return;
      }

      setRulePreview(null);
      setRuleCopyState("done");
      setRuleCopyMessage(
        `${imported.length} rule${imported.length === 1 ? "" : "s"} copied from ${formatMonth(sourceMonthKey)}${skippedSpecificDates ? ` · ${skippedSpecificDates} specific-date rule${skippedSpecificDates === 1 ? "" : "s"} skipped` : ""}. Preview before applying.`
      );
    } catch (error) {
      setRuleCopyState("error");
      setRuleSaveError(error?.message || "Could not copy rules from the previous month.");
    }
  };

  const describeRule = (rule) => {
    const dayLabels = weekdayOptions.filter(([id]) => (rule.days || []).includes(id)).map(([, label]) => label).join(", ");
    const scopeLabel = rule.scope === "specific_date"
      ? new Date(`${rule.date}T12:00:00`).toLocaleDateString("en-ZA", { day: "numeric", month: "short" })
      : rule.scope === "week"
      ? `Week ${rule.weekNumber}${dayLabels ? ` · ${dayLabels}` : ""}`
      : `Whole month${dayLabels ? ` · ${dayLabels}` : ""}`;
    const actionLabel = rule.action === "off" ? "OFF" : rule.shiftValue;
    return { scopeLabel, actionLabel };
  };

  const weekdayIdForDate = (date) => ["sun", "mon", "tue", "wed", "thu", "fri", "sat"][date.getDay()];

  const ruleMatchesDate = (rule, date) => {
    if (!rule || rule.enabled === false) return false;
    const dateKey = isoDate(date);
    if (rule.scope === "specific_date") return rule.date === dateKey;
    const weekdayId = weekdayIdForDate(date);
    if (!(rule.days || []).includes(weekdayId)) return false;
    if (rule.scope === "week") {
      const weekNumber = Math.ceil(date.getDate() / 7);
      return Number(rule.weekNumber) === weekNumber;
    }
    return rule.scope === "whole_month";
  };

  const rulePrecedence = (rule) => {
    if (rule.scope === "specific_date") return 300;
    if (rule.scope === "week") return 200;
    return 100;
  };

  const buildRulePreview = (person) => {
    if (!person) return null;
    const rules = (rulesByStaff[person.id] || []).filter((rule) => rule.enabled !== false);
    const rows = [];
    const unchanged = [];
    days.forEach((date) => {
      const matching = rules.filter((rule) => ruleMatchesDate(rule, date));
      if (!matching.length) return;
      matching.sort((a, b) => {
        const precedenceDiff = rulePrecedence(b) - rulePrecedence(a);
        if (precedenceDiff) return precedenceDiff;
        return Number(b.updatedAtMs || b.createdAtMs || 0) - Number(a.updatedAtMs || a.createdAtMs || 0);
      });
      const winner = matching[0];
      const dateKey = isoDate(date);
      const currentValue = draftRef.current[`${storeId}__${person.id}__${dateKey}`] || "";
      const nextValue = winner.action === "off" ? "OFF" : String(winner.shiftValue || "");
      const row = {
        date,
        dateKey,
        currentValue,
        nextValue,
        rule: winner,
        shadowedRuleCount: Math.max(0, matching.length - 1),
      };
      if (currentValue === nextValue) unchanged.push(row);
      else rows.push(row);
    });
    return { personId: person.id, rows, unchanged, generatedAtMs: Date.now() };
  };

  const previewStaffRules = () => {
    const person = rulesModalPerson;
    if (!person) return;
    const nextPreview = buildRulePreview(person);
    setRulePreview(nextPreview);
    setRuleSaveError("");
  };

  const applicableRuleRows = (preview = rulePreview, mode = ruleApplyMode) => {
    const rows = preview?.rows || [];
    if (mode === "overwrite") return rows;
    return rows.filter((row) => !String(row.currentValue || "").trim());
  };

  const applyRulePreview = async () => {
    const person = rulesModalPerson;
    const rows = applicableRuleRows();
    if (!person || !rows.length) return;

    const keys = [];
    const nextDraft = { ...draftRef.current };
    rows.forEach((row) => {
      const key = `${storeId}__${person.id}__${row.dateKey}`;
      nextDraft[key] = row.nextValue;
      keys.push(key);
    });

    draftRef.current = nextDraft;
    setDraft(nextDraft);
    setDirtyKeys((current) => {
      const next = new Set(current);
      keys.forEach((key) => next.add(key));
      dirtyKeysRef.current = next;
      return next;
    });
    setPublishableKeys((current) => {
      const next = new Set(current);
      keys.forEach((key) => next.add(key));
      return next;
    });
    setDirty(true);
    setSaveState("idle");
    setSaveError("");
    setRuleUndoState("idle");
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => saveDraft(), 450);

    try {
      const historyRef = doc(collection(db, "orgs", orgId, "neo_schedule_history"));
      const batch = writeBatch(db);
      batch.set(historyRef, {
        orgId,
        storeId,
        storeName: selectedStore?.name || "",
        monthKey,
        type: "staff_rules_applied",
        staffId: person.id,
        staffName: person.name || "",
        applyMode: ruleApplyMode,
        changedCellCount: rows.length,
        changes: rows.map((row) => ({
          date: row.dateKey,
          before: row.currentValue || "",
          after: row.nextValue || "",
          ruleId: row.rule?.id || "",
          ruleScope: row.rule?.scope || "",
        })),
        createdAt: serverTimestamp(),
        createdByUid: actor?.uid || "",
        createdByEmail: actor?.email || "",
        createdByName: actor?.name || "",
      });
      await batch.commit();
    } catch (error) {
      console.warn("Could not record staff rule application history", error);
    }

    setRulePreview(buildRulePreview(person));
  };

  const undoLastRuleApplication = async (confirmed = false) => {
    const person = rulesModalPerson;
    if (!person || !orgId || !storeId) return;
    if (!confirmed) {
      setRuleUndoConfirmOpen(true);
      return;
    }
    setRuleUndoConfirmOpen(false);
    setRuleUndoState("loading");
    setRuleSaveError("");
    try {
      const historyCollection = collection(db, "orgs", orgId, "neo_schedule_history");
      const [appliedSnap, undoneSnap] = await Promise.all([
        getDocs(query(historyCollection, where("type", "==", "staff_rules_applied"))),
        getDocs(query(historyCollection, where("type", "==", "staff_rules_undone"))),
      ]);
      const undoneIds = new Set(
        undoneSnap.docs
          .map((d) => ({ id: d.id, ...(d.data() || {}) }))
          .filter((row) => row.undoneApplicationId)
          .map((row) => row.undoneApplicationId)
      );
      const applications = appliedSnap.docs
        .map((d) => ({ id: d.id, ...(d.data() || {}) }))
        .filter((row) => row.storeId === storeId && row.monthKey === monthKey && row.staffId === person.id && Array.isArray(row.changes) && row.changes.length && !undoneIds.has(row.id))
        .sort((a, b) => {
          const am = a.createdAt?.toMillis?.() || (a.createdAt?.seconds ? a.createdAt.seconds * 1000 : 0);
          const bm = b.createdAt?.toMillis?.() || (b.createdAt?.seconds ? b.createdAt.seconds * 1000 : 0);
          return bm - am;
        });
      const latest = applications[0];
      if (!latest) {
        setRuleUndoState("empty");
        setRuleSaveError("No applied rule change is available to undo for this staff member and month.");
        return;
      }

      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
      const batch = writeBatch(db);
      const nextDraft = { ...draftRef.current };
      const nextPublishable = new Set(publishableKeys);
      latest.changes.forEach((change) => {
        const dateKey = String(change.date || "");
        if (!dateKey) return;
        const value = String(change.before || "");
        const key = `${storeId}__${person.id}__${dateKey}`;
        nextDraft[key] = value;
        nextPublishable.add(key);
        batch.set(entryRefFor(person.id, dateKey), {
          ...buildEntryPayload(person.id, dateKey, value),
          draftShift: value,
          draftSavedAt: serverTimestamp(),
          draftUpdatedAt: serverTimestamp(),
          draftStatus: "draft",
        }, { merge: true });
      });

      const historyRef = doc(collection(db, "orgs", orgId, "neo_schedule_history"));
      batch.set(historyRef, {
        orgId,
        storeId,
        storeName: selectedStore?.name || "",
        monthKey,
        type: "staff_rules_undone",
        staffId: person.id,
        staffName: person.name || "",
        undoneApplicationId: latest.id,
        changeCount: latest.changes.length,
        changes: latest.changes.map((change) => ({
          date: change.date || "",
          before: change.after || "",
          after: change.before || "",
          reason: "Undo staff schedule rules",
        })),
        createdAt: serverTimestamp(),
        createdByUid: actor?.uid || "",
        createdByEmail: actor?.email || "",
        createdByName: actor?.name || "",
      });
      await batch.commit();

      draftRef.current = nextDraft;
      setDraft(nextDraft);
      setLoadedDraft(nextDraft);
      setPublishableKeys(nextPublishable);
      dirtyKeysRef.current = new Set();
      setDirtyKeys(new Set());
      setDirty(false);
      setSaveState("saved");
      setLastSavedAt(Date.now());
      setRuleUndoState("done");
      setRulePreview(buildRulePreview(person));
    } catch (error) {
      setRuleUndoState("error");
      setRuleSaveError(error?.message || "Could not undo the most recent rule application.");
    }
  };

  const closeBuilder = () => {
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    setEditing(null);
    draftRef.current = { ...loadedDraft };
    setDraft({ ...loadedDraft });
    dirtyKeysRef.current = new Set();
    setDirtyKeys(new Set());
    setDirty(false);
    setSaveError("");
    setPdfExportError("");
    setSaveState("idle");
    onClose();
  };

  const openEditor = (person, date) => {
    if (readOnly) return;
    const dateKey = isoDate(date);
    const current = getShift(person.id, dateKey);
    if (current && current !== "OFF" && current.includes("-") && !attendanceIdFromValue(current)) {
      const [start, end] = current.split("-");
      setCustomStart(start);
      setCustomEnd(end);
    }
    const currentAttendanceId = attendanceIdFromValue(current);
    if (currentAttendanceId) {
      const currentType = attendanceTypes.find((row) => row.id === currentAttendanceId);
      setPendingAttendanceId(currentAttendanceId);
      setPendingAttendanceHours(String(currentType?.allocateHours ? attendanceHoursFromValue(current, attendanceTypes) : 0));
    } else {
      setPendingAttendanceId("");
      setPendingAttendanceHours("");
    }
    setEditing({ person, date, dateKey, current });
  };

  const lunchTotalsByStaff = Object.fromEntries(
    storeStaff.map((person) => [
      person.id,
      days.reduce(
        (sum, day) => sum + scheduledLunchHours(getShift(person.id, isoDate(day)), lunchBreakMinutes),
        0
      ),
    ])
  );

  const totalsByStaff = Object.fromEntries(
    storeStaff.map((person) => [
      person.id,
      days.reduce(
        (sum, day) =>
          sum + scheduleCellHours(getShift(person.id, isoDate(day)), attendanceTypes, lunchBreakMinutes),
        0
      ),
    ])
  );

  return (
    <div
      className="fixed inset-0 z-[99999] block h-[100dvh] w-screen max-w-none overflow-y-auto overflow-x-hidden lg:flex lg:flex-col lg:overflow-hidden"
      style={{
        background: "linear-gradient(160deg, #07111f 0%, #091a2f 58%, #06101d 100%)",
        width: "100vw",
        maxWidth: "100vw",
        height: "100dvh",
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
      }}
    >
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-white/10 px-3 py-2 sm:px-4 sm:py-3 md:px-6 lg:shrink-0">
        <div>
          <div className="text-xs font-bold uppercase tracking-[.18em] text-cyan-300">Schedule Intelligence</div>
          <h2 className="mt-1 text-xl font-black leading-tight text-white sm:text-2xl">Monthly Schedule Builder</h2>
          <div className="mt-1 hidden text-xs text-white/55 sm:block">
            Staff and public holidays are absorbed from Neo's existing source-of-truth settings.
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {saveState === "saving" ? (
            <Pill tone="blue">Auto-saving…</Pill>
          ) : saveState === "publishing" ? (
            <Pill tone="blue">Publishing…</Pill>
          ) : dirty ? (
            <Pill tone="amber">Saving {dirtyKeys.size} change{dirtyKeys.size === 1 ? "" : "s"}…</Pill>
          ) : saveState === "published" ? (
            <Pill tone="green">Published</Pill>
          ) : saveState === "saved" ? (
            <Pill tone="green">Auto-saved {lastSavedAt ? new Date(lastSavedAt).toLocaleTimeString("en-ZA", { hour: "2-digit", minute: "2-digit", hour12: false }) : ""}</Pill>
          ) : (
            <Pill tone="slate">No changes</Pill>
          )}
          <button
            type="button"
            onClick={closeBuilder}
            className="rounded-xl border border-white/10 px-4 py-2 text-sm font-bold text-white hover:bg-white/5"
          >
            Close
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-2 border-b border-white/10 bg-black/10 px-3 py-2 sm:px-4 md:px-6 lg:shrink-0 lg:grid-cols-3">
        <label>
          <span className="text-[11px] font-bold uppercase tracking-wider text-white/50">Store</span>
          <select
            value={storeId}
            onChange={(e) => setStoreId(e.target.value)}
            className="mt-1 w-full rounded-xl border border-white/10 bg-slate-950/80 px-3 py-2 text-sm text-white"
          >
            {stores.map((store) => <option key={store.id} value={store.id}>{store.name}</option>)}
          </select>
        </label>
        <div className="block">
          <span className="text-[11px] font-bold uppercase tracking-wider text-white/50">Schedule month</span>
          <div className="mt-1 flex min-h-[38px] items-center justify-between gap-2 rounded-xl border border-white/10 bg-slate-950/80 px-3 py-2 text-sm text-white">
            <span className="font-bold">{formatMonth(monthKey)}</span>
            <Pill tone={monthKey === todayMonthKey ? "green" : monthKey > todayMonthKey ? "blue" : "slate"}>
              {monthKey === todayMonthKey ? "Current" : monthKey > todayMonthKey ? "Planned" : "History"}
            </Pill>
          </div>
        </div>
        <div className="flex items-end">
          <div className="w-full rounded-xl border border-white/10 px-3 py-2 text-sm text-white/70">
            <strong className="text-white">{storeStaff.length}</strong> active staff · <strong className="text-white">{holidaysThisMonth.size}</strong> public holidays this month
          </div>
        </div>
      </div>

      {readOnly ? (
        <div className="border-b border-amber-400/15 bg-amber-500/5 px-3 py-2 text-xs font-semibold text-amber-100 sm:px-4 md:px-6">
          Historical schedule · view only. Previous months cannot be edited from History.
        </div>
      ) : null}

      <div
        className="w-full min-w-0 overflow-visible p-2 sm:p-3 md:p-4 lg:min-h-0 lg:flex-1 lg:overflow-auto lg:overscroll-contain lg:p-5"
        style={{ WebkitOverflowScrolling: "touch" }}
      >
        {!selectedStore ? (
          <div className="rounded-2xl border border-amber-400/25 bg-amber-500/5 p-5 text-amber-100">
            Add/select a store before building a schedule.
          </div>
        ) : !storeStaff.length ? (
          <div className="rounded-2xl border border-amber-400/25 bg-amber-500/5 p-5 text-amber-100">
            No active staff are assigned to {selectedStore.name}. Add staff in the Staff tab first.
          </div>
        ) : (
          <>
            {/* Mobile / tablet: horizontal staff calendar */}
            <div className="w-full min-w-0 lg:hidden">
              <div className="mb-3 rounded-2xl border border-white/10 bg-slate-950/30 p-3">
                <div className="text-[11px] font-bold uppercase tracking-wider text-white/45">
                  Staff member
                </div>
                <select
                  value={mobileStaff?.id || ""}
                  onChange={(e) => setMobileStaffId(e.target.value)}
                  className="mt-2 w-full rounded-xl border border-white/10 bg-slate-950/80 px-3 py-2.5 text-sm font-bold text-white"
                >
                  {storeStaff.map((person) => (
                    <option key={person.id} value={person.id}>
                      {person.name} · {roleById[person.roleId]?.name || "Role not set"}
                    </option>
                  ))}
                </select>

                {mobileStaff ? (
                  <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-white/50">
                    <span className="rounded-full border border-white/10 px-2 py-1">
                      {formatMonth(monthKey)}
                    </span>
                    <span className="rounded-full border border-white/10 px-2 py-1">
                      {roleById[mobileStaff.roleId]?.name || "Role not set"}
                    </span>
                    <span className="rounded-full border border-cyan-400/20 bg-cyan-400/5 px-2 py-1 text-cyan-100">
                      {formatScheduleHours(totalsByStaff[mobileStaff.id] || 0)}
                    </span>
                  </div>
                ) : null}

                {mobileStaff && !readOnly ? (
                  <button
                    type="button"
                    onClick={() => openRulesModal(mobileStaff)}
                    className="mt-3 w-full rounded-xl border border-cyan-400/25 bg-cyan-400/5 px-3 py-2.5 text-sm font-bold text-cyan-100 hover:bg-cyan-400/10"
                  >
                    Schedule Rules{(rulesByStaff[mobileStaff.id] || []).length ? ` · ${(rulesByStaff[mobileStaff.id] || []).length}` : ""}
                  </button>
                ) : null}

                <div className="mt-2 text-xs text-white/45">
                  Swipe horizontally through the month. Tap a shift cell to add or edit.
                </div>
              </div>

              {mobileStaff ? (
                <div
                  ref={mobileScheduleScrollRef}
                  className="w-full max-w-full overflow-x-auto overflow-y-hidden rounded-2xl border border-white/10 bg-slate-950/25"
                  style={{ WebkitOverflowScrolling: "touch", touchAction: "pan-x pan-y" }}
                >
                  <div className="inline-flex min-w-max flex-col">
                    {/* Date strip */}
                    <div className="flex">
                      <div className="sticky left-0 z-30 flex w-[132px] shrink-0 items-center border-r border-b border-white/10 bg-[#071527] px-3 py-3">
                        <div>
                          <div className="text-[10px] font-bold uppercase tracking-wider text-white/45">
                            {formatMonth(monthKey)}
                          </div>
                          <div className="mt-1 text-sm font-black text-white">
                            {mobileStaff.name}
                          </div>
                        </div>
                      </div>

                      {days.map((day) => {
                        const dateKey = isoDate(day);
                        const sunday = day.getDay() === 0;
                        const holiday = holidaysThisMonth.has(dateKey);
                        return (
                          <div
                            key={`mobile-date-${dateKey}`}
                            data-schedule-date={dateKey}
                            className="flex h-[72px] w-[82px] shrink-0 flex-col items-center justify-center border-r border-b border-white/10 px-1 text-center"
                            style={{
                              background: dateKey === todayDateKey
                                ? "linear-gradient(180deg, rgba(34,211,238,.22), rgba(8,47,73,.42))"
                                : holiday
                                ? "rgba(244,63,94,.12)"
                                : sunday
                                ? "rgba(245,158,11,.09)"
                                : "rgba(2,6,23,.58)",
                              boxShadow: dateKey === todayDateKey
                                ? "inset 0 0 0 2px rgba(103,232,249,.72), 0 0 20px rgba(34,211,238,.24)"
                                : "none",
                            }}
                          >
                            <div className="text-[9px] font-bold uppercase text-white/45">
                              {day.toLocaleDateString("en-ZA", { weekday: "short" }).slice(0, 3)}
                            </div>
                            <div className="mt-1 text-base font-black text-white">
                              {day.getDate()}
                            </div>
                            {holiday ? (
                              <div className="mt-1 text-[8px] font-bold uppercase text-rose-300">Public</div>
                            ) : sunday ? (
                              <div className="mt-1 text-[8px] font-bold uppercase text-amber-300">Sun</div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>

                    {/* Shift strip */}
                    <div className="flex">
                      <div className="sticky left-0 z-20 flex w-[132px] shrink-0 items-center border-r border-white/10 bg-[#0a1a2d] px-3 py-3">
                        <div>
                          <div className="text-xs font-black text-white">Shift</div>
                          <div className="mt-1 text-[10px] text-white/40">
                            Tap a day
                          </div>
                        </div>
                      </div>

                      {days.map((day) => {
                        const dateKey = isoDate(day);
                        const shift = getShift(mobileStaff.id, dateKey);
                        const attendanceTypeId = attendanceIdFromValue(shift);
                        const attendanceType =
                          attendanceTypes.find((row) => row.id === attendanceTypeId) || null;
                        const sunday = day.getDay() === 0;
                        const holiday = holidaysThisMonth.has(dateKey);

                        return (
                          <button
                            key={`mobile-shift-${dateKey}`}
                            type="button"
                            onClick={() => openEditor(mobileStaff, day)}
                            className="flex h-[88px] w-[82px] shrink-0 flex-col items-center justify-center border-r border-white/10 px-1.5 text-center transition active:bg-cyan-400/10"
                            style={{
                              background: dateKey === todayDateKey
                                ? "linear-gradient(180deg, rgba(34,211,238,.13), rgba(8,47,73,.20))"
                                : attendanceType
                                ? "rgba(139,92,246,.13)"
                                : shift === "OFF"
                                ? "rgba(245,158,11,.13)"
                                : shift
                                ? "rgba(16,185,129,.08)"
                                : holiday
                                ? "rgba(244,63,94,.05)"
                                : sunday
                                ? "rgba(245,158,11,.035)"
                                : "transparent",
                              boxShadow: dateKey === todayDateKey
                                ? "inset 2px 0 0 rgba(103,232,249,.58), inset -2px 0 0 rgba(103,232,249,.58)"
                                : "none",
                            }}
                          >
                            {shift ? (
                              <>
                                <div className={`max-w-full break-words text-[9px] font-black leading-tight ${
                                  attendanceType
                                    ? "text-violet-200"
                                    : shift === "OFF"
                                    ? "text-amber-200"
                                    : "text-cyan-100"
                                }`}>
                                  {attendanceType ? attendanceType.label : shift}
                                </div>
                                <div className="mt-1 text-[8px] text-white/40">
                                  {attendanceType
                                    ? attendanceType.allocateHours
                                      ? `${attendanceHoursFromValue(shift, attendanceTypes)}h`
                                      : "No hours"
                                    : shift === "OFF"
                                    ? "Off"
                                    : `${shiftHours(shift)}h`}
                                </div>
                              </>
                            ) : (
                              <div className="text-[10px] font-semibold text-white/35">
                                + Shift
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ) : null}
            </div>

            {/* Desktop: full monthly matrix */}
            <div className="hidden lg:block">
            <div
              ref={desktopScheduleScrollRef}
              className="block w-full max-w-full overflow-x-auto overflow-y-visible overscroll-x-contain rounded-2xl border border-white/10 bg-slate-950/25"
              style={{ WebkitOverflowScrolling: "touch", touchAction: "pan-x pan-y" }}
            >
            <div className="w-max" style={{ minWidth: `${180 + (days.length * 80) + 80 + 72}px` }}>
            <div
              className="grid border-b border-white/10 bg-slate-950/80"
              style={{ gridTemplateColumns: `180px repeat(${days.length}, minmax(80px, 1fr)) 80px 72px` }}
            >
              <div className="sticky left-0 z-20 border-r border-white/10 bg-slate-950 px-4 py-3">
                <div className="text-xs font-bold uppercase tracking-wider text-white/50">Staff</div>
                <div className="mt-1 text-sm font-bold text-white">{formatMonth(monthKey)}</div>
              </div>
              {days.map((day) => {
                const key = isoDate(day);
                const sunday = day.getDay() === 0;
                const holiday = holidaysThisMonth.has(key);
                return (
                  <div
                    key={key}
                    data-schedule-date={key}
                    className="border-r border-white/10 px-2 py-3 text-center"
                    style={{
                      background: key === todayDateKey
                        ? "linear-gradient(180deg, rgba(34,211,238,.22), rgba(8,47,73,.42))"
                        : holiday
                        ? "rgba(244,63,94,.12)"
                        : sunday
                        ? "rgba(245,158,11,.09)"
                        : "transparent",
                      boxShadow: key === todayDateKey
                        ? "inset 0 0 0 2px rgba(103,232,249,.72), 0 0 22px rgba(34,211,238,.24)"
                        : "none",
                    }}
                  >
                    <div className="text-[10px] font-bold uppercase tracking-wider text-white/45">
                      {day.toLocaleDateString("en-ZA", { weekday: "short" })}
                    </div>
                    <div className="mt-1 font-black text-white">{day.getDate()}</div>
                    {holiday ? <div className="mt-1 text-[9px] font-bold uppercase text-rose-300">Public</div> : null}
                    {!holiday && sunday ? <div className="mt-1 text-[9px] font-bold uppercase text-amber-300">Sun</div> : null}
                  </div>
                );
              })}
              <div className="border-r border-white/10 px-2 py-3 text-center">
                <div className="text-[10px] font-bold uppercase leading-tight tracking-wider text-white/45">
                  Lunch<br />Breaks
                </div>
                <div className="mt-1 text-[9px] text-white/35">
                  {Number(lunchBreakMinutes) === 60
                    ? "1h / shift"
                    : Number(lunchBreakMinutes) === 30
                    ? "0:30 / shift"
                    : `${Math.max(0, Number(lunchBreakMinutes) || 0)}m / shift`}
                </div>
              </div>
              <div className="px-2 py-3 text-center">
                <div className="text-[10px] font-bold uppercase tracking-wider text-white/45">Hours</div>
              </div>
            </div>

            {storeStaff.map((person) => (
              <div
                key={person.id}
                className="grid border-b border-white/5 last:border-b-0"
                style={{ gridTemplateColumns: `180px repeat(${days.length}, minmax(80px, 1fr)) 80px 72px` }}
              >
                <div className="sticky left-0 z-10 border-r border-white/10 bg-[#0a1729] px-4 py-3">
                  <button
                    type="button"
                    disabled={readOnly}
                    onClick={() => openRulesModal(person)}
                    className="w-full rounded-lg text-left disabled:cursor-default"
                    title={readOnly ? "Historical schedule" : "Open staff schedule rules"}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-bold text-white">{person.name}</div>
                      {!readOnly ? (
                        <span className="rounded-full border border-cyan-400/20 bg-cyan-400/5 px-1.5 py-0.5 text-[9px] font-bold text-cyan-200">
                          Rules{(rulesByStaff[person.id] || []).length ? ` ${(rulesByStaff[person.id] || []).length}` : ""}
                        </span>
                      ) : null}
                    </div>
                    <div className="mt-1 text-xs text-white/50">{roleById[person.roleId]?.name || "Role not set"}</div>
                  </button>
                </div>

                {days.map((day) => {
                  const key = isoDate(day);
                  const shift = getShift(person.id, key);
                  const attendanceTypeId = attendanceIdFromValue(shift);
                  const attendanceType = attendanceTypes.find((row) => row.id === attendanceTypeId) || null;
                  const sunday = day.getDay() === 0;
                  const holiday = holidaysThisMonth.has(key);
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => openEditor(person, day)}
                      className="min-h-[64px] border-r border-white/5 px-2 py-2 text-center transition hover:z-[1]"
                      style={{
                        background: key === todayDateKey
                          ? "linear-gradient(180deg, rgba(34,211,238,.11), rgba(8,47,73,.18))"
                          : attendanceType
                          ? "rgba(139,92,246,.13)"
                          : shift === "OFF"
                          ? "rgba(245,158,11,.13)"
                          : shift
                          ? "rgba(16,185,129,.08)"
                          : holiday
                          ? "rgba(244,63,94,.055)"
                          : sunday
                          ? "rgba(245,158,11,.035)"
                          : "transparent",
                        boxShadow: key === todayDateKey
                          ? "inset 2px 0 0 rgba(103,232,249,.58), inset -2px 0 0 rgba(103,232,249,.58)"
                          : shift
                          ? "inset 0 0 0 1px rgba(96,165,250,.08)"
                          : "none",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.boxShadow = "0 0 24px rgba(34,211,238,.16), inset 0 0 0 1px rgba(34,211,238,.35)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.boxShadow = shift ? "inset 0 0 0 1px rgba(96,165,250,.08)" : "none";
                      }}
                    >
                      {shift ? (
                        <>
                          <div className={`text-xs font-black ${attendanceType ? "text-violet-200" : shift === "OFF" ? "text-amber-200" : "text-cyan-100"}`}>
                            {attendanceType ? attendanceType.label : shift}
                          </div>
                          <div className="mt-1 text-[10px] text-white/45">
                            {attendanceType
                              ? attendanceType.allocateHours
                                ? `${attendanceHoursFromValue(shift, attendanceTypes)}h allocated`
                                : attendanceType.paid === false
                                ? "Unpaid · no hours"
                                : "No hours allocated"
                              : shift === "OFF"
                              ? "Off day"
                              : `${shiftHours(shift)}h`}
                          </div>
                        </>
                      ) : (
                        <div className="text-xs text-white/25">+ Shift</div>
                      )}
                    </button>
                  );
                })}

                <div className="flex items-center justify-center border-r border-white/10 px-2 py-3">
                  <Pill tone="slate">{formatLunchBreak(lunchTotalsByStaff[person.id] || 0)}</Pill>
                </div>
                <div className="flex items-center justify-center px-2 py-3">
                  <Pill tone="blue">{formatScheduleHours(totalsByStaff[person.id] || 0)}</Pill>
                </div>
              </div>
            ))}
            </div>
          </div>
            </div>
          </>
        )}
      </div>

      <div className="flex flex-col gap-2 border-t border-white/10 bg-black/15 px-3 py-3 sm:px-4 md:px-6 lg:shrink-0 lg:flex-row lg:items-center lg:justify-between pb-5 sm:pb-4" style={{ paddingBottom: "max(20px, env(safe-area-inset-bottom))" }}>
        <div>
          <div className="hidden text-xs text-white/45 sm:block">
            Sunday/public-holiday highlighting is read from existing Neo settings. PDF/Print exports use the published roster only. Payroll targets and multipliers remain in Payroll.
          </div>
          {lastSavedAt ? (
            <div className="mt-1 text-[11px] text-white/35">
              Last schedule save: {new Date(lastSavedAt).toLocaleString("en-ZA")}
            </div>
          ) : null}
          {saveError ? (
            <div className="mt-1 text-xs font-semibold text-rose-300">{saveError}</div>
          ) : null}
          {pdfExportError ? (
            <div className="mt-1 text-xs font-semibold text-rose-300">{pdfExportError}</div>
          ) : null}
        </div>
        <div className="grid w-full grid-cols-2 gap-2 lg:w-auto lg:flex lg:flex-wrap lg:justify-end">
          <button
            type="button"
            disabled={pdfExportState === "exporting" || pdfExportState === "printing"}
            onClick={() => exportPublishedSchedulePdf({ printAfter: false })}
            className="rounded-xl border border-cyan-400/25 bg-cyan-400/5 px-4 py-2 text-sm font-bold text-cyan-100 hover:bg-cyan-400/10 disabled:opacity-35"
            title="Exports the published schedule only"
          >
            {pdfExportState === "exporting" ? "Generating PDF…" : "Download PDF"}
          </button>
          <button
            type="button"
            disabled={pdfExportState === "exporting" || pdfExportState === "printing"}
            onClick={() => exportPublishedSchedulePdf({ printAfter: true })}
            className="rounded-xl border border-white/10 px-4 py-2 text-sm font-bold text-white hover:bg-white/5 disabled:opacity-35"
            title="Opens the published roster for printing"
          >
            {pdfExportState === "printing" ? "Preparing print…" : "Print Schedule"}
          </button>
          <button
            type="button"
            disabled={readOnly || recoverState === "loading" || saveState === "saving" || saveState === "publishing"}
            onClick={recoverLastClearedDraft}
            className="rounded-xl border border-sky-400/20 bg-sky-400/5 px-4 py-2 text-sm font-bold text-sky-100 hover:bg-sky-400/10 disabled:opacity-35"
            title="Recover the most recently cleared draft for this store and month"
          >
            {recoverState === "loading" ? "Recovering…" : "Recover Draft"}
          </button>
          <button
            type="button"
            disabled={readOnly || saveState === "saving" || saveState === "publishing"}
            onClick={() => setClearConfirmOpen(true)}
            className="rounded-xl border border-rose-400/25 bg-rose-400/5 px-4 py-2 text-sm font-bold text-rose-100 hover:bg-rose-400/10 disabled:opacity-35"
          >
            Clear Schedule
          </button>
          <button
            type="button"
            disabled={readOnly || !publishableKeys.size || saveState === "saving" || saveState === "publishing"}
            onClick={requestPublishChanges}
            className="min-h-[44px] rounded-xl border border-emerald-400/30 bg-emerald-400/10 px-4 py-2 text-sm font-bold text-emerald-100 disabled:opacity-35"
          >
            {saveState === "publishing" ? "Publishing…" : "Publish Changes"}
          </button>
        </div>
      </div>

      {!readOnly && clearConfirmOpen ? (
        <div className="fixed inset-0 z-[3200] flex items-center justify-center bg-black/75 p-4 backdrop-blur-sm">
          <div className="w-full max-w-lg rounded-2xl border border-rose-400/25 bg-[#0b1b30] p-5 shadow-2xl">
            <div className="text-xs font-bold uppercase tracking-[.14em] text-rose-300">Clear schedule</div>
            <h3 className="mt-2 text-xl font-black text-white">Clear {formatMonth(monthKey)}?</h3>
            <p className="mt-2 text-sm leading-6 text-white/65">
              This clears the working draft for {selectedStore?.name || "this store"}. Neo will save a recoverable snapshot in Schedule History first. Any currently published roster remains live until you publish the cleared draft.
            </p>
            <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setClearConfirmOpen(false)}
                className="rounded-xl border border-white/10 px-4 py-2.5 text-sm font-bold text-white/75 hover:bg-white/5"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={saveState === "saving" || saveState === "publishing"}
                onClick={clearSchedule}
                className="rounded-xl border border-rose-400/30 bg-rose-400/10 px-4 py-2.5 text-sm font-black text-rose-100 disabled:opacity-35"
              >
                Clear Schedule
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {!readOnly && revisionConfirmOpen ? (
        <div className="fixed inset-0 z-[3200] flex items-center justify-center bg-black/75 p-4 backdrop-blur-sm">
          <div className="w-full max-w-lg rounded-2xl border border-amber-400/25 bg-[#0b1b30] p-5 shadow-2xl">
            <div className="text-xs font-bold uppercase tracking-[.14em] text-amber-300">
              Published schedule revision
            </div>
            <h3 className="mt-2 text-xl font-black text-white">Reason required</h3>
            <p className="mt-2 text-sm leading-6 text-white/65">
              You are changing a shift that has already been published. Neo will keep the original schedule, revised schedule, manager, timestamp and this reason in Schedule History.
            </p>
            <label className="mt-4 block">
              <span className="text-xs font-bold uppercase tracking-wider text-white/45">Reason for change</span>
              <textarea
                value={revisionReason}
                onChange={(e) => setRevisionReason(e.target.value)}
                rows={4}
                autoFocus
                placeholder="Example: Staff requested an approved earlier shift due to transport."
                className="mt-2 w-full resize-none rounded-xl border border-white/10 bg-slate-950/70 px-3 py-3 text-sm text-white outline-none focus:border-amber-300/50"
              />
            </label>
            <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => {
                  setRevisionConfirmOpen(false);
                  setRevisionReason("");
                }}
                className="rounded-xl border border-white/10 px-4 py-2.5 text-sm font-bold text-white/75 hover:bg-white/5"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!revisionReason.trim() || saveState === "publishing"}
                onClick={() => publishChanges(revisionReason)}
                className="rounded-xl border border-amber-400/30 bg-amber-400/10 px-4 py-2.5 text-sm font-black text-amber-100 disabled:opacity-35"
              >
                {saveState === "publishing" ? "Publishing…" : "Confirm & publish revision"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {!readOnly && rulesModalPerson ? (
        <div className="fixed inset-0 z-[3150] flex items-start justify-center overflow-y-auto bg-black/75 p-2 backdrop-blur-sm sm:items-center sm:p-4" onMouseDown={() => setRulesModalPerson(null)}>
          <div
            className="my-2 w-full max-w-3xl overflow-y-auto rounded-2xl border border-cyan-400/20 bg-[#0b1b30] p-4 shadow-2xl sm:my-0 sm:p-5"
            style={{ maxHeight: "calc(100dvh - 16px)", WebkitOverflowScrolling: "touch" }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-xs font-bold uppercase tracking-[.14em] text-cyan-300">Staff Schedule Rules</div>
                <h3 className="mt-1 text-xl font-black text-white">{rulesModalPerson.name}</h3>
                <div className="mt-1 text-sm text-white/55">
                  {roleById[rulesModalPerson.roleId]?.name || "Role not set"} · {formatMonth(monthKey)}
                </div>
              </div>
              <button type="button" onClick={() => setRulesModalPerson(null)} className="rounded-lg border border-white/10 px-3 py-1.5 text-white/70">✕</button>
            </div>

            <div className="mt-4 rounded-xl border border-cyan-400/15 bg-cyan-400/5 p-3 text-sm leading-6 text-cyan-50/80">
              Define the manager-authored pattern for this person. Neo resolves overlapping rules by precedence: specific date → specific week → whole month. Reuse recurring patterns next month with Copy Previous Month Rules; one-off specific-date rules stay behind.
            </div>

            <div className="mt-5 rounded-2xl border border-white/10 bg-slate-950/25 p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="text-xs font-bold uppercase tracking-wider text-white/50">{editingRuleId ? "Edit rule" : "Create rule"}</div>
                {editingRuleId ? (
                  <button type="button" onClick={resetRuleEditor} className="rounded-lg border border-white/10 px-2.5 py-1 text-xs font-bold text-white/55 hover:bg-white/5">Cancel edit</button>
                ) : null}
              </div>
              <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                <label>
                  <span className="text-xs font-semibold text-white/55">Applies to</span>
                  <select value={ruleScope} onChange={(e) => setRuleScope(e.target.value)} className="mt-1 w-full rounded-xl border border-white/10 bg-slate-950/80 px-3 py-2.5 text-sm text-white">
                    <option value="whole_month">Whole month</option>
                    <option value="week">Specific week</option>
                    <option value="specific_date">Specific date</option>
                  </select>
                </label>
                {ruleScope === "week" ? (
                  <label>
                    <span className="text-xs font-semibold text-white/55">Week</span>
                    <select value={ruleWeek} onChange={(e) => setRuleWeek(Number(e.target.value))} className="mt-1 w-full rounded-xl border border-white/10 bg-slate-950/80 px-3 py-2.5 text-sm text-white">
                      {[1,2,3,4,5].map((week) => <option key={week} value={week}>Week {week}</option>)}
                    </select>
                  </label>
                ) : ruleScope === "specific_date" ? (
                  <label>
                    <span className="text-xs font-semibold text-white/55">Date</span>
                    <input type="date" value={ruleDate} min={`${monthKey}-01`} max={`${monthKey}-${pad(days.length)}`} onChange={(e) => setRuleDate(e.target.value)} className="mt-1 w-full rounded-xl border border-white/10 bg-slate-950/80 px-3 py-2.5 text-sm text-white" />
                  </label>
                ) : <div />}
              </div>

              {ruleScope !== "specific_date" ? (
                <div className="mt-4">
                  <div className="text-xs font-semibold text-white/55">Days</div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {weekdayOptions.map(([id, label]) => {
                      const selected = ruleDays.includes(id);
                      return (
                        <button key={id} type="button" onClick={() => setRuleDays((current) => selected ? current.filter((day) => day !== id) : [...current, id])} className={`rounded-full border px-3 py-1.5 text-xs font-bold ${selected ? "border-cyan-300/40 bg-cyan-400/10 text-cyan-100" : "border-white/10 text-white/45"}`}>
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : null}

              <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                <label>
                  <span className="text-xs font-semibold text-white/55">Action</span>
                  <select value={ruleAction} onChange={(e) => setRuleAction(e.target.value)} className="mt-1 w-full rounded-xl border border-white/10 bg-slate-950/80 px-3 py-2.5 text-sm text-white">
                    <option value="shift">Work shift</option>
                    <option value="off">OFF</option>
                  </select>
                </label>
                {ruleAction === "shift" ? (
                  <label>
                    <span className="text-xs font-semibold text-white/55">Shift source</span>
                    <select value={ruleShiftMode} onChange={(e) => setRuleShiftMode(e.target.value)} className="mt-1 w-full rounded-xl border border-white/10 bg-slate-950/80 px-3 py-2.5 text-sm text-white">
                      <option value="preset">Quick shift</option>
                      <option value="custom">Custom time</option>
                    </select>
                  </label>
                ) : <div />}
              </div>

              {ruleAction === "shift" && ruleShiftMode === "preset" ? (
                <label className="mt-3 block">
                  <span className="text-xs font-semibold text-white/55">Quick shift</span>
                  <select value={rulePreset} onChange={(e) => setRulePreset(e.target.value)} className="mt-1 w-full rounded-xl border border-white/10 bg-slate-950/80 px-3 py-2.5 text-sm text-white">
                    {presets.filter(([value]) => value !== "OFF").map(([value, label]) => <option key={value} value={value}>{value} · {label}</option>)}
                  </select>
                </label>
              ) : null}

              {ruleAction === "shift" && ruleShiftMode === "custom" ? (
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <label><span className="text-xs font-semibold text-white/55">Start</span><input type="time" value={ruleCustomStart} onChange={(e) => setRuleCustomStart(e.target.value)} className="mt-1 w-full rounded-xl border border-white/10 bg-slate-950/80 px-3 py-2.5 text-sm text-white" /></label>
                  <label><span className="text-xs font-semibold text-white/55">End</span><input type="time" value={ruleCustomEnd} onChange={(e) => setRuleCustomEnd(e.target.value)} className="mt-1 w-full rounded-xl border border-white/10 bg-slate-950/80 px-3 py-2.5 text-sm text-white" /></label>
                </div>
              ) : null}

              {ruleSaveError ? <div className="mt-3 text-xs font-semibold text-rose-300">{ruleSaveError}</div> : null}
              <button type="button" disabled={ruleSaving} onClick={addStaffRule} className="mt-4 w-full rounded-xl border border-emerald-400/30 bg-emerald-400/10 px-4 py-2.5 text-sm font-black text-emerald-100 disabled:opacity-35">
                {ruleSaving ? "Saving rule…" : editingRuleId ? "Save Rule Changes" : "+ Add Rule"}
              </button>
            </div>

            <div className="mt-5">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-white/50">Configured rules</div>
                  <div className="mt-1 text-sm text-white/45">Specific date overrides week rules; week rules override whole-month rules. If two rules have equal precedence, the most recently edited rule wins.</div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={requestCopyPreviousMonthRules}
                    disabled={ruleSaving || ruleCopyState === "loading"}
                    className="rounded-xl border border-violet-400/25 bg-violet-400/10 px-3 py-2 text-xs font-black text-violet-100 disabled:opacity-35"
                  >
                    {ruleCopyState === "loading" ? "Copying…" : `Copy ${formatMonth(addMonthsToKey(monthKey, -1))} Rules`}
                  </button>
                  <Pill tone={(rulesByStaff[rulesModalPerson.id] || []).length ? "blue" : "slate"}>{(rulesByStaff[rulesModalPerson.id] || []).length} rules</Pill>
                </div>
              </div>
              {ruleCopyMessage ? (
                <div className="mt-3 rounded-xl border border-violet-400/15 bg-violet-400/5 px-3 py-2 text-xs font-semibold text-violet-100/80">
                  {ruleCopyMessage}
                </div>
              ) : null}
              <div className="mt-3 space-y-2">
                {(rulesByStaff[rulesModalPerson.id] || []).length ? (rulesByStaff[rulesModalPerson.id] || []).map((rule) => {
                  const description = describeRule(rule);
                  return (
                    <div key={rule.id} className="flex flex-col gap-3 rounded-xl border border-white/10 bg-slate-950/25 p-3 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <div className="text-sm font-black text-white">{description.actionLabel}</div>
                        <div className="mt-1 text-xs text-white/50">{description.scopeLabel}</div>
                      </div>
                      <div className="flex gap-2">
                        <button type="button" disabled={ruleSaving} onClick={() => editStaffRule(rule)} className="rounded-lg border border-cyan-400/20 px-3 py-2 text-xs font-bold text-cyan-100 hover:bg-cyan-400/5 disabled:opacity-35">Edit</button>
                        <button type="button" disabled={ruleSaving} onClick={() => removeStaffRule(rule.id)} className="rounded-lg border border-rose-400/20 px-3 py-2 text-xs font-bold text-rose-200 hover:bg-rose-400/5 disabled:opacity-35">Delete</button>
                      </div>
                    </div>
                  );
                }) : (
                  <div className="rounded-xl border border-dashed border-white/10 p-5 text-center text-sm text-white/40">No rules configured yet.</div>
                )}
              </div>
            </div>

            <div className="mt-5 rounded-2xl border border-cyan-400/20 bg-cyan-400/5 p-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-cyan-200">Rule Preview</div>
                  <div className="mt-1 text-sm text-white/55">See the winning rule for each date before Neo changes the schedule.</div>
                </div>
                <button
                  type="button"
                  onClick={previewStaffRules}
                  disabled={ruleSaving || !(rulesByStaff[rulesModalPerson.id] || []).length}
                  className="rounded-xl border border-cyan-400/30 bg-cyan-400/10 px-4 py-2.5 text-sm font-black text-cyan-100 disabled:opacity-35"
                >
                  Preview Rules
                </button>
              </div>

              {rulePreview ? (
                <div className="mt-4">
                  <div className="flex flex-wrap gap-2">
                    <Pill tone={rulePreview.rows.length ? "amber" : "green"}>{rulePreview.rows.length} change{rulePreview.rows.length === 1 ? "" : "s"}</Pill>
                    <Pill tone="slate">{rulePreview.unchanged.length} already matching</Pill>
                  </div>

                  {rulePreview.rows.length ? (
                    <>
                      <div className="mt-3 rounded-xl border border-white/10 bg-slate-950/20 p-3">
                        <div className="text-xs font-bold uppercase tracking-wider text-white/50">Apply behavior</div>
                        <div className="mt-2 grid gap-2 sm:grid-cols-2">
                          <button type="button" onClick={() => setRuleApplyMode("empty_only")} className={`rounded-xl border px-3 py-3 text-left ${ruleApplyMode === "empty_only" ? "border-cyan-400/40 bg-cyan-400/10" : "border-white/10 bg-white/[.02]"}`}>
                            <div className="text-sm font-black text-white">Fill empty cells only</div>
                            <div className="mt-1 text-xs text-white/45">Safest. Existing manual shifts, OFF days and leave stay untouched.</div>
                          </button>
                          <button type="button" onClick={() => setRuleApplyMode("overwrite")} className={`rounded-xl border px-3 py-3 text-left ${ruleApplyMode === "overwrite" ? "border-amber-400/40 bg-amber-400/10" : "border-white/10 bg-white/[.02]"}`}>
                            <div className="text-sm font-black text-white">Overwrite matching dates</div>
                            <div className="mt-1 text-xs text-white/45">Rules replace any existing value on dates they control.</div>
                          </button>
                        </div>
                        <div className="mt-2 text-xs text-white/45">{applicableRuleRows().length} cells will be changed · {Math.max(0, rulePreview.rows.length - applicableRuleRows().length)} existing cells protected.</div>
                      </div>
                      <div className="mt-3 max-h-72 overflow-y-auto rounded-xl border border-white/10">
                        {rulePreview.rows.slice(0, 31).map((row) => {
                          const details = describeRule(row.rule);
                          return (
                            <div key={row.dateKey} className="grid grid-cols-1 gap-2 border-b border-white/10 px-3 py-3 last:border-b-0 sm:grid-cols-[110px_1fr_auto] sm:items-center">
                              <div>
                                <div className="text-sm font-black text-white">{row.date.toLocaleDateString("en-ZA", { weekday: "short", day: "numeric" })}</div>
                                <div className="text-[11px] text-white/35">{row.dateKey}</div>
                              </div>
                              <div className="text-xs text-white/60">
                                <span className="text-white/40">{row.currentValue || "Empty"}</span>
                                <span className="mx-2 text-cyan-300">→</span>
                                <span className="font-bold text-white">{row.nextValue || "Empty"}</span>
                                <div className="mt-1 text-[11px] text-white/35">{details.scopeLabel}{row.shadowedRuleCount ? ` · overrides ${row.shadowedRuleCount} lower rule${row.shadowedRuleCount === 1 ? "" : "s"}` : ""}</div>
                              </div>
                              <Pill tone={row.rule.scope === "specific_date" ? "rose" : row.rule.scope === "week" ? "amber" : "blue"}>
                                {row.rule.scope === "specific_date" ? "Date" : row.rule.scope === "week" ? `Week ${row.rule.weekNumber}` : "Month"}
                              </Pill>
                            </div>
                          );
                        })}
                      </div>
                      <button
                        type="button"
                        onClick={applyRulePreview}
                        disabled={!applicableRuleRows().length}
                        className="mt-3 w-full rounded-xl border border-emerald-400/30 bg-emerald-400/10 px-4 py-3 text-sm font-black text-emerald-100 disabled:opacity-35"
                      >
                        Apply {applicableRuleRows().length} Change{applicableRuleRows().length === 1 ? "" : "s"} to Schedule
                      </button>
                      <div className="mt-2 text-[11px] leading-5 text-white/40">Every application is recorded in Schedule History with the before/after value for each cell.</div>
                      <button
                        type="button"
                        onClick={undoLastRuleApplication}
                        disabled={ruleUndoState === "loading"}
                        className="mt-3 w-full rounded-xl border border-white/15 bg-white/[.03] px-4 py-2.5 text-sm font-bold text-white disabled:opacity-35"
                      >
                        {ruleUndoState === "loading" ? "Finding last application…" : ruleUndoState === "done" ? "Last Rule Application Undone" : "Undo Last Rule Application"}
                      </button>
                    </>
                  ) : (
                    <div className="mt-3 rounded-xl border border-emerald-400/20 bg-emerald-400/5 p-4 text-sm text-emerald-100">The current schedule already matches these rules.</div>
                  )}
                </div>
              ) : null}
            </div>

            <div className="mt-5 flex justify-end">
              <button type="button" onClick={() => setRulesModalPerson(null)} className="rounded-xl border border-white/10 px-4 py-2.5 text-sm font-bold text-white hover:bg-white/5">Done</button>
            </div>
          </div>
        </div>
      ) : null}

      {!readOnly && ruleDeleteConfirm && rulesModalPerson ? (
        <div className="fixed inset-0 z-[3270] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm" onMouseDown={() => setRuleDeleteConfirm(null)}>
          <div className="w-full max-w-lg rounded-2xl border border-rose-400/25 bg-[#0b1b30] p-5 shadow-2xl" onMouseDown={(e) => e.stopPropagation()}>
            <div className="text-xs font-bold uppercase tracking-[.16em] text-rose-300">Confirm rule deletion</div>
            <h3 className="mt-2 text-xl font-black text-white">Delete {ruleDeleteConfirm.label}?</h3>
            <p className="mt-3 text-sm leading-6 text-white/65">
              Neo will remove this rule for <span className="font-bold text-white">{rulesModalPerson.name}</span>. Existing schedule cells will not change until you preview and apply the remaining rules.
            </p>
            <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button type="button" onClick={() => setRuleDeleteConfirm(null)} className="rounded-xl border border-white/10 px-4 py-2.5 text-sm font-bold text-white/70 hover:bg-white/5">Cancel</button>
              <button type="button" onClick={() => removeStaffRule(ruleDeleteConfirm.ruleId, true)} className="rounded-xl border border-rose-400/30 bg-rose-400/10 px-4 py-2.5 text-sm font-black text-rose-100 hover:bg-rose-400/15">Yes, delete rule</button>
            </div>
          </div>
        </div>
      ) : null}

      {!readOnly && ruleUndoConfirmOpen && rulesModalPerson ? (
        <div className="fixed inset-0 z-[3270] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm" onMouseDown={() => setRuleUndoConfirmOpen(false)}>
          <div className="w-full max-w-lg rounded-2xl border border-amber-400/25 bg-[#0b1b30] p-5 shadow-2xl" onMouseDown={(e) => e.stopPropagation()}>
            <div className="text-xs font-bold uppercase tracking-[.16em] text-amber-300">Confirm rule undo</div>
            <h3 className="mt-2 text-xl font-black text-white">Undo the most recent rule application?</h3>
            <p className="mt-3 text-sm leading-6 text-white/65">Neo will restore <span className="font-bold text-white">{rulesModalPerson.name}</span>&apos;s schedule cells to the values they had immediately before the most recent rule application.</p>
            <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button type="button" onClick={() => setRuleUndoConfirmOpen(false)} className="rounded-xl border border-white/10 px-4 py-2.5 text-sm font-bold text-white/70 hover:bg-white/5">Cancel</button>
              <button type="button" onClick={() => undoLastRuleApplication(true)} className="rounded-xl border border-amber-400/30 bg-amber-400/10 px-4 py-2.5 text-sm font-black text-amber-100 hover:bg-amber-400/15">Yes, undo application</button>
            </div>
          </div>
        </div>
      ) : null}

      {!readOnly && ruleCopyConfirmOpen && rulesModalPerson ? (
        <div className="fixed inset-0 z-[3260] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm" onMouseDown={() => setRuleCopyConfirmOpen(false)}>
          <div
            className="w-full max-w-lg rounded-2xl border border-violet-400/25 bg-[#0b1b30] p-5 shadow-2xl"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="text-xs font-bold uppercase tracking-[.16em] text-violet-300">Copy previous month rules</div>
            <h3 className="mt-2 text-xl font-black text-white">Keep current rules and add reusable patterns?</h3>
            <p className="mt-3 text-sm leading-6 text-white/65">
              <span className="font-bold text-white">{rulesModalPerson.name}</span> already has {(rulesByStaff[rulesModalPerson.id] || []).length} rule{(rulesByStaff[rulesModalPerson.id] || []).length === 1 ? "" : "s"} for {formatMonth(monthKey)}.
              Neo will keep {(rulesByStaff[rulesModalPerson.id] || []).length === 1 ? "it" : "them"} and add only non-duplicate recurring rules from <span className="font-bold text-violet-100">{formatMonth(addMonthsToKey(monthKey, -1))}</span>. Specific-date rules will not be copied.
            </p>
            <div className="mt-4 rounded-xl border border-violet-400/15 bg-violet-400/5 px-3 py-2.5 text-xs leading-5 text-violet-100/75">
              Nothing is applied to the roster yet. After copying, you can preview the rules before changing any schedule cells.
            </div>
            <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setRuleCopyConfirmOpen(false)}
                className="rounded-xl border border-white/10 px-4 py-2.5 text-sm font-bold text-white/70 hover:bg-white/5"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={copyPreviousMonthRules}
                className="rounded-xl border border-violet-400/30 bg-violet-400/10 px-4 py-2.5 text-sm font-black text-violet-100 hover:bg-violet-400/15"
              >
                Copy {formatMonth(addMonthsToKey(monthKey, -1))} Rules
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {!readOnly && editing ? (
        <div className="fixed inset-0 z-[3100] flex items-start justify-center overflow-y-auto bg-black/70 p-2 sm:items-center sm:p-4" onMouseDown={() => setEditing(null)} style={{ WebkitOverflowScrolling: "touch" }}>
          <div
            className="my-2 w-full max-w-xl overflow-y-auto rounded-2xl border border-white/10 p-4 shadow-2xl sm:my-0 sm:p-5"
            style={{ background: "#0b1b30", maxHeight: "calc(100dvh - 16px)", WebkitOverflowScrolling: "touch" }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-xs font-bold uppercase tracking-[.14em] text-cyan-300">Edit shift</div>
                <h3 className="mt-1 text-xl font-black text-white">{editing.person.name}</h3>
                <div className="mt-1 text-sm text-white/55">
                  {editing.date.toLocaleDateString("en-ZA", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
                </div>
              </div>
              <button onClick={() => setEditing(null)} className="rounded-lg border border-white/10 px-3 py-1.5 text-white/70">✕</button>
            </div>

            <div className="mt-5">
              <div className="mb-2 flex items-center justify-between gap-3">
                <div className="text-xs font-bold uppercase tracking-wider text-white/50">Quick shifts</div>
                <div className="text-[11px] text-white/35">Add/remove presets for this store</div>
              </div>

              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {presets.map(([value, label]) => (
                  <div key={value} className="relative">
                    <button
                      type="button"
                      onClick={() => {
                        setShift(editing.person.id, editing.dateKey, value);
                        setPendingAttendanceId("");
                        setPendingAttendanceHours("");
                        setEditing(null);
                      }}
                      className="w-full rounded-xl border border-white/10 px-3 py-3 text-left hover:border-cyan-300/40 hover:bg-cyan-400/5"
                    >
                      <div className={`font-bold ${value === "OFF" ? "text-amber-200" : "text-white"}`}>{value}</div>
                      <div className="mt-1 text-xs text-white/45">{label}</div>
                    </button>

                    {value !== "OFF" ? (
                      <button
                        type="button"
                        title="Remove preset"
                        onClick={(e) => {
                          e.stopPropagation();
                          removePreset(value);
                        }}
                        className="absolute right-2 top-2 rounded-full border border-white/10 bg-slate-950/80 px-1.5 py-0.5 text-[10px] font-bold text-white/45 hover:border-rose-300/40 hover:text-rose-200"
                      >
                        ×
                      </button>
                    ) : null}
                  </div>
                ))}
              </div>

              <div className="mt-3 rounded-xl border border-white/10 p-3">
                <div className="text-xs font-bold uppercase tracking-wider text-white/50">Add quick shift</div>
                <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-[1fr_110px_110px_auto]">
                  <input
                    type="text"
                    value={newPresetLabel}
                    onChange={(e) => setNewPresetLabel(e.target.value)}
                    placeholder="Label, e.g. Mid shift"
                    className="rounded-lg border border-white/10 bg-slate-950/70 px-3 py-2 text-sm text-white"
                  />
                  <input
                    type="time"
                    value={newPresetStart}
                    onChange={(e) => setNewPresetStart(e.target.value)}
                    className="rounded-lg border border-white/10 bg-slate-950/70 px-3 py-2 text-sm text-white"
                  />
                  <input
                    type="time"
                    value={newPresetEnd}
                    onChange={(e) => setNewPresetEnd(e.target.value)}
                    className="rounded-lg border border-white/10 bg-slate-950/70 px-3 py-2 text-sm text-white"
                  />
                  <button
                    type="button"
                    onClick={addPreset}
                    className="rounded-lg border border-emerald-400/30 bg-emerald-400/10 px-3 py-2 text-sm font-bold text-emerald-100"
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>

            {attendanceTypes.filter((row) => row.showOnSchedule !== false).length ? (
              <div className="mt-5 rounded-xl border border-violet-400/20 bg-violet-500/5 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-xs font-bold uppercase tracking-wider text-violet-200">Leave & attendance</div>
                    <div className="mt-1 text-xs text-white/45">Configured in Settings → Leave & attendance types.</div>
                  </div>
                </div>

                <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {attendanceTypes
                    .filter((row) => row.showOnSchedule !== false)
                    .map((row) => (
                      <button
                        key={row.id}
                        type="button"
                        onClick={() => {
                          if (row.allocateHours) {
                            setPendingAttendanceId(row.id);
                            setPendingAttendanceHours(String(Number(row.defaultHours || 0)));
                            return;
                          }
                          setShift(editing.person.id, editing.dateKey, attendanceValue(row.id, 0));
                          setEditing(null);
                        }}
                        className="rounded-xl border border-violet-300/15 bg-violet-400/5 px-3 py-3 text-left transition hover:border-violet-300/40 hover:bg-violet-400/10"
                      >
                        <div className="font-bold text-violet-100">{row.label}</div>
                        <div className="mt-1 text-[11px] text-white/45">
                          {row.paid === false ? "Unpaid" : "Paid"}
                          {" · "}
                          {row.allocateHours ? `${Number(row.defaultHours || 0)}h` : "No hours"}
                          {row.requiresApproval ? " · Approval" : ""}
                        </div>
                      </button>
                    ))}
                </div>
              </div>
            ) : null}

            {pendingAttendanceId ? (() => {
              const selectedAttendance = attendanceTypes.find((row) => row.id === pendingAttendanceId);
              if (!selectedAttendance?.allocateHours) return null;
              return (
                <div className="mt-4 rounded-xl border border-violet-400/25 bg-violet-400/5 p-4">
                  <div className="text-xs font-bold uppercase tracking-wider text-violet-200">Leave hours for this day</div>
                  <div className="mt-1 text-xs text-white/55">
                    Settings default: {Number(selectedAttendance.defaultHours || 0)}h. Change it only for this date if needed.
                  </div>
                  <div className="mt-3 grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
                    <label>
                      <span className="text-xs text-white/55">Allocated hours</span>
                      <input type="number" min="0" max="24" step="0.25" value={pendingAttendanceHours} onChange={(e) => setPendingAttendanceHours(e.target.value)} className="mt-1 w-full rounded-lg border border-white/10 bg-slate-950/70 px-3 py-2 text-white" />
                    </label>
                    <button type="button" onClick={() => {
                      const hours = Math.max(0, Number(pendingAttendanceHours) || 0);
                      setShift(editing.person.id, editing.dateKey, attendanceValue(pendingAttendanceId, hours));
                      setEditing(null);
                      setPendingAttendanceId("");
                      setPendingAttendanceHours("");
                    }} className="rounded-xl border border-violet-400/30 bg-violet-400/10 px-4 py-2.5 text-sm font-bold text-violet-100">
                      Apply {selectedAttendance.label}
                    </button>
                  </div>
                </div>
              );
            })() : null}

            <div className="mt-5 rounded-xl border border-white/10 p-4">
              <div className="text-xs font-bold uppercase tracking-wider text-white/55">Custom shift</div>
              <div className="mt-3 grid grid-cols-2 gap-3">
                <label>
                  <span className="text-xs text-white/50">Start</span>
                  <input type="time" value={customStart} onChange={(e) => setCustomStart(e.target.value)} className="mt-1 w-full rounded-lg border border-white/10 bg-slate-950/70 px-3 py-2 text-white"/>
                </label>
                <label>
                  <span className="text-xs text-white/50">End</span>
                  <input type="time" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} className="mt-1 w-full rounded-lg border border-white/10 bg-slate-950/70 px-3 py-2 text-white"/>
                </label>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShift(editing.person.id, editing.dateKey, `${customStart}-${customEnd}`);
                  setPendingAttendanceId("");
                  setPendingAttendanceHours("");
                  setEditing(null);
                }}
                className="mt-3 w-full rounded-xl border border-cyan-400/30 bg-cyan-400/10 px-4 py-2.5 text-sm font-bold text-cyan-100"
              >
                Apply custom shift
              </button>
              {editing.current ? (
                <button
                  type="button"
                  onClick={() => {
                    setShift(editing.person.id, editing.dateKey, "");
                    setPendingAttendanceId("");
                    setPendingAttendanceHours("");
                    setEditing(null);
                  }}
                  className="mt-2 w-full rounded-xl border border-rose-400/20 px-4 py-2.5 text-sm font-bold text-rose-200"
                >
                  Clear this shift
                </button>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// Duty ownership is resolved once, server-side, from Standard role + live active-shift role.
// Read-only permission diagnostic, invoked ONLY after an attendance decision batch fails.
// A denied batch does not identify which operation failed. Inspect the precise
// existing documents and the active Firebase identity without writing test data,
// broadening rules, or replaying an attendance decision.
async function diagnoseNeoVarianceBatch({ orgId, storeId, scheduleEntryId, actual, historyRef, action }) {
  const authUser = getAuth(db.app).currentUser;
  const uid = authUser?.uid || "";
  const shiftId = String(actual?.id || "").replace(/^(nested|legacy):/, "");
  const shiftRef = actual?.source === "nested" && actual?.storeId && shiftId
    ? doc(db, "orgs", orgId, "stores", actual.storeId, "shifts", shiftId)
    : actual?.source === "legacy" && shiftId
    ? doc(db, "cashup_shifts", shiftId)
    : null;
  const checks = [
    { label: "activeUidMember", ref: uid ? doc(db, "orgs", orgId, "members", uid) : null },
    { label: "scheduleEntry", ref: scheduleEntryId ? doc(db, "orgs", orgId, "neo_schedule_entries", scheduleEntryId) : null },
    { label: "originalShift", ref: shiftRef },
    { label: "historyWritePath", ref: historyRef || null },
  ];
  const results = await Promise.all(checks.map(async ({ label, ref }) => {
    if (!ref) return { label, status: "no-document-reference" };
    try {
      const snap = await getDoc(ref);
      const data = snap.exists() ? snap.data() || {} : {};
      const result = { label, path: ref.path, status: "read-allowed", exists: snap.exists() };
      if (label === "activeUidMember" && snap.exists()) {
        result.role = data.role || "missing";
        result.disabled = data.disabled === true;
      }
      if (label === "scheduleEntry" && snap.exists()) {
        result.storedStoreId = String(data.storeId || "");
        result.storeIdMatches = String(data.storeId || "") === String(storeId || "");
        result.published = data.published === true;
      }
      return result;
    } catch (error) {
      return { label, path: ref.path, status: "read-failed", code: error?.code || "unknown", message: error?.message || String(error) };
    }
  }));
  console.error("[Neo Schedule] Decision batch diagnostic — READ ONLY", {
    action,
    authUid: uid || "not-signed-in-in-this-firebase-app",
    authOrgClaim: authUser ? (await authUser.getIdTokenResult().catch(() => null))?.claims?.orgId || "missing" : "missing",
    orgId,
    storeId,
    source: actual?.source || "unknown",
    firebaseProjectId: db.app.options.projectId,
    checks: results,
    note: "Reads only. A successful read does not prove the corresponding write is authorised. No attendance record was changed by this diagnostic.",
  });
}

const SERVER_DUTY_ROUTING_AUTHORITY = true;

export default function ScheduleIntelligenceTab({ siteScope = { allSites: true, siteIds: [] }, canViewAttendancePhotos = false }) {
  const { claims } = useAuth();
  const norm = normalizeClaims(claims || {});
  const orgId = norm.orgId || "";
  const actor = {
    uid: claims?.uid || claims?.user_id || "",
    email: claims?.email || "",
    name: claims?.name || claims?.displayName || claims?.email || "",
  };
  const [view, setView] = useState("today");
  const [stores, setStores] = useState([]);
  const [roles, setRoles] = useState([]);
  const [staff, setStaff] = useState([]);
  const [publicHolidays, setPublicHolidays] = useState(new Set());
  const [attendanceTypes, setAttendanceTypes] = useState(DEFAULT_ATTENDANCE_TYPES);
  const [lunchBreakMinutes, setLunchBreakMinutes] = useState(() => {
    const rawLocal = localStorage.getItem("labor_lunchMin");
    if (rawLocal === null || rawLocal === "") return 60;
    const local = Number(rawLocal);
    return Number.isFinite(local) && local >= 0 ? local : 60;
  });
  const [builderOpen, setBuilderOpen] = useState(false);
  const [builderStoreId, setBuilderStoreId] = useState("");
  const [builderMonthKey, setBuilderMonthKey] = useState(() => monthKeyFor(new Date()));
  const [builderReadOnly, setBuilderReadOnly] = useState(false);
  const [scheduleMonths, setScheduleMonths] = useState([]);
  const [newScheduleOpen, setNewScheduleOpen] = useState(false);
  const [newScheduleMonthKey, setNewScheduleMonthKey] = useState(() => addMonthsToKey(monthKeyFor(new Date()), 1));
  const [todaySchedules, setTodaySchedules] = useState([]);
  const [todayActualShifts, setTodayActualShifts] = useState([]);
  const [todayLoading, setTodayLoading] = useState(false);
  const [todayError, setTodayError] = useState("");
  const [attendanceRefreshFeedback, setAttendanceRefreshFeedback] = useState("");
  const attendanceRefreshTimerRef = useRef(null);
  const [todayNow, setTodayNow] = useState(() => Date.now());
  const [todayGraceMinutes, setTodayGraceMinutes] = useState(() => {
    const raw = localStorage.getItem("neo_schedule_grace_minutes");
    const value = Number(raw);
    return [0, 5, 10, 15, 30].includes(value) ? value : 5;
  });
  const [todayEscalationMinutes, setTodayEscalationMinutes] = useState(() => {
    const raw = Number(localStorage.getItem("neo_schedule_escalation_minutes"));
    return Number.isFinite(raw) && raw >= 0 ? Math.round(raw) : 15;
  });
  const todayEscalationInputRef = useRef(null);
  const todayEscalationEditingRef = useRef(false);
  const todayEscalationPendingRef = useRef(null);
  const [todayEscalationSaved, setTodayEscalationSaved] = useState(false);
  const todayEscalationSavedTimerRef = useRef(null);
  const [dutyQueueRows, setDutyQueueRows] = useState([]);
  const [dutyResponseSettings, setDutyResponseSettings] = useState({
    whatsappDutyDueEnabled: true,
    whatsappDutyDueTemplate: "neo_duty_due",
    whatsappDutyDueLanguage: "en",
    neoStaffAppUrl: "https://neo.matrixos.co.za",
    defaultRepeatEveryMins: "10",
    quietHoursStart: "",
    quietHoursEnd: "",
  });
  const [varianceApprovalState, setVarianceApprovalState] = useState({});
  const [swapDecisionState, setSwapDecisionState] = useState({});
  const exceptionCardFilterRef = useRef("");
  const todayCardFilterRef = useRef("");
  const [swapConfirm, setSwapConfirm] = useState(null);
  const [varianceConfirm, setVarianceConfirm] = useState(null);
  const applyInstantCardFilter = useCallback((scope, filterKey) => {
    const filterRef = scope === "today" ? todayCardFilterRef : exceptionCardFilterRef;
    const next = filterRef.current === filterKey ? "" : filterKey;
    filterRef.current = next;

    const cardSelector = scope === "today" ? "[data-today-filter-card]" : "[data-exception-filter-card]";
    const rowSelector = scope === "today" ? "[data-today-filter-row]" : "[data-exception-filter-row]";

    document.querySelectorAll(cardSelector).forEach((node) => {
      const key = node.getAttribute(scope === "today" ? "data-today-filter-card" : "data-exception-filter-card");
      const selected = !!next && key === next;
      node.setAttribute("aria-pressed", selected ? "true" : "false");
      node.style.borderColor = selected ? "rgba(103,232,249,.78)" : "rgba(96,165,250,.20)";
      node.style.background = selected
        ? "linear-gradient(145deg, rgba(8,73,91,.94), rgba(8,35,54,.98))"
        : "linear-gradient(145deg, rgba(15,37,65,.92), rgba(8,20,38,.96))";
      node.style.boxShadow = selected
        ? "0 0 26px rgba(34,211,238,.32)"
        : "0 18px 50px rgba(0,0,0,.18)";
      node.style.transform = selected ? "translateY(-2px)" : "";
    });

    document.querySelectorAll(rowSelector).forEach((node) => {
      if (!next) {
        node.style.display = "";
        return;
      }

      const rowType = node.getAttribute(scope === "today" ? "data-today-filter-row" : "data-exception-filter-row") || "";
      let visible = false;

      if (scope === "today") {
        visible = next === "scheduled"
          ? node.getAttribute("data-expects-clock") === "true"
          : next === "on-time"
          ? node.getAttribute("data-arrival-on-time") === "true"
          : rowType === next;
      } else if (next === "critical") {
        visible = ["no-show", "missing-owner", "swap", "previous-shift-open"].includes(rowType);
      } else {
        visible = rowType === next;
      }

      node.style.display = visible ? "" : "none";
    });
  }, []);

  const [scheduleHistory, setScheduleHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [historyStoreId, setHistoryStoreId] = useState("ALL");
  const [historyPeriod, setHistoryPeriod] = useState("today");
  const [exceptionPeriod, setExceptionPeriod] = useState("today");
  const [leavePeriod, setLeavePeriod] = useState("this-month");
  const [historicalPendingOvertime, setHistoricalPendingOvertime] = useState([]);
  const [historicalExceptionLoading, setHistoricalExceptionLoading] = useState(false);
  const [historicalExceptionRefreshKey, setHistoricalExceptionRefreshKey] = useState(0);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [leaveLoading, setLeaveLoading] = useState(true);
  const [selectedLeaveId, setSelectedLeaveId] = useState("");
  const [leaveDecisionNote, setLeaveDecisionNote] = useState("");
  const [leaveDecisionState, setLeaveDecisionState] = useState({});
  const [leaveSyncState, setLeaveSyncState] = useState({});

  // LR8 — Opening balances & auditable manual adjustments. These are deliberately
  // on-demand rather than realtime: leave balances change rarely and do not justify
  // another permanent listener on Schedule Intelligence.
  const [leaveBalanceRows, setLeaveBalanceRows] = useState([]);
  const [leaveBalanceLoading, setLeaveBalanceLoading] = useState(false);
  const [leaveBalanceError, setLeaveBalanceError] = useState("");
  const [leaveBalanceEditor, setLeaveBalanceEditor] = useState(null);
  const [leaveBalanceMode, setLeaveBalanceMode] = useState("opening");
  const [leaveBalanceTypeId, setLeaveBalanceTypeId] = useState("annual-leave");
  const [leaveBalanceAmount, setLeaveBalanceAmount] = useState("");
  const [leaveBalanceDirection, setLeaveBalanceDirection] = useState("add");
  const [leaveBalanceEffectiveDate, setLeaveBalanceEffectiveDate] = useState(() => localTodayKey());
  const [leaveBalanceReason, setLeaveBalanceReason] = useState("");
  const [leaveBalanceSaveState, setLeaveBalanceSaveState] = useState("idle");
  const [leaveLedgerStaff, setLeaveLedgerStaff] = useState(null);
  const [leaveLedgerRows, setLeaveLedgerRows] = useState([]);
  const [leaveLedgerLoading, setLeaveLedgerLoading] = useState(false);
  const [leaveBalanceSearch, setLeaveBalanceSearch] = useState("");
  const [expandedLeaveBalanceStaff, setExpandedLeaveBalanceStaff] = useState(() => new Set());
  const leaveBalanceCacheRef = useRef(new Map());

  useEffect(() => {
    if (view === "builder" && builderStoreId === "ALL" && stores[0]?.id) {
      setBuilderStoreId(stores[0].id);
    }
  }, [view, builderStoreId, stores]);

  useEffect(() => {
    if (!orgId) return undefined;
    let cancelled = false;

    // C7.3 — Schedule reference data is FRESH, not LIVE. Stores, roles, staff and
    // labor settings are loaded once when Schedule Intelligence is opened instead
    // of holding four permanent listeners for the lifetime of this route.
    const loadScheduleReferenceData = async () => {
      try {
        const [storesSnap, rolesSnap, staffSnap, laborSnap, responseSnap] = await Promise.all([
          getDocs(collection(db, "orgs", orgId, "cashup_stores")),
          getDocs(collection(db, "orgs", orgId, "cashup_roles")),
          getDocs(collection(db, "orgs", orgId, "cashup_users")),
          getDoc(doc(db, "orgs", orgId, "settings", "labor")),
          getDoc(doc(db, "orgs", orgId, "neo_settings", "staffDashboardResponse")),
        ]);
        if (cancelled) return;

        const allowedSites = new Set(siteScope.siteIds || []);
        const nextStores = storesSnap.docs
          .map((d) => ({ id: d.id, ...(d.data() || {}) }))
          .filter((x) => !x.archived && !x.isArchived)
          .filter((x) => siteScope.allSites || allowedSites.has(x.id))
          .sort((a,b) => String(a.name || "").localeCompare(String(b.name || "")));
        setStores(nextStores);
        setBuilderStoreId((current) => current || "ALL");
        setRoles(rolesSnap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) })).filter((x) => !x.archived && !x.isArchived));
        setStaff(staffSnap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) })).filter((x) => !x.archived && !x.isArchived).filter((x) => siteScope.allSites || allowedSites.has(x.storeId)));

        const data = laborSnap.data() || {};
        setPublicHolidays(new Set(Array.isArray(data.publicHolidays) ? data.publicHolidays : []));
        setAttendanceTypes(Array.isArray(data.attendanceTypes) && data.attendanceTypes.length ? data.attendanceTypes : DEFAULT_ATTENDANCE_TYPES);
        const rawGrace = Number(data.scheduleGraceMinutes);
        if ([0, 5, 10, 15, 30].includes(rawGrace)) {
          setTodayGraceMinutes(rawGrace);
          localStorage.setItem("neo_schedule_grace_minutes", String(rawGrace));
        }
        const rawEscalation = Number(data.scheduleEscalationMinutes);
        if (Number.isFinite(rawEscalation) && rawEscalation >= 0) {
          const normalizedEscalation = Math.round(rawEscalation);
          setTodayEscalationMinutes(normalizedEscalation);
          localStorage.setItem("neo_schedule_escalation_minutes", String(normalizedEscalation));
          if (!todayEscalationEditingRef.current && todayEscalationInputRef.current) {
            todayEscalationInputRef.current.value = String(normalizedEscalation);
          }
        }
        const rawSharedLunch = data.lunchBreakMinutes ?? data.lunchMin ?? data.lunchMinutes;
        if (rawSharedLunch !== undefined && rawSharedLunch !== null && rawSharedLunch !== "") {
          const sharedLunch = Number(rawSharedLunch);
          if (Number.isFinite(sharedLunch) && sharedLunch >= 0) {
            setLunchBreakMinutes(Math.round(sharedLunch));
            localStorage.setItem("labor_lunchMin", String(Math.round(sharedLunch)));
          }
        }

        const responseData = responseSnap.data() || {};
        setDutyResponseSettings((current) => ({
          ...current,
          ...responseData,
          whatsappDutyDueEnabled: typeof responseData.whatsappDutyDueEnabled === "boolean" ? responseData.whatsappDutyDueEnabled : true,
          whatsappDutyDueTemplate: responseData.whatsappDutyDueTemplate || current.whatsappDutyDueTemplate || "neo_duty_due",
          whatsappDutyDueLanguage: responseData.whatsappDutyDueLanguage || current.whatsappDutyDueLanguage || "en",
          neoStaffAppUrl: responseData.neoStaffAppUrl || current.neoStaffAppUrl || "https://neo.matrixos.co.za",
        }));
      } catch (error) {
        console.error("Could not load Schedule Intelligence reference data", error);
      }
    };

    loadScheduleReferenceData();
    return () => { cancelled = true; };
  }, [orgId, siteScope.allSites, siteScope.siteIds]);

  const historyStaffName = useCallback((staffId, candidateName = "") => {
    const candidate = String(candidateName || "").trim();
    const candidateIsPlaceholder =
      !candidate || candidate.toLowerCase() === "staff member" || candidate === "—";

    const byId = staffId ? staff.find((person) => person.id === staffId) : null;
    if (byId?.name) return byId.name;

    if (!candidateIsPlaceholder) {
      const byName = staff.find(
        (person) => String(person.name || "").trim().toLowerCase() === candidate.toLowerCase()
      );
      return byName?.name || candidate;
    }

    return "Staff member";
  }, [staff]);

  const roleNameForStaff = useCallback((staffId, staffName = "") => {
    const byId = staffId ? staff.find((person) => String(person.id) === String(staffId)) : null;
    const byName = !byId && staffName
      ? staff.find((person) => String(person.name || "").trim().toLowerCase() === String(staffName).trim().toLowerCase())
      : null;
    const person = byId || byName || {};
    const role = person.roleId ? roles.find((item) => String(item.id) === String(person.roleId)) : null;
    return String(role?.name || person.roleName || person.role || "").trim();
  }, [staff, roles]);

  const roleIdForStaff = useCallback((staffId, staffName = "") => {
    const byId = staffId ? staff.find((person) => String(person.id) === String(staffId)) : null;
    const byName = !byId && staffName
      ? staff.find((person) => String(person.name || "").trim().toLowerCase() === String(staffName).trim().toLowerCase())
      : null;
    return String((byId || byName || {}).roleId || "").trim();
  }, [staff]);

  useEffect(() => {
    // C7.3 — Historical exception reconstruction is expensive. Keep it DEAD until
    // the manager explicitly opens Exceptions.
    if (view !== "exceptions") return undefined;
    if (!orgId || !stores.length) {
      setHistoricalPendingOvertime([]);
      return undefined;
    }
    let cancelled = false;
    const loadHistoricalPending = async () => {
      setHistoricalExceptionLoading(true);
      try {
        const [periodStart, periodEnd] = schedulePeriodRange(exceptionPeriod);
        const startMs = periodStart.getTime();
        const endMs = periodEnd.getTime();

        const scheduleSnap = await getDocs(collection(db, "orgs", orgId, "neo_schedule_entries")).catch(() => null);
        const scheduleMap = new Map();
        scheduleSnap?.docs?.forEach((scheduleDoc) => {
          const data = scheduleDoc.data() || {};
          const dateKey = String(data.date || "");
          const storeId = String(data.storeId || "");
          if (!dateKey || !storeId || data.published !== true) return;
          const publishedShift = String(
            (data.varianceApproved === true && data.originalPublishedShift ? data.originalPublishedShift : data.publishedShift) || data.shift || ""
          );
          if (!/^\d{2}:\d{2}-\d{2}:\d{2}$/.test(publishedShift)) return;
          const staffId = String(data.staffId || "");
          const staffName = String(data.staffName || data.name || "").trim().toLowerCase();
          if (staffId) scheduleMap.set(`${storeId}|id:${staffId}|${dateKey}`, publishedShift);
          if (staffName) scheduleMap.set(`${storeId}|name:${staffName}|${dateKey}`, publishedShift);
        });

        const nestedLists = await Promise.all(stores.map(async (store) => {
          const snap = await getDocs(collection(db, "orgs", orgId, "stores", store.id, "shifts")).catch(() => null);
          return (snap?.docs || []).map((shiftDoc) => ({
            source: "nested", docId: shiftDoc.id, forcedStoreId: store.id, forcedStoreName: store.name || "", data: shiftDoc.data() || {},
          }));
        }));
        const legacySnap = await getDocs(query(collection(db, "cashup_shifts"), where("orgId", "==", orgId))).catch(() => null);
        const rawRows = [
          ...nestedLists.flat(),
          ...(legacySnap?.docs || []).map((shiftDoc) => ({ source: "legacy", docId: shiftDoc.id, forcedStoreId: "", forcedStoreName: "", data: shiftDoc.data() || {} })),
        ];

        const storeNameById = new Map(stores.map((store) => [String(store.id), String(store.name || store.id)]));
        const storeIdByNameForDecisions = new Map(
          stores.map((store) => [String(store.name || "").trim().toLowerCase(), String(store.id)])
        );

        // MS3.2:
        // A physical shift can exist in both the nested shift collection and
        // legacy cashup_shifts. If either copy has already been approved or
        // rejected, the exception is resolved. Build this set BEFORE filtering
        // individual rows so an unapproved legacy duplicate cannot resurrect an
        // already-resolved nested exception.
        const resolvedHistoricalShiftKeys = new Set();

        const historicalShiftAliases = ({ data = {}, raw = {}, clockInSec = 0, storeId = "" }) => {
          const staffId = String(
            data.staffId || data.userId || data.userUid || data.uid || data.employeeId || ""
          ).trim();
          const staffName = String(
            data.name ||
            data.staffName ||
            data.staffNameSnapshot ||
            data.employeeName ||
            data.displayName ||
            ""
          ).trim().toLowerCase();

          const rawStore = String(
            storeId || raw.forcedStoreId || data.storeId || data.store || ""
          ).trim();
          const normalizedStoreId =
            storeNameById.has(rawStore)
              ? rawStore
              : (storeIdByNameForDecisions.get(rawStore.toLowerCase()) || rawStore);

          const aliases = [];
          // Exact identity aliases.
          if (staffId) aliases.push(`id:${staffId}|${clockInSec}|${normalizedStoreId}`);
          if (staffName) aliases.push(`name:${staffName}|${clockInSec}|${normalizedStoreId}`);

          // Cross-collection fallback: the same physical clock-in is the strongest
          // common identifier when legacy and nested copies use different staff/store IDs.
          if (staffId) aliases.push(`id:${staffId}|${clockInSec}`);
          if (staffName) aliases.push(`name:${staffName}|${clockInSec}`);

          return aliases;
        };

        rawRows.forEach((raw) => {
          const data = raw.data || {};
          if (data.scheduleVarianceApproved !== true && data.scheduleVarianceRejected !== true) return;

          const clockInSec =
            firestoreSeconds(data.clockIn) ??
            firestoreSeconds(data.clockInAt) ??
            firestoreSeconds(data.in) ??
            firestoreSeconds(data.inAt);
          if (!clockInSec) return;

          historicalShiftAliases({ data, raw, clockInSec }).forEach((key) => {
            resolvedHistoricalShiftKeys.add(key);
          });
        });
        const storeIdByName = new Map(stores.map((store) => [String(store.name || "").trim().toLowerCase(), String(store.id)]));
        const dedupe = new Map();

        rawRows.forEach((raw) => {
          const data = raw.data || {};
          const clockInSec = firestoreSeconds(data.clockIn) ?? firestoreSeconds(data.clockInAt) ?? firestoreSeconds(data.in) ?? firestoreSeconds(data.inAt);
          const clockOutSec = firestoreSeconds(data.clockOut) ?? firestoreSeconds(data.clockOutAt) ?? firestoreSeconds(data.out) ?? firestoreSeconds(data.outAt);
          if (!clockInSec || !clockOutSec || clockOutSec <= clockInSec) return;
          if (data.archived === true || data.isArchived === true || data.payrollZeroHours === true) return;
          const clockInMs = clockInSec * 1000;
          if (clockInMs < startMs || clockInMs >= endMs) return;

          const date = new Date(clockInMs);
          const dateKey = isoDate(date);
          const rawStore = String(raw.forcedStoreId || data.storeId || data.store || "").trim();
          const storeId = storeNameById.has(rawStore) ? rawStore : (storeIdByName.get(rawStore.toLowerCase()) || rawStore);
          const storeName = raw.forcedStoreName || data.storeName || storeNameById.get(storeId) || data.store || "";
          if (builderStoreId !== "ALL" && storeId !== builderStoreId) return;

          const staffId = String(
            data.staffId || data.userId || data.userUid || data.uid || data.employeeId || ""
          );
          const staffName = String(
            data.name ||
            data.staffName ||
            data.staffNameSnapshot ||
            data.employeeName ||
            data.displayName ||
            ""
          ).trim();

          const physicalShiftAliases = historicalShiftAliases({
            data,
            raw,
            clockInSec,
            storeId,
          });
          if (
            data.scheduleVarianceApproved === true ||
            data.scheduleVarianceRejected === true ||
            physicalShiftAliases.some((key) => resolvedHistoricalShiftKeys.has(key))
          ) return;

          const publishedShift =
            (staffId ? scheduleMap.get(`${storeId}|id:${staffId}|${dateKey}`) : "") ||
            (staffName ? scheduleMap.get(`${storeId}|name:${staffName.toLowerCase()}|${dateKey}`) : "") || "";
          const match = publishedShift.match(/^(\d{2}):(\d{2})-(\d{2}):(\d{2})$/);

          const roleName = String(data.roleName || data.role || "").trim() || roleNameForStaff(staffId, staffName);
          const autoClockedOut =
            data.autoClockedOutAtThreshold === true ||
            firestoreSeconds(data.thresholdClockOut) != null ||
            data.autoClockOutMode === "force-close";

          let exceptionType = "unscheduled";
          let exceptionTone = "amber";
          let pendingMinutes = Math.max(1, Math.round((clockOutSec - clockInSec) / 60));
          let exceptionTitle = `${staffName || "Staff member"} worked an unscheduled shift`;
          let exceptionDetail = `${compactDuration(pendingMinutes)} worked without a published shift · ${date.toLocaleDateString("en-ZA")}`;

          if (match) {
            const scheduledStart = new Date(date.getFullYear(), date.getMonth(), date.getDate(), Number(match[1]), Number(match[2]), 0, 0);
            const scheduledEnd = new Date(date.getFullYear(), date.getMonth(), date.getDate(), Number(match[3]), Number(match[4]), 0, 0);
            if (scheduledEnd <= scheduledStart) scheduledEnd.setDate(scheduledEnd.getDate() + 1);
            const startSec = Math.floor(scheduledStart.getTime() / 1000);
            const endSec = Math.floor(scheduledEnd.getTime() / 1000);
            const earlySecondsRaw = Math.max(0, Math.min(clockOutSec, startSec) - clockInSec);
            const earlyGraceSeconds = Math.max(0, Number(todayGraceMinutes || 0)) * 60;
            const earlySeconds = Math.max(0, earlySecondsRaw - earlyGraceSeconds);
            const lateSeconds = Math.max(0, clockOutSec - Math.max(clockInSec, endSec));
            const outsideSeconds = earlySeconds + lateSeconds;
            if (outsideSeconds <= 0) return;

            pendingMinutes = Math.max(1, Math.round(outsideSeconds / 60));
            exceptionType = "overtime";
            exceptionTone = "blue";
            exceptionTitle = autoClockedOut
              ? `${staffName || "Staff member"} was auto clocked out with overtime pending`
              : `${staffName || "Staff member"} has overtime pending`;
            exceptionDetail = `${compactDuration(pendingMinutes)} outside the published ${publishedShift} shift · ${date.toLocaleDateString("en-ZA")}`;
          }

          const item = {
            id: `historical-${exceptionType}-${raw.source}-${raw.docId}`,
            type: exceptionType, tone: exceptionTone,
            title: exceptionTitle,
            detail: exceptionDetail,
            autoClockedOut,
            storeId, storeName, roleName, source: "attendance", historical: true,
            row: {
              id: `historical-${raw.source}-${raw.docId}`,
              staffName, staffId, roleName, storeId, storeName,
              date: dateKey,
              publishedShift,
              shift: publishedShift,
              status: exceptionType, tone: exceptionTone,
              detail: exceptionType === "overtime"
                ? `${compactDuration(pendingMinutes)} overtime pending`
                : `${compactDuration(pendingMinutes)} unscheduled shift pending`,
              autoClockedOut,
              historical: true,
              actual: {
                id: `${raw.source}:${raw.docId}`, source: raw.source, name: staffName, staffId, role: roleName,
                storeId, storeName, clockInSec, clockOutSec,
                autoClockedOutAtThreshold: data.autoClockedOutAtThreshold === true,
                thresholdClockOut: data.thresholdClockOut || null,
                autoClockOutMode: data.autoClockOutMode || "",
                scheduleVarianceApproved: false, scheduleVarianceRejected: false,
              },
            },
          };
          const key = `${staffId || staffName.toLowerCase()}|${clockInSec}|${storeId}`;
          const existing = dedupe.get(key);
          if (!existing || raw.source === "nested") dedupe.set(key, item);
        });

        if (!cancelled) setHistoricalPendingOvertime(Array.from(dedupe.values()).sort((a,b) => (b.row?.actual?.clockInSec || 0) - (a.row?.actual?.clockInSec || 0)));
      } catch (error) {
        console.error("Could not load historical pending overtime exceptions", error);
        if (!cancelled) setHistoricalPendingOvertime([]);
      } finally {
        if (!cancelled) setHistoricalExceptionLoading(false);
      }
    };
    loadHistoricalPending();
    return () => { cancelled = true; };
  }, [view, orgId, stores, builderStoreId, exceptionPeriod, roleNameForStaff, historicalExceptionRefreshKey, todayGraceMinutes]);

  const allStoresSelected = builderStoreId === "ALL";
  const activeStore = useMemo(
    () => allStoresSelected ? null : (stores.find((store) => store.id === builderStoreId) || stores[0] || null),
    [stores, builderStoreId, allStoresSelected]
  );

  const todayKey = localTodayKey();

  useEffect(() => {
    // C7.4 — Leave requests are FRESH, not LIVE. Read them once when Leave opens
    // (or the store catalogue changes) and let approve/reject flows update local state.
    if (view !== "leave") {
      setLeaveLoading(false);
      return undefined;
    }
    if (!orgId || !stores.length) {
      setLeaveRequests([]);
      setLeaveLoading(false);
      return undefined;
    }

    let cancelled = false;
    const loadLeaveRequests = async () => {
      setLeaveLoading(true);
      try {
        const snapshots = await Promise.all(stores.map(async (store) => {
          const snap = await getDocs(collection(db, "orgs", orgId, "stores", store.id, "leaveRequests"));
          return snap.docs.map((d) => ({
            id: d.id,
            ...(d.data() || {}),
            storeId: (d.data() || {}).storeId || store.id,
            storeName: (d.data() || {}).storeName || store.name || "",
          }));
        }));
        if (!cancelled) {
          const rows = snapshots.flat().sort((a, b) => {
            const aMs = leaveTsDate(a.submittedAt)?.getTime?.() || 0;
            const bMs = leaveTsDate(b.submittedAt)?.getTime?.() || 0;
            return bMs - aMs;
          });
          setLeaveRequests(rows);
        }
      } catch (error) {
        console.error("Could not load leave requests", error);
        if (!cancelled) setLeaveRequests([]);
      } finally {
        if (!cancelled) setLeaveLoading(false);
      }
    };

    loadLeaveRequests();
    return () => { cancelled = true; };
  }, [orgId, stores, view]);

  const managedLeaveTypes = useMemo(
    () => attendanceTypes.filter((row) => isLeaveAttendanceType(row) && row.paid !== false),
    [attendanceTypes]
  );

  const loadLeaveBalances = useCallback(async (storeId, force = false) => {
    if (!orgId || !storeId || storeId === "ALL") {
      setLeaveBalanceRows([]);
      setLeaveBalanceLoading(false);
      return;
    }

    if (!force && leaveBalanceCacheRef.current.has(storeId)) {
      setLeaveBalanceRows(leaveBalanceCacheRef.current.get(storeId) || []);
      setLeaveBalanceError("");
      return;
    }

    setLeaveBalanceLoading(true);
    setLeaveBalanceError("");
    try {
      const snap = await getDocs(collection(db, "orgs", orgId, "stores", storeId, "leaveBalances"));
      const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
      leaveBalanceCacheRef.current.set(storeId, rows);
      setLeaveBalanceRows(rows);
    } catch (error) {
      console.error("Could not load leave balances", error);
      setLeaveBalanceRows([]);
      setLeaveBalanceError("Could not load leave balances.");
    } finally {
      setLeaveBalanceLoading(false);
    }
  }, [orgId, siteScope.allSites, siteScope.siteIds]);

  useEffect(() => {
    if (view !== "leave") return;
    if (!builderStoreId || builderStoreId === "ALL") {
      setLeaveBalanceRows([]);
      setLeaveBalanceLoading(false);
      return;
    }
    loadLeaveBalances(builderStoreId);
  }, [view, builderStoreId, loadLeaveBalances]);

  const visibleLeaveBalanceStaff = useMemo(() => {
    if (!builderStoreId || builderStoreId === "ALL") return [];
    const queryText = String(leaveBalanceSearch || "").trim().toLowerCase();
    return staff
      .filter((person) => String(person.storeId || "") === String(builderStoreId))
      .filter((person) => {
        if (!queryText) return true;
        const roleName = roleNameForStaff(person.id, person.name) || "";
        return `${person.name || ""} ${roleName}`.toLowerCase().includes(queryText);
      })
      .sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), undefined, { sensitivity: "base" }));
  }, [staff, builderStoreId, leaveBalanceSearch, roleNameForStaff]);

  const toggleLeaveBalanceStaff = useCallback((staffId) => {
    setExpandedLeaveBalanceStaff((current) => {
      const next = new Set(current);
      if (next.has(staffId)) next.delete(staffId);
      else next.add(staffId);
      return next;
    });
  }, []);

  const balanceDocForStaff = useCallback((staffId) =>
    leaveBalanceRows.find((row) => String(row.staffId || row.id) === String(staffId)) || null,
  [leaveBalanceRows]);

  const openLeaveBalanceEditor = useCallback((person, mode = "opening") => {
    const firstType = managedLeaveTypes[0]?.id || "annual-leave";
    setLeaveBalanceEditor(person);
    setLeaveBalanceMode(mode);
    setLeaveBalanceTypeId(firstType);
    setLeaveBalanceAmount("");
    setLeaveBalanceDirection("add");
    setLeaveBalanceEffectiveDate(localTodayKey());
    setLeaveBalanceReason("");
    setLeaveBalanceSaveState("idle");
  }, [managedLeaveTypes]);

  const saveLeaveBalanceTransaction = useCallback(async () => {
    const person = leaveBalanceEditor;
    const storeId = String(person?.storeId || builderStoreId || "");
    const type = managedLeaveTypes.find((row) => row.id === leaveBalanceTypeId);
    const amount = Math.abs(numericDays(leaveBalanceAmount));
    const reason = String(leaveBalanceReason || "").trim();
    if (!orgId || !person?.id || !storeId || storeId === "ALL" || !type || amount <= 0 || !reason) return;

    setLeaveBalanceSaveState("saving");
    try {
      const balanceRef = doc(db, "orgs", orgId, "stores", storeId, "leaveBalances", person.id);
      const ledgerRef = doc(collection(balanceRef, "ledger"));
      let nextSummary = null;

      await runTransaction(db, async (tx) => {
        const snap = await tx.get(balanceRef);
        const current = snap.exists() ? (snap.data() || {}) : {};
        const balances = { ...(current.balances || {}) };
        const previous = normaliseLeaveBalanceEntry(balances[type.id] || {});
        const before = numericDays(previous.availableDays);

        if (leaveBalanceMode === "opening" && previous.openingBalanceSet === true) {
          throw new Error(`${type.label} already has an opening balance. Use Adjust balance instead.`);
        }

        const delta = leaveBalanceMode === "opening"
          ? amount
          : (leaveBalanceDirection === "deduct" ? -amount : amount);

        const nextEntry = normaliseLeaveBalanceEntry({
          ...previous,
          manualAdjustmentDays: numericDays(
            previous.manualAdjustmentDays + (leaveBalanceMode === "adjustment" ? delta : 0)
          ),
          ...(leaveBalanceMode === "opening" ? {
            openingBalanceSet: true,
            openingBalanceDays: amount,
            openingEffectiveDate: leaveBalanceEffectiveDate || localTodayKey(),
          } : {}),
        });
        const after = nextEntry.availableDays;
        balances[type.id] = nextEntry;

        const summaryData = {
          staffId: person.id,
          staffName: person.name || "Staff member",
          storeId,
          storeName: stores.find((row) => row.id === storeId)?.name || person.storeName || "",
          balances,
          updatedAt: serverTimestamp(),
          updatedByUid: actor.uid || "",
          updatedByName: actor.name || actor.email || "Admin",
          updatedByEmail: actor.email || "",
        };
        tx.set(balanceRef, summaryData, { merge: true });
        tx.set(ledgerRef, {
          type: leaveBalanceMode === "opening" ? "opening_balance" : "manual_adjustment",
          leaveTypeId: type.id,
          leaveTypeLabel: type.label || type.id,
          deltaDays: delta,
          balanceBeforeDays: before,
          balanceAfterDays: after,
          openingAfterDays: nextEntry.openingBalanceDays,
          accruedAfterDays: nextEntry.accruedDays,
          adjustmentAfterDays: nextEntry.manualAdjustmentDays,
          creditedAfterDays: nextEntry.creditedDays,
          bookedAfterDays: nextEntry.bookedDays,
          usedAfterDays: nextEntry.usedDays,
          effectiveDate: leaveBalanceEffectiveDate || localTodayKey(),
          reason,
          createdAt: serverTimestamp(),
          createdByUid: actor.uid || "",
          createdByName: actor.name || actor.email || "Admin",
          createdByEmail: actor.email || "",
          staffId: person.id,
          staffName: person.name || "Staff member",
          storeId,
        });
        nextSummary = { id: person.id, ...current, ...summaryData, balances };
      });

      if (nextSummary) {
        setLeaveBalanceRows((rows) => {
          const next = rows.some((row) => row.id === person.id)
            ? rows.map((row) => row.id === person.id ? nextSummary : row)
            : [...rows, nextSummary];
          leaveBalanceCacheRef.current.set(storeId, next);
          return next;
        });
      }
      setLeaveBalanceSaveState("saved");
      setTimeout(() => setLeaveBalanceEditor(null), 450);
    } catch (error) {
      console.error("Could not save leave balance transaction", error);
      setLeaveBalanceSaveState(error?.message || "error");
    }
  }, [leaveBalanceEditor, builderStoreId, managedLeaveTypes, leaveBalanceTypeId, leaveBalanceAmount, leaveBalanceReason, leaveBalanceMode, leaveBalanceDirection, leaveBalanceEffectiveDate, orgId, stores, actor.uid, actor.name, actor.email]);

  const openLeaveLedger = useCallback(async (person) => {
    const storeId = String(person?.storeId || builderStoreId || "");
    if (!orgId || !person?.id || !storeId || storeId === "ALL") return;
    setLeaveLedgerStaff(person);
    setLeaveLedgerRows([]);
    setLeaveLedgerLoading(true);
    try {
      const ledgerQ = query(
        collection(db, "orgs", orgId, "stores", storeId, "leaveBalances", person.id, "ledger"),
        orderBy("createdAt", "desc"),
        limit(100)
      );
      const snap = await getDocs(ledgerQ);
      setLeaveLedgerRows(snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) })));
    } catch (error) {
      console.error("Could not load leave ledger", error);
      setLeaveLedgerRows([]);
    } finally {
      setLeaveLedgerLoading(false);
    }
  }, [orgId, builderStoreId]);

  const visibleLeaveRequests = useMemo(() => {
    const storeRows = allStoresSelected
      ? leaveRequests
      : leaveRequests.filter((row) => String(row.storeId || "") === String(activeStore?.id || ""));
    const [periodStart, periodEnd] = schedulePeriodRange(leavePeriod);
    return storeRows.filter((row) => {
      // Pending requests stay actionable regardless of the selected historical period.
      if (String(row.status || "pending").toLowerCase() === "pending") return true;
      const date = leaveTsDate(row.reviewedAt || row.updatedAt || row.submittedAt);
      return date instanceof Date && !Number.isNaN(date.getTime()) && date >= periodStart && date < periodEnd;
    });
  }, [leaveRequests, allStoresSelected, activeStore?.id, leavePeriod]);

  const pendingLeaveRequests = useMemo(
    () => visibleLeaveRequests.filter((row) => String(row.status || "pending").toLowerCase() === "pending"),
    [visibleLeaveRequests]
  );

  const resolvedLeaveRequests = useMemo(
    () => visibleLeaveRequests.filter((row) => ["approved", "rejected"].includes(String(row.status || "").toLowerCase())),
    [visibleLeaveRequests]
  );

  const selectedLeaveRequest = useMemo(
    () => leaveRequests.find((row) => row.id === selectedLeaveId) || null,
    [leaveRequests, selectedLeaveId]
  );

  const applyApprovedLeaveToSchedule = useCallback(async (request) => {
    if (!request?.id || !orgId || !request.storeId) return;
    if (String(request.status || "").toLowerCase() !== "approved") return;
    if (request.scheduleSyncStatus === "synced") return;

    const requestKey = `${request.storeId}:${request.id}`;
    setLeaveSyncState((current) => ({ ...current, [requestKey]: "syncing" }));

    try {
      const targetStore = stores.find((row) => String(row.id) === String(request.storeId)) || {};
      const staffName = String(request.staffName || "").trim().toLowerCase();
      const person =
        staff.find((row) =>
          String(row.storeId || "") === String(request.storeId) &&
          String(row.name || "").trim().toLowerCase() === staffName
        ) || null;

      if (!person?.id) {
        throw new Error("Neo could not match this leave request to an active staff profile.");
      }

      const type = attendanceTypeForLeave(request.leaveType, attendanceTypes);
      const dates = leaveDateKeys(request.startDate, request.endDate);
      if (!dates.length) throw new Error("Leave request has no valid dates.");

      const batch = writeBatch(db);
      const changes = [];

      for (const dateKey of dates) {
        const entryId = `${request.storeId}__${person.id}__${dateKey}`;
        const entryRef = doc(db, "orgs", orgId, "neo_schedule_entries", entryId);
        const entrySnap = await getDoc(entryRef);
        const current = entrySnap.exists() ? (entrySnap.data() || {}) : {};

        const previousPublished = String(current.publishedShift || current.shift || "");
        const previousDraft = current.draftShift !== undefined && current.draftShift !== null
          ? String(current.draftShift)
          : previousPublished;

        const inheritedHours = type.paid === false || type.allocateHours !== true
          ? 0
          : Math.max(
              0,
              Number(
                current.scheduledNetHours ??
                (previousPublished && !attendanceIdFromValue(previousPublished)
                  ? scheduleCellHours(previousPublished, attendanceTypes, Number(current.lunchBreakMinutes ?? lunchBreakMinutes))
                  : type.defaultHours ?? 0)
              ) || 0
            );

        const leaveValue = attendanceValue(type.id, type.allocateHours ? inheritedHours : 0);

        batch.set(entryRef, {
          orgId,
          storeId: request.storeId,
          storeName: request.storeName || targetStore.name || "",
          staffId: person.id,
          staffName: person.name || request.staffName || "",
          roleId: person.roleId || "",
          roleName: roles.find((row) => row.id === person.roleId)?.name || request.staffRole || "",
          date: dateKey,
          monthKey: dateKey.slice(0, 7),
          shift: leaveValue,
          draftShift: leaveValue,
          publishedShift: leaveValue,
          off: false,
          startTime: "",
          endTime: "",
          scheduledGrossHours: 0,
          lunchBreakMinutes: 0,
          scheduledNetHours: inheritedHours,
          isAttendance: true,
          attendanceTypeId: type.id,
          attendanceLabel: type.label || leaveTypeLabel(request.leaveType),
          attendancePaid: type.paid !== false,
          attendanceAllocateHours: type.allocateHours === true,
          attendanceHours: type.allocateHours ? inheritedHours : 0,
          attendanceRequiresApproval: false,
          leaveRequestId: request.id,
          leaveType: request.leaveType || "",
          leaveApproved: true,
          leaveApprovedAt: request.reviewedAt || request.updatedAt || serverTimestamp(),
          leavePreviousPublishedShift: previousPublished,
          leavePreviousDraftShift: previousDraft,
          source: "approved-leave",
          draftStatus: "published",
          published: true,
          publishedAt: serverTimestamp(),
          publishedByUid: actor?.uid || "",
          publishedByEmail: actor?.email || "",
          publishedByName: actor?.name || "Neo Leave",
          updatedAt: serverTimestamp(),
          updatedByUid: actor?.uid || "",
          updatedByEmail: actor?.email || "",
          updatedByName: actor?.name || "Neo Leave",
        }, { merge: true });

        batch.set(
          doc(db, "orgs", orgId, "neo_schedule_months", `${request.storeId}__${dateKey.slice(0, 7)}`),
          {
            orgId,
            storeId: request.storeId,
            storeName: request.storeName || targetStore.name || "",
            monthKey: dateKey.slice(0, 7),
            status: "published",
            updatedAt: serverTimestamp(),
            updatedByUid: actor?.uid || "",
            updatedByEmail: actor?.email || "",
            updatedByName: actor?.name || "Neo Leave",
          },
          { merge: true }
        );

        changes.push({
          staffId: person.id,
          staffName: person.name || request.staffName || "",
          date: dateKey,
          before: previousPublished,
          after: leaveValue,
          reason: `${type.label || leaveTypeLabel(request.leaveType)} approved`,
        });
      }

      const historyRef = doc(collection(db, "orgs", orgId, "neo_schedule_history"));
      batch.set(historyRef, {
        orgId,
        storeId: request.storeId,
        storeName: request.storeName || targetStore.name || "",
        monthKey: String(request.startDate || "").slice(0, 7),
        type: "leave_approved",
        leaveRequestId: request.id,
        staffId: person.id,
        staffName: person.name || request.staffName || "",
        leaveType: request.leaveType || "",
        leaveLabel: type.label || leaveTypeLabel(request.leaveType),
        changeCount: changes.length,
        changes,
        createdAt: serverTimestamp(),
        decision: "approved",
        decisionAt: request.reviewedAt || request.updatedAt || null,
        reviewedVia: request.reviewedVia || "",
        managerNote: request.managerNote || "",
        createdByUid: request.reviewedById || actor?.uid || "",
        createdByEmail: request.reviewedByEmail || actor?.email || "",
        createdByName: request.reviewedBy || actor?.name || "Neo Leave",
      });

      batch.set(
        doc(db, "orgs", orgId, "stores", request.storeId, "leaveRequests", request.id),
        {
          scheduleSyncStatus: "synced",
          scheduleSyncedAt: serverTimestamp(),
          scheduleSyncedByUid: actor?.uid || "",
          scheduleSyncedByEmail: actor?.email || "",
          scheduleSyncedByName: actor?.name || "Neo Leave",
          scheduleAttendanceTypeId: type.id,
          scheduleAttendanceLabel: type.label || leaveTypeLabel(request.leaveType),
          scheduleAllocatedHours: changes.reduce((sum, change) => {
            const parts = attendancePartsFromValue(change.after);
            return sum + (Number(parts.hours) || 0);
          }, 0),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      await batch.commit();
      setLeaveSyncState((current) => ({ ...current, [requestKey]: "synced" }));
    } catch (error) {
      console.error("Could not sync approved leave to Schedule Intelligence", error);
      setLeaveSyncState((current) => ({ ...current, [requestKey]: error?.message || "error" }));
    }
  }, [orgId, stores, staff, roles, attendanceTypes, lunchBreakMinutes, actor?.uid, actor?.email, actor?.name]);

  useEffect(() => {
    const unsynced = leaveRequests.filter((row) =>
      String(row.status || "").toLowerCase() === "approved" &&
      row.scheduleSyncStatus !== "synced" &&
      leaveSyncState[`${row.storeId}:${row.id}`] !== "syncing"
    );
    unsynced.slice(0, 3).forEach((row) => applyApprovedLeaveToSchedule(row));
  }, [leaveRequests, leaveSyncState, applyApprovedLeaveToSchedule]);

  const decideAdminLeave = useCallback(async (request, approved) => {
    if (!request?.id || !orgId || !request.storeId) return;
    const nextStatus = approved ? "approved" : "rejected";
    setLeaveDecisionState((current) => ({ ...current, [request.id]: "saving" }));
    try {
      const requestRef = doc(db, "orgs", orgId, "stores", request.storeId, "leaveRequests", request.id);
      if (approved) {
        const wantedName = String(request.staffName || "").trim().toLowerCase();
        const person = staff.find((row) =>
          String(row.storeId || "") === String(request.storeId) &&
          String(row.name || "").trim().toLowerCase() === wantedName
        ) || null;
        if (!person?.id) throw new Error("Neo could not match this leave request to an active staff profile.");

        const type = attendanceTypeForLeave(request.leaveType, attendanceTypes);
        const requestedDays = leaveDateKeys(request.startDate, request.endDate).length;
        const configuredType = attendanceTypes.find((row) => String(row?.id || "") === String(type.id)) || {};
        const blockIfInsufficient = configuredType?.leavePolicy?.blockIfInsufficient === true;
        const balanceRef = doc(db, "orgs", orgId, "stores", request.storeId, "leaveBalances", person.id);

        await runTransaction(db, async (tx) => {
          const freshRequestSnap = await tx.get(requestRef);
          const balanceSnap = await tx.get(balanceRef);
          const fresh = freshRequestSnap.data() || {};
          if (String(fresh.status || fresh.decision || "pending").toLowerCase() !== "pending") {
            throw new Error("This leave request has already been decided.");
          }

          const balanceData = balanceSnap.exists() ? (balanceSnap.data() || {}) : {};
          const balances = { ...(balanceData.balances || {}) };
          const raw = balances[type.id] || {};
          const opening = Number(raw.openingBalanceDays || 0);
          const accrued = Number(raw.accruedDays || 0);
          const adjustments = Number(raw.manualAdjustmentDays || 0);
          const booked = Math.max(0, Number(raw.bookedDays || 0));
          const used = Math.max(0, Number(raw.usedDays || 0));
          const available = Number.isFinite(Number(raw.availableDays))
            ? Number(raw.availableDays)
            : opening + accrued + adjustments - booked - used;

          const requiresLeaveBalance = type.paid !== false;
          if (requiresLeaveBalance && (!balanceSnap.exists() || available <= 0)) {
            throw new Error(`${person.name || request.staffName || "This staff member"} has no ${type.label || leaveTypeLabel(request.leaveType)} days available. Please allocate leave days before approving this request.`);
          }

          if (requiresLeaveBalance && available < requestedDays) {
            throw new Error(`${person.name || request.staffName || "This staff member"} only has ${formatLeaveDays(available)} available, but ${formatLeaveDays(requestedDays)} was requested. Please allocate or adjust leave days before approving.`);
          }

          if (blockIfInsufficient && available < requestedDays) {
            throw new Error(`Insufficient ${type.label || leaveTypeLabel(request.leaveType)} balance. ${available} day(s) available; ${requestedDays} requested.`);
          }

          if (balanceSnap.exists()) {
            const nextBooked = booked + requestedDays;
            balances[type.id] = {
              ...raw,
              leaveTypeLabel: raw.leaveTypeLabel || type.label || leaveTypeLabel(request.leaveType),
              openingBalanceDays: opening,
              accruedDays: accrued,
              manualAdjustmentDays: adjustments,
              bookedDays: nextBooked,
              usedDays: used,
              creditedDays: opening + accrued + adjustments,
              availableDays: opening + accrued + adjustments - nextBooked - used,
            };
            tx.set(balanceRef, {
              balances,
              updatedAt: serverTimestamp(),
              updatedByUid: actor?.uid || "",
              updatedByName: actor?.name || actor?.email || "Neo Admin",
            }, { merge: true });
            tx.set(doc(collection(db, "orgs", orgId, "stores", request.storeId, "leaveBalances", person.id, "ledger")), {
              type: "leave_booked",
              leaveRequestId: request.id,
              leaveTypeId: type.id,
              leaveTypeLabel: type.label || leaveTypeLabel(request.leaveType),
              deltaDays: requestedDays,
              balanceBeforeDays: available,
              balanceAfterDays: available - requestedDays,
              bookedAfterDays: nextBooked,
              usedAfterDays: used,
              reason: `${type.label || leaveTypeLabel(request.leaveType)} approved and reserved`,
              startDate: request.startDate || "",
              endDate: request.endDate || request.startDate || "",
              createdAt: serverTimestamp(),
              createdByUid: actor?.uid || "",
              createdByName: actor?.name || actor?.email || "Neo Admin",
              staffId: person.id,
              staffName: person.name || request.staffName || "",
              storeId: request.storeId,
              orgId,
            });
          }

          tx.set(requestRef, {
            status: "approved",
            decision: "approved",
            managerNote: String(leaveDecisionNote || "").trim(),
            reviewedAt: serverTimestamp(),
            reviewedBy: actor?.name || actor?.email || "Neo Admin",
            reviewedById: actor?.uid || "",
            reviewedVia: "neo_admin",
            balanceTypeId: type.id,
            balanceStaffId: person.id,
            staffId: person.id,
            balanceReservedDays: balanceSnap.exists() ? requestedDays : 0,
            balanceState: balanceSnap.exists() ? "booked" : "not_enrolled",
            balanceReservedAt: balanceSnap.exists() ? serverTimestamp() : null,
            updatedAt: serverTimestamp(),
            schemaVersion: 3,
            scheduleSyncStatus: "pending",
          }, { merge: true });
        });
      } else {
        const batch = writeBatch(db);
        batch.set(requestRef, {
          status: nextStatus,
          decision: nextStatus,
          managerNote: String(leaveDecisionNote || "").trim(),
          reviewedAt: serverTimestamp(),
          reviewedBy: actor?.name || actor?.email || "Neo Admin",
          reviewedById: actor?.uid || "",
          reviewedVia: "neo_admin",
          updatedAt: serverTimestamp(),
          schemaVersion: Math.max(2, Number(request.schemaVersion || 1)),
          scheduleSyncStatus: request.scheduleSyncStatus || "",
        }, { merge: true });
        await batch.commit();
      }
      setLeaveDecisionState((current) => ({ ...current, [request.id]: nextStatus }));
      setSelectedLeaveId("");
      setLeaveDecisionNote("");
    } catch (error) {
      setLeaveDecisionState((current) => ({ ...current, [request.id]: error?.message || "error" }));
    }
  }, [orgId, actor?.name, actor?.email, actor?.uid, leaveDecisionNote, staff, attendanceTypes]);

  const syncRejectedLeaveHistory = useCallback(async (request) => {
    if (!orgId || !request?.id || !request?.storeId) return;
    if (String(request.status || request.decision || "").toLowerCase() !== "rejected") return;
    if (request.historySyncStatus === "synced") return;

    const safeStore = String(request.storeId).replace(/[^a-zA-Z0-9_-]/g, "_");
    const safeRequest = String(request.id).replace(/[^a-zA-Z0-9_-]/g, "_");
    const historyId = `leave_rejected_${safeStore}_${safeRequest}`;
    const leaveLabel = request.leaveTypeLabel || request.scheduleAttendanceLabel || leaveTypeLabel(request.leaveType);
    const reviewer = request.reviewedBy || request.reviewedByName || request.reviewedByEmail || "Neo Manager";
    const managerNote = String(request.managerNote || "").trim();
    const dates = request.startDate
      ? `${request.startDate}${request.endDate && request.endDate !== request.startDate ? ` → ${request.endDate}` : ""}`
      : "Date unavailable";

    try {
      const batch = writeBatch(db);
      batch.set(
        doc(db, "orgs", orgId, "neo_schedule_history", historyId),
        {
          orgId,
          storeId: request.storeId,
          storeName: request.storeName || stores.find((store) => store.id === request.storeId)?.name || "",
          monthKey: String(request.startDate || "").slice(0, 7),
          type: "leave_rejected",
          action: "leave_rejected",
          decision: "rejected",
          leaveRequestId: request.id,
          staffId: request.staffId || "",
          staffName: request.staffName || "Staff member",
          leaveType: request.leaveType || "",
          leaveLabel,
          requestedDates: dates,
          managerNote,
          reviewedVia: request.reviewedVia || "",
          decisionAt: request.reviewedAt || request.updatedAt || null,
          changeCount: 1,
          changes: [{
            staffId: request.staffId || "",
            staffName: request.staffName || "Staff member",
            date: request.startDate || "",
            before: `${leaveLabel} requested`,
            after: "Rejected",
            reason: managerNote || `${leaveLabel} rejected`,
            decision: "rejected",
          }],
          createdAt: request.reviewedAt || request.updatedAt || serverTimestamp(),
          createdByUid: request.reviewedById || "",
          createdByEmail: request.reviewedByEmail || "",
          createdByName: reviewer,
        },
        { merge: true }
      );
      batch.set(
        doc(db, "orgs", orgId, "stores", request.storeId, "leaveRequests", request.id),
        {
          historySyncStatus: "synced",
          historySyncedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      await batch.commit();
    } catch (error) {
      console.error("Could not sync rejected leave into Schedule History", error);
    }
  }, [orgId, stores]);

  useEffect(() => {
    const rejected = leaveRequests.filter((row) =>
      String(row.status || row.decision || "").toLowerCase() === "rejected" &&
      row.historySyncStatus !== "synced"
    );
    rejected.slice(0, 5).forEach((row) => syncRejectedLeaveHistory(row));
  }, [leaveRequests, syncRejectedLeaveHistory]);

  const updateTodayGraceMinutes = useCallback(async (minutes) => {
    const value = Number(minutes);
    if (![0, 5, 10, 15, 30].includes(value)) return;

    setTodayGraceMinutes(value);
    localStorage.setItem("neo_schedule_grace_minutes", String(value));

    if (!orgId) return;
    try {
      const batch = writeBatch(db);
      batch.set(
        doc(db, "orgs", orgId, "settings", "labor"),
        { scheduleGraceMinutes: value, updatedAt: serverTimestamp() },
        { merge: true }
      );
      await batch.commit();
    } catch (error) {
      console.error("Could not save Schedule Intelligence grace period", error);
    }
  }, [orgId, siteScope.allSites, siteScope.siteIds]);

  const updateTodayEscalationMinutes = useCallback((minutes) => {
    const parsed = Number(minutes);
    if (!Number.isFinite(parsed)) return;
    const value = Math.max(0, Math.min(240, Math.round(parsed)));

    // Commit locally immediately. Firestore persistence happens in the background
    // and must never block or reset the input.
    todayEscalationPendingRef.current = value;
    setTodayEscalationMinutes(value);
    if (todayEscalationInputRef.current) {
      todayEscalationInputRef.current.value = String(value);
    }
    localStorage.setItem("neo_schedule_escalation_minutes", String(value));

    if (!orgId) {
      todayEscalationPendingRef.current = null;
      return;
    }

    const batch = writeBatch(db);
    batch.set(
      doc(db, "orgs", orgId, "settings", "labor"),
      { scheduleEscalationMinutes: value, updatedAt: serverTimestamp() },
      { merge: true }
    );

    batch.commit()
      .then(() => {
        if (todayEscalationPendingRef.current === value) {
          todayEscalationPendingRef.current = null;
        }
        setTodayEscalationSaved(true);
        if (todayEscalationSavedTimerRef.current) {
          clearTimeout(todayEscalationSavedTimerRef.current);
        }
        todayEscalationSavedTimerRef.current = setTimeout(() => {
          setTodayEscalationSaved(false);
          todayEscalationSavedTimerRef.current = null;
        }, 1500);
      })
      .catch((error) => {
        console.error("Could not save Schedule Intelligence escalation delay", error);
        setTodayEscalationSaved(false);
      });
  }, [orgId, siteScope.allSites, siteScope.siteIds]);

  const loadTodayActuals = useCallback(async () => {
    if (!orgId || (!allStoresSelected && !activeStore?.id)) {
      setTodayActualShifts([]);
      return;
    }

    const toShiftRow = (docSnap, source, forcedStoreId = "") => {
      const data = docSnap.data() || {};
      const clockInSec =
        firestoreSeconds(data.clockIn) ??
        firestoreSeconds(data.clockInAt) ??
        firestoreSeconds(data.in) ??
        firestoreSeconds(data.inAt);
      if (!clockInSec) return null;

      const clockOutSec =
        firestoreSeconds(data.clockOut) ??
        firestoreSeconds(data.clockOutAt) ??
        firestoreSeconds(data.out) ??
        firestoreSeconds(data.outAt);

      return {
        id: `${source}:${docSnap.id}`,
        source,
        name:
          data.name ||
          data.staffName ||
          data.staffNameSnapshot ||
          data.employeeName ||
          data.displayName ||
          "",
        staffId: data.staffId || data.userId || data.userUid || data.uid || data.employeeId || "",
        role: data.role || data.roleName || "",
        storeId: forcedStoreId || data.storeId || data.store || "",
        storeName: data.storeName || data.store || "",
        clockInSec,
        clockOutSec,

        // Persisted Schedule Intelligence decisions must survive refresh.
        scheduleVarianceApproved: data.scheduleVarianceApproved === true,
        scheduleVarianceRejected: data.scheduleVarianceRejected === true,
        scheduleVarianceType: data.scheduleVarianceType || "",
        scheduleVarianceDecisionAt: data.scheduleVarianceDecisionAt || null,
        swapExpectedStaffId: data.swapExpectedStaffId || "",
        swapExpectedStaffName: data.swapExpectedStaffName || "",
        swapReplacementStaffId: data.swapReplacementStaffId || "",
        swapReplacementStaffName: data.swapReplacementStaffName || "",
        approvedActualClockInSec:
          typeof data.approvedActualClockInSec === "number"
            ? data.approvedActualClockInSec
            : firestoreSeconds(data.approvedActualClockInSec),
        approvedActualClockOutSec:
          typeof data.approvedActualClockOutSec === "number"
            ? data.approvedActualClockOutSec
            : firestoreSeconds(data.approvedActualClockOutSec),
        payrollZeroHours: data.payrollZeroHours === true,
      };
    };

    try {
      const storeTargets = allStoresSelected ? stores : [activeStore].filter(Boolean);
      const [nestedSnaps, legacySnap] = await Promise.all([
        Promise.all(
          storeTargets.map(async (store) => ({
            store,
            snap: await getDocs(collection(db, "orgs", orgId, "stores", store.id, "shifts")).catch(() => null),
          }))
        ),
        getDocs(query(collection(db, "cashup_shifts"), where("orgId", "==", orgId))).catch(() => null),
      ]);

      const rows = [];
      nestedSnaps.forEach(({ store, snap }) => {
        snap?.docs?.forEach((d) => {
          const row = toShiftRow(d, "nested", store.id);
          if (row) {
            row.storeName = row.storeName || store.name || "";
            rows.push(row);
          }
        });
      });
      legacySnap?.docs?.forEach((d) => {
        const row = toShiftRow(d, "legacy");
        if (row) rows.push(row);
      });

      const todayRows = rows
        .map((row) => {
          const date = new Date(row.clockInSec * 1000);
          const rowDate = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
          return {
            ...row,
            clockInDateKey: rowDate,
            carriedOpenShift: rowDate !== todayKey && !row.clockOutSec,
          };
        })
        .filter((row) => {
          // Phase 4 — Management site scope is authoritative even when "All stores"
          // is selected. For a restricted manager, ALL means all authorised sites.
          const authorisedIds = new Set((siteScope.siteIds || []).map((id) => String(id).trim().toLowerCase()));
          const rowStoreId = String(row.storeId || "").trim().toLowerCase();
          if (!siteScope.allSites && !authorisedIds.has(rowStoreId)) return false;

          // Schedule Intelligence must also see forgotten open shifts from a
          // previous day. Closed historic shifts remain outside Today's view.
          if (row.clockInDateKey !== todayKey && !row.carriedOpenShift) return false;

          const normalizedStore = String(row.storeId || row.storeName || "").trim().toLowerCase();
          const selectedId = String(activeStore?.id || "").trim().toLowerCase();
          const selectedName = String(activeStore?.name || "").trim().toLowerCase();
          return allStoresSelected || (
            row.source === "nested" ||
            normalizedStore === selectedId ||
            normalizedStore === selectedName
          );
        });

      // MS3.4 — one physical attendance shift can exist in both nested + legacy
      // collections with different identity fields (one copy may have staffId while
      // the other only has the staff name). First propagate any persisted decision
      // across aliases, then dedupe. This makes Today/Exceptions consume the same
      // approved/rejected state already visible in Labor + History.
      const decisionByAlias = new Map();
      const aliasesForTodayShift = (row) => {
        const aliases = [];
        const staffId = String(row.staffId || "").trim().toLowerCase();
        const staffName = String(row.name || "").trim().toLowerCase();
        if (staffId) aliases.push(`id:${staffId}|${row.clockInSec}`);
        if (staffName) aliases.push(`name:${staffName}|${row.clockInSec}`);
        return aliases;
      };

      todayRows.forEach((row) => {
        if (row.scheduleVarianceApproved !== true && row.scheduleVarianceRejected !== true) return;
        const decision = {
          scheduleVarianceApproved: row.scheduleVarianceApproved === true,
          scheduleVarianceRejected: row.scheduleVarianceRejected === true,
          scheduleVarianceType: row.scheduleVarianceType || "",
          scheduleVarianceDecisionAt: row.scheduleVarianceDecisionAt || null,
          approvedActualClockInSec: row.approvedActualClockInSec || null,
          approvedActualClockOutSec: row.approvedActualClockOutSec || null,
          payrollZeroHours: row.payrollZeroHours === true,
        };
        aliasesForTodayShift(row).forEach((alias) => decisionByAlias.set(alias, decision));
      });

      const reconciledTodayRows = todayRows.map((row) => {
        const persistedDecision = aliasesForTodayShift(row)
          .map((alias) => decisionByAlias.get(alias))
          .find(Boolean);
        return persistedDecision ? { ...row, ...persistedDecision } : row;
      });

      const deduped = new Map();
      reconciledTodayRows.forEach((row) => {
        // Prefer name when present because legacy/nested copies do not always share
        // the same staffId representation. Clock-in time keeps the occurrence unique.
        const identity = String(row.name || row.staffId || "").trim().toLowerCase();
        const key = `${identity}|${row.clockInSec}`;
        const previous = deduped.get(key);

        const rowHasDecision = row.scheduleVarianceApproved === true || row.scheduleVarianceRejected === true;
        const previousHasDecision = previous?.scheduleVarianceApproved === true || previous?.scheduleVarianceRejected === true;

        if (
          !previous ||
          (rowHasDecision && !previousHasDecision) ||
          (row.clockOutSec && !previous.clockOutSec)
        ) {
          deduped.set(key, row);
        }
      });

      setTodayActualShifts(Array.from(deduped.values()).sort((a, b) => a.clockInSec - b.clockInSec));
    } catch (error) {
      setTodayError(error?.message || "Could not read today's attendance.");
    }
  }, [orgId, activeStore?.id, activeStore?.name, allStoresSelected, stores, todayKey, siteScope.allSites, siteScope.siteIds]);

  const refreshTodayAttendance = useCallback(async () => {
    setAttendanceRefreshFeedback("refreshing");
    try {
      await loadTodayActuals();
      setAttendanceRefreshFeedback("refreshed");
      if (attendanceRefreshTimerRef.current) {
        clearTimeout(attendanceRefreshTimerRef.current);
      }
      attendanceRefreshTimerRef.current = setTimeout(() => {
        setAttendanceRefreshFeedback("");
        attendanceRefreshTimerRef.current = null;
      }, 1500);
    } catch {
      setAttendanceRefreshFeedback("");
    }
  }, [loadTodayActuals]);


  // MS3.1 shared source-of-truth sync:
  // Manager App decisions are written by Cloud Functions directly to the same
  // shift documents used by Labor and Schedule Intelligence. Keep the Admin
  // Exceptions view live when those external decisions arrive.
  useEffect(() => {
    // C7.3 — Attendance is LIVE only where it drives operational intelligence.
    if (view !== "today" && view !== "exceptions") return undefined;
    if (!orgId || (!allStoresSelected && !activeStore?.id)) return undefined;

    let refreshTimer = null;
    const storeTargets = allStoresSelected ? stores : [activeStore].filter(Boolean);
    const initialSnapshotsSeen = new Set();

    const queueRefresh = (listenerKey) => {
      // Each onSnapshot fires immediately once. The initial attendance load is
      // already handled elsewhere, so only react to later changes.
      if (!initialSnapshotsSeen.has(listenerKey)) {
        initialSnapshotsSeen.add(listenerKey);
        return;
      }

      if (refreshTimer) clearTimeout(refreshTimer);
      refreshTimer = setTimeout(() => {
        loadTodayActuals().catch((error) => {
          console.error("Could not sync external Schedule Intelligence decision", error);
        });
      }, 180);
    };

    const unsubs = [];

    storeTargets.forEach((store) => {
      if (!store?.id) return;
      const listenerKey = `nested:${store.id}`;
      unsubs.push(
        onSnapshot(
          collection(db, "orgs", orgId, "stores", store.id, "shifts"),
          () => queueRefresh(listenerKey),
          () => {}
        )
      );
    });

    // Legacy shifts are still read by Schedule Intelligence, so keep them live too.
    const legacyKey = "legacy:cashup_shifts";
    unsubs.push(
      onSnapshot(
        collection(db, "cashup_shifts"),
        () => queueRefresh(legacyKey),
        () => {}
      )
    );

    return () => {
      if (refreshTimer) clearTimeout(refreshTimer);
      unsubs.forEach((unsubscribe) => unsubscribe?.());
    };
  }, [
    view,
    orgId,
    allStoresSelected,
    activeStore?.id,
    stores,
    loadTodayActuals,
  ]);

  useEffect(() => {
    if (!orgId || (!allStoresSelected && !activeStore?.id)) {
      setTodaySchedules([]);
      return undefined;
    }

    setTodayLoading(true);
    setTodayError("");
    const allowed = Array.from(new Set(siteScope.siteIds || [])).filter(Boolean);
    if (!siteScope.allSites && !allowed.length) {
      setTodaySchedules([]);
      setTodayLoading(false);
      return undefined;
    }
    const todayRef = collection(db, "orgs", orgId, "neo_schedule_entries");
    const q = siteScope.allSites
      ? query(todayRef, where("date", "==", todayKey))
      : query(todayRef, where("storeId", "in", allowed.slice(0, 30)), where("date", "==", todayKey));

    return onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs
          .map((d) => {
            const row = { id: d.id, ...(d.data() || {}) };

            // The published roster remains operational truth until another draft is
            // explicitly published. Auto-saved draft edits intentionally set
            // draftStatus="draft", but that must never make the already-published
            // shift disappear from Today/Exceptions and turn a legitimate clock-in
            // into an "unscheduled" exception.
            const publishedShift = String(row.publishedShift || "").trim();
            const staffMaster = staff.find((person) => String(person.id || "") === String(row.staffId || "")) || {};
            const roleMaster = roles.find((role) => String(role.id || "") === String(row.roleId || staffMaster.roleId || "")) || {};
            const attendanceTypeId = attendanceIdFromValue(publishedShift);
            const attendanceType = attendanceTypes.find((item) => item.id === attendanceTypeId) || null;
            const isAttendance = !!attendanceTypeId;
            const off = publishedShift === "OFF";
            const [startTime = "", endTime = ""] =
              !off && !isAttendance && publishedShift.includes("-")
                ? publishedShift.split("-")
                : ["", ""];

            return {
              ...row,
              // Older published roster documents do not all carry the later
              // published/draftStatus flags or a staff-name snapshot. The
              // publishedShift itself is the durable published-roster value.
              // Fill missing identity snapshots from the current Staff master so
              // attendance can still match by name when legacy shift IDs differ.
              staffName: row.staffName || staffMaster.name || "",
              // Always reconstruct live comparison fields from publishedShift, not
              // draftShift/top-level draft metadata. Drafts may coexist with the
              // current published roster until Publish Changes is confirmed.
              shift: publishedShift,
              publishedShift,
              off,
              startTime,
              endTime,
              isAttendance,
              attendanceTypeId,
              attendanceLabel: attendanceType?.label || row.attendanceLabel || "",
              attendancePaid: attendanceType ? attendanceType.paid !== false : row.attendancePaid,
              attendanceAllocateHours: attendanceType ? attendanceType.allocateHours === true : row.attendanceAllocateHours,
              attendanceHours: attendanceType
                ? attendanceHoursFromValue(publishedShift, attendanceTypes)
                : 0,
              // Published Schedule is its own planning snapshot. Do not replace
              // its role with the employee's current Staff-master role.
              roleId: row.roleId || staffMaster.roleId || "",
              roleName: row.roleName || row.role || roleMaster.name || "",
            };
          })
          .filter((row) => {
            // publishedShift is the canonical published roster snapshot. Do not
            // require newer metadata flags here: older valid roster documents may
            // not have `published === true`, and auto-saved drafts may coexist with
            // a still-valid publishedShift.
            return (allStoresSelected || row.storeId === activeStore?.id) &&
              !!String(row.publishedShift || "").trim();
          });
        setTodaySchedules(rows);
        setTodayLoading(false);
      },
      (error) => {
        setTodayError(error?.message || "Could not read today's published schedule.");
        setTodayLoading(false);
      }
    );
  }, [orgId, activeStore?.id, allStoresSelected, todayKey, roleNameForStaff, roleIdForStaff, siteScope.allSites, siteScope.siteIds, attendanceTypes]);

  useEffect(() => {
    if (!orgId) {
      setDutyQueueRows([]);
      return undefined;
    }

    const queueRef = collection(db, "orgs", orgId, "neoDutyQueue");
    const allowed = Array.from(new Set(siteScope.siteIds || [])).filter(Boolean);
    if (!siteScope.allSites && !allowed.length) {
      setDutyQueueRows([]);
      return undefined;
    }
    const queueQuery = siteScope.allSites
      ? queueRef
      : query(queueRef, where("storeId", "in", allowed.slice(0, 30)));

    return onSnapshot(
      queueQuery,
      (snap) => {
        setDutyQueueRows(snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) })));
      },
      () => setDutyQueueRows([])
    );
  }, [orgId, siteScope.allSites, siteScope.siteIds]);

  // S7.7 — Automatic Duty Reassignment (built on S7.3 availability-aware routing).
  // Published schedule remains the planning authority, but live attendance decides
  // whether the planned owner can actually receive an operational duty.
  useEffect(() => {
    if (SERVER_DUTY_ROUTING_AUTHORITY) return undefined;
    if (!orgId || !dutyQueueRows.length) return undefined;

    let cancelled = false;
    const resolveDutyOwners = async () => {
      const batch = writeBatch(db);
      let writes = 0;
      const nowSec = Math.floor(Date.now() / 1000);

      const actualByStaffId = new Map();
      const actualByName = new Map();
      todayActualShifts.forEach((shift) => {
        if (shift.staffId) actualByStaffId.set(String(shift.staffId), shift);
        if (shift.name) actualByName.set(String(shift.name).trim().toLowerCase(), shift);
      });

      const actualForSchedule = (schedule) =>
        (schedule?.staffId && actualByStaffId.get(String(schedule.staffId))) ||
        actualByName.get(String(schedule?.staffName || "").trim().toLowerCase()) ||
        null;

      const isActuallyOnShift = (actual) =>
        !!actual?.clockInSec && !actual?.clockOutSec && !actual?.approvedActualClockOutSec;

      const actualOperationalRole = (actual) => {
        // S7 source of truth: the role selected on the active shift is the
        // operational authority. Staff-master role is fallback only for legacy
        // shifts that genuinely have no role snapshot.
        const shiftRoleName = String(actual?.role || actual?.roleName || "").trim();
        const shiftRoleId = String(actual?.roleId || "").trim();

        if (shiftRoleName || shiftRoleId) {
          return {
            id: shiftRoleId,
            name: shiftRoleName,
          };
        }

        return {
          id: roleIdForStaff(actual?.staffId, actual?.name) || "",
          name: roleNameForStaff(actual?.staffId, actual?.name) || "",
        };
      };

      dutyQueueRows.forEach((item) => {
        if (item.active === false || item.archived === true || item.deleted === true) return;
        if (item.completed === true || item.completedAt || item.resolved === true || item.resolvedAt) return;

        const dueMs =
          item.dueAt?.toMillis?.() ||
          (item.dueAt?.seconds ? item.dueAt.seconds * 1000 : 0) ||
          (item.dueAt ? new Date(item.dueAt).getTime() : 0);
        const dueSec = dueMs ? Math.floor(dueMs / 1000) : 0;
        const dutyDate = dueMs ? isoDate(new Date(dueMs)) : todayKey;
        if (dutyDate !== todayKey) return;

        const dutyStoreId = String(item.storeId || "");
        const dutyStoreName = String(item.storeName || item.siteName || "").trim().toLowerCase();
        const requiredRoleId = String(item.ownerRoleId || "");
        const requiredRoleName = String(item.ownerRoleName || "").trim().toLowerCase();
        const roleOwned = item.ownershipMode === "scheduled_role" || !!requiredRoleId || !!requiredRoleName;

        const sameStore = (schedule) => {
          if (dutyStoreId) return String(schedule.storeId || "") === dutyStoreId;
          return !dutyStoreName || String(schedule.storeName || "").trim().toLowerCase() === dutyStoreName;
        };

        const rosterCandidates = todaySchedules
          // todaySchedules is already built from the canonical publishedShift.
          // Do not re-introduce legacy metadata requirements here or duty routing
          // can lose a valid published owner when a draft coexists with the roster.
          .filter((schedule) => !!String(schedule.publishedShift || "").trim())
          .filter((schedule) => !schedule.off && !schedule.isAttendance && schedule.startTime && schedule.endTime)
          .filter(sameStore)
          .map((schedule) => ({
            ...schedule,
            resolvedRoleId:
              schedule.roleId ||
              roleIdForStaff(schedule.staffId, schedule.staffName) ||
              "",
            resolvedRoleName:
              schedule.roleName ||
              schedule.role ||
              roleNameForStaff(schedule.staffId, schedule.staffName) ||
              "",
          }))
          .filter((schedule) => {
            if (!roleOwned) return true;
            if (requiredRoleId) return String(schedule.resolvedRoleId || "") === requiredRoleId;
            return String(schedule.resolvedRoleName || "").trim().toLowerCase() === requiredRoleName;
          });

        const plannedOwner = rosterCandidates.find((schedule) => {
          if (!dueSec) return true;
          const startSec = secondsForLocalDateTime(dutyDate, schedule.startTime);
          let endSec = secondsForLocalDateTime(dutyDate, schedule.endTime);
          if (endSec && startSec && endSec <= startSec) endSec += 86400;
          let compareDue = dueSec;
          if (startSec && compareDue < startSec && endSec > startSec + 86400) compareDue += 86400;
          return !!startSec && !!endSec && compareDue >= startSec && compareDue <= endSec;
        }) || (!dueSec ? rosterCandidates[0] : null);

        const plannedActual = actualForSchedule(plannedOwner);
        const dutyIsDue = !dueSec || nowSec >= dueSec;

        // An approved same-day swap explicitly transfers the absent person's
        // operational coverage to the approved replacement, regardless of permanent role.
        const approvedSwapActual = plannedOwner
          ? todayActualShifts.find((actual) =>
              actual.scheduleVarianceApproved === true &&
              actual.scheduleVarianceType === "swap" &&
              isActuallyOnShift(actual) &&
              (
                (plannedOwner.staffId && String(actual.swapExpectedStaffId || "") === String(plannedOwner.staffId)) ||
                String(actual.swapExpectedStaffName || "").trim().toLowerCase() ===
                  String(plannedOwner.staffName || "").trim().toLowerCase()
              )
            )
          : null;

        // If the planned owner is currently present, they remain authoritative.
        // Before the duty becomes due, a rostered-but-not-yet-present owner remains expected.
        let owner = null;
        let resolutionStatus = "missing_owner";
        let resolvedFrom = "live_availability_no_match";

        if (dutyIsDue) {
          // At/after due time the ACTIVE SHIFT ROLE is authoritative. A published
          // roster is planning context only and may not represent who is actually
          // operating that role right now.
          const liveRoleCandidates = todayActualShifts
            .filter((actual) => {
              if (!isActuallyOnShift(actual)) return false;

              const actualStoreId = String(actual.storeId || "");
              const actualStoreName = String(actual.storeName || "").trim().toLowerCase();
              if (dutyStoreId && actualStoreId !== dutyStoreId) return false;
              if (!dutyStoreId && dutyStoreName && actualStoreName !== dutyStoreName) return false;

              if (!roleOwned) return true;
              const operationalRole = actualOperationalRole(actual);
              if (requiredRoleId && operationalRole.id) {
                return String(operationalRole.id) === requiredRoleId;
              }
              const requiredNameFromId = requiredRoleId
                ? roles.find((role) => String(role.id) === requiredRoleId)?.name || ""
                : "";
              return String(operationalRole.name || "").trim().toLowerCase() ===
                String(requiredRoleName || requiredNameFromId).trim().toLowerCase();
            })
            .sort((a, b) => {
              const aIn = Number(a.clockInSec || 0);
              const bIn = Number(b.clockInSec || 0);
              if (aIn !== bIn) return aIn - bIn;
              return String(a.name || "").localeCompare(String(b.name || ""));
            });

          const liveOwner = liveRoleCandidates[0] || null;
          if (liveOwner) {
            const liveRole = actualOperationalRole(liveOwner);
            owner = {
              id: "",
              staffId: liveOwner.staffId || "",
              staffName: liveOwner.name || "",
              resolvedRoleId: liveRole.id || requiredRoleId || "",
              resolvedRoleName: liveRole.name || item.ownerRoleName || "",
              startTime: "",
              endTime: "",
              availabilityActualId: liveOwner.id || "",
            };
            resolutionStatus = "available";
            resolvedFrom = "active_shift_role";
          }
        }

        if (!owner && plannedOwner && approvedSwapActual) {
          owner = {
            ...plannedOwner,
            staffId: approvedSwapActual.staffId || "",
            staffName: approvedSwapActual.name || approvedSwapActual.swapReplacementStaffName || "",
            resolvedRoleId:
              actualOperationalRole(approvedSwapActual).id ||
              plannedOwner.resolvedRoleId ||
              "",
            resolvedRoleName:
              actualOperationalRole(approvedSwapActual).name ||
              plannedOwner.resolvedRoleName ||
              "",
            availabilityActualId: approvedSwapActual.id || "",
          };
          resolutionStatus = "reassigned";
          resolvedFrom = "approved_shift_swap";
        } else if (
          plannedOwner &&
          !dutyIsDue &&
          !(plannedActual?.clockOutSec || plannedActual?.approvedActualClockOutSec)
        ) {
          owner = plannedOwner;
          resolutionStatus = "expected";
          resolvedFrom = "published_schedule_expected";
        } else {
          // At/after due time, an absent/no-show or already clocked-out owner cannot
          // receive the duty. Find another CURRENTLY WORKING person at the same store
          // with the required Staff-master role.
          const liveReplacementCandidates = todayActualShifts
            .filter((actual) => {
              if (!isActuallyOnShift(actual)) return false;
              if (plannedActual?.id && actual.id === plannedActual.id) return false;

              const actualStoreId = String(actual.storeId || "");
              const actualStoreName = String(actual.storeName || "").trim().toLowerCase();
              if (dutyStoreId && actualStoreId !== dutyStoreId) return false;
              if (!dutyStoreId && dutyStoreName && actualStoreName !== dutyStoreName) return false;

              const operationalRole = actualOperationalRole(actual);
              const actualRoleId = String(operationalRole.id || "");
              const actualRoleName = String(operationalRole.name || "").trim().toLowerCase();
              if (!roleOwned) return true;

              // Match by ID only when both sides have IDs. Otherwise match the
              // active shift role label so legacy rows still route correctly.
              if (requiredRoleId && actualRoleId) return actualRoleId === requiredRoleId;

              const requiredNameFromId = requiredRoleId
                ? roles.find((role) => String(role.id) === requiredRoleId)?.name || ""
                : "";
              return actualRoleName === String(requiredRoleName || requiredNameFromId).trim().toLowerCase();
            })
            .sort((a, b) => {
              // Deterministic owner selection: longest-active eligible worker first,
              // then staff name. This prevents the duty bouncing between candidates.
              const aIn = Number(a.clockInSec || 0);
              const bIn = Number(b.clockInSec || 0);
              if (aIn !== bIn) return aIn - bIn;
              return String(a.name || "").localeCompare(String(b.name || ""));
            });

          const liveReplacement = liveReplacementCandidates[0] || null;

          if (liveReplacement) {
            owner = {
              id: "",
              staffId: liveReplacement.staffId || "",
              staffName: liveReplacement.name || "",
              resolvedRoleId:
                actualOperationalRole(liveReplacement).id ||
                requiredRoleId,
              resolvedRoleName:
                actualOperationalRole(liveReplacement).name ||
                item.ownerRoleName ||
                "",
              startTime: "",
              endTime: "",
              availabilityActualId: liveReplacement.id || "",
            };
            resolutionStatus = "reassigned";
            resolvedFrom = plannedOwner ? "live_role_reassignment" : "live_role_coverage";
          }
        }

        const existingId = String(item.assignedStaffId || item.responseOwnerUid || "");
        const existingName = String(item.assignedStaffName || item.responseOwner || "");
        const nextId = String(owner?.staffId || "");
        const nextName = String(owner?.staffName || "");
        const nextRoleId = String(owner?.resolvedRoleId || requiredRoleId || "");
        const nextRoleName = String(owner?.resolvedRoleName || item.ownerRoleName || "");
        const plannedId = String(plannedOwner?.staffId || "");
        const plannedName = String(plannedOwner?.staffName || "");
        const ownerChanged = existingId !== nextId || existingName !== nextName;
        const reassignedNow =
          ownerChanged &&
          !!nextId &&
          resolutionStatus === "reassigned";

        if (
          existingId === nextId &&
          existingName === nextName &&
          String(item.ownershipResolutionStatus || "") === resolutionStatus &&
          String(item.resolvedOwnerRoleId || "") === nextRoleId &&
          String(item.ownershipResolvedFrom || "") === resolvedFrom
        ) return;

        batch.set(doc(db, "orgs", orgId, "neoDutyQueue", item.id), {
          assignedStaffId: nextId,
          assignedStaffName: nextName,
          responseOwnerUid: nextId,
          responseOwner: nextName,
          plannedOwnerStaffId: plannedId,
          plannedOwnerStaffName: plannedName,
          resolvedOwnerRoleId: nextRoleId,
          resolvedOwnerRoleName: nextRoleName,
          ownershipResolutionStatus: resolutionStatus,
          ownershipResolvedFrom: resolvedFrom,
          ownershipScheduleEntryId: plannedOwner?.id || "",
          ownershipScheduleStart: plannedOwner?.startTime || "",
          ownershipScheduleEnd: plannedOwner?.endTime || "",
          ownershipActualShiftId: owner?.availabilityActualId || plannedActual?.id || "",
          ownershipAvailabilityChecked: true,
          ownershipResolvedAt: serverTimestamp(),

          // S7.7 audit: preserve who Neo moved the duty from/to and why.
          ...(reassignedNow
            ? {
                reassignedFromStaffId: existingId || plannedId,
                reassignedFromStaffName: existingName || plannedName,
                reassignedToStaffId: nextId,
                reassignedToStaffName: nextName,
                reassignmentReason:
                  resolvedFrom === "approved_shift_swap"
                    ? "Approved shift swap"
                    : plannedActual?.clockOutSec || plannedActual?.approvedActualClockOutSec
                    ? "Planned owner clocked out before duty"
                    : plannedOwner
                    ? "Planned owner unavailable at duty time"
                    : "Live role coverage",
                reassignmentSource: resolvedFrom,
                reassignedAt: serverTimestamp(),
                reassignmentCount: Number(item.reassignmentCount || 0) + 1,

                // A new owner must receive their own reminder. Never inherit the
                // previous person's reminder fingerprint/delivery status.
                staffReminderStatus: "",
                staffReminderFingerprint: "",
                staffReminderFingerprints: [],
                staffReminderRecipientId: "",
                staffReminderRecipientName: "",
                staffReminderWhatsapp: "",
                staffReminderError: "",
                staffReminder: {},
              }
            : {}),
          updatedAt: serverTimestamp(),
        }, { merge: true });

        if (reassignedNow) {
          const historyRef = doc(collection(db, "orgs", orgId, "neo_duty_history"));
          batch.set(historyRef, {
            orgId,
            queueId: item.id,
            dutyId: item.dutyId || item.id,
            dutyLabel: item.dutyLabel || item.label || item.dutyName || "Duty",
            storeId: item.storeId || "",
            storeName: item.storeName || item.siteName || "",
            action: "automatic_reassignment",
            fromStaffId: existingId || plannedId,
            fromStaffName: existingName || plannedName,
            toStaffId: nextId,
            toStaffName: nextName,
            roleId: nextRoleId,
            roleName: nextRoleName,
            source: resolvedFrom,
            reason:
              resolvedFrom === "approved_shift_swap"
                ? "Approved shift swap"
                : plannedActual?.clockOutSec || plannedActual?.approvedActualClockOutSec
                ? "Planned owner clocked out before duty"
                : plannedOwner
                ? "Planned owner unavailable at duty time"
                : "Live role coverage",
            createdAt: serverTimestamp(),
          });
        }

        writes += 1;
      });

      if (!cancelled && writes) await batch.commit();
    };

    resolveDutyOwners().catch((error) => {
      console.error("S7.3 availability-aware duty routing failed", error);
    });
    return () => { cancelled = true; };
  }, [
    orgId,
    dutyQueueRows,
    todaySchedules,
    todayActualShifts,
    todayKey,
    todayNow,
    roleIdForStaff,
    roleNameForStaff,
  ]);

  // S7.4 — Duty lifecycle.
  useEffect(() => {
    if (!orgId || !dutyQueueRows.length) return undefined;

    let cancelled = false;

    const toMs = (value) => {
      if (!value) return 0;
      if (typeof value?.toMillis === "function") return value.toMillis();
      if (typeof value?.seconds === "number") return value.seconds * 1000;
      const parsed = new Date(value).getTime();
      return Number.isNaN(parsed) ? 0 : parsed;
    };

    const deriveLifecycle = (item, nowMs) => {
      const completed =
        item.completed === true ||
        item.status === "completed" ||
        !!item.completedAt ||
        item.resolved === true ||
        !!item.resolvedAt;

      if (completed) return { status: "completed", reason: "Duty completed", attention: false };

      const dueMs = toMs(item.dueAt);
      const rule = item.responseRule || {};
      const graceMinutes = Math.max(
        0,
        Number(
          item.gracePeriodMins ??
          item.graceMinutes ??
          rule.gracePeriodMins ??
          rule.graceMinutes ??
          0
        ) || 0
      );
      const graceEndMs = dueMs ? dueMs + graceMinutes * 60000 : 0;

      const escalated =
        item.escalationStatus === "ready" ||
        item.escalationReady === true ||
        item.status === "escalated" ||
        !!item.escalatedAt ||
        !!item.managerEscalation?.sentAt ||
        item.managerEscalation?.status === "sent";

      if (escalated) return { status: "escalated", reason: "Duty escalated to management", attention: true };

      const hasOwner = !!(
        item.assignedStaffId ||
        item.assignedStaffName ||
        item.responseOwnerUid ||
        item.responseOwner
      );
      const ownerResolution = String(item.ownershipResolutionStatus || "");

      if (!hasOwner && dueMs && nowMs >= dueMs) {
        return {
          status: ownerResolution === "missing_owner" ? "owner_unavailable" : "awaiting_replacement",
          reason:
            ownerResolution === "missing_owner"
              ? "No available duty owner"
              : "Duty owner unavailable · awaiting replacement",
          attention: true,
        };
      }

      if (ownerResolution === "reassigned") {
        const fromName = item.reassignedFromStaffName || item.plannedOwnerStaffName || "";
        const toName = item.assignedStaffName || item.reassignedToStaffName || "";
        return {
          status: "reassigned",
          reason:
            fromName && toName
              ? `Duty reassigned · ${fromName} → ${toName}`
              : `Duty reassigned${toName ? ` to ${toName}` : ""}`,
          attention: false,
        };
      }

      const reminderSent =
        !!item.reminderSentAt ||
        !!item.staffReminderSentAt ||
        item.reminderStatus === "sent" ||
        ["queued", "sending", "sent", "delivered"].includes(String(item.staffReminderStatus || "")) ||
        ["queued", "sending", "sent", "delivered"].includes(String(item.staffReminder?.status || "")) ||
        item.whatsappStatus === "sent";

      const reminderHeld =
        item.staffReminderStatus === "quiet_hours" ||
        item.staffReminder?.status === "quiet_hours";

      if (dueMs && nowMs < dueMs) {
        return reminderSent
          ? { status: "reminder_sent", reason: "Reminder sent before due time", attention: false }
          : { status: "upcoming", reason: "Duty is upcoming", attention: false };
      }

      if (dueMs && nowMs >= dueMs) {
        if (graceEndMs && nowMs > graceEndMs) {
          return {
            status: "overdue",
            reason: graceMinutes
              ? `Grace expired · ${Math.max(1, Math.floor((nowMs - graceEndMs) / 60000))}m overdue`
              : `${Math.max(1, Math.floor((nowMs - dueMs) / 60000))}m overdue`,
            attention: true,
          };
        }

        return reminderSent
          ? { status: "reminder_sent", reason: "Duty is due · reminder queued/sent", attention: false }
          : reminderHeld
          ? { status: "due", reason: "Duty is due · reminder held by quiet hours", attention: false }
          : {
              status: "due",
              reason: graceMinutes
                ? `Duty is due · ${Math.max(0, Math.ceil((graceEndMs - nowMs) / 60000))}m grace remaining`
                : "Duty is due",
              attention: false,
            };
      }

      return hasOwner
        ? { status: "upcoming", reason: "Duty is upcoming", attention: false }
        : { status: "awaiting_replacement", reason: "Awaiting an available duty owner", attention: true };
    };

    const syncDutyLifecycle = async () => {
      const batch = writeBatch(db);
      let writes = 0;
      const nowMs = Date.now();

      dutyQueueRows.forEach((item) => {
        if (item.active === false || item.archived === true || item.deleted === true) return;

        const next = deriveLifecycle(item, nowMs);
        if (
          String(item.lifecycleStatus || "") === next.status &&
          String(item.lifecycleReason || "") === next.reason &&
          item.lifecycleNeedsAttention === next.attention
        ) return;

        batch.set(
          doc(db, "orgs", orgId, "neoDutyQueue", item.id),
          {
            lifecycleStatus: next.status,
            lifecycleReason: next.reason,
            lifecycleNeedsAttention: next.attention,
            lifecycleUpdatedAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
        writes += 1;
      });

      if (!cancelled && writes) await batch.commit();
    };

    syncDutyLifecycle().catch((error) => {
      console.error("S7.4 duty lifecycle sync failed", error);
    });

    return () => { cancelled = true; };
  }, [orgId, dutyQueueRows, todayNow]);

  // S7.9 — Duty + Attendance Correlation.
  // Duty intelligence must understand whether the expected person actually arrived,
  // is still on shift, left early, or was replaced. Attendance is evidence; the
  // published schedule remains the expectation and the live clocked-in role remains
  // the operational role for the shift.
  useEffect(() => {
    if (!orgId || !dutyQueueRows.length) return undefined;

    let cancelled = false;

    const correlateDutyAttendance = async () => {
      const batch = writeBatch(db);
      let writes = 0;
      const nowSec = Math.floor(Date.now() / 1000);

      const actualByStaffId = new Map();
      const actualByName = new Map();
      todayActualShifts.forEach((shift) => {
        if (shift.staffId) actualByStaffId.set(String(shift.staffId), shift);
        if (shift.name) actualByName.set(String(shift.name).trim().toLowerCase(), shift);
      });

      const actualFor = (staffId, staffName) =>
        (staffId && actualByStaffId.get(String(staffId))) ||
        actualByName.get(String(staffName || "").trim().toLowerCase()) ||
        null;

      dutyQueueRows.forEach((item) => {
        if (item.active === false || item.archived === true || item.deleted === true) return;

        const dueMs =
          item.dueAt?.toMillis?.() ||
          (item.dueAt?.seconds ? item.dueAt.seconds * 1000 : 0) ||
          (item.dueAt ? new Date(item.dueAt).getTime() : 0);
        if (!dueMs || isoDate(new Date(dueMs)) !== todayKey) return;

        const plannedId = String(item.plannedOwnerStaffId || "");
        const plannedName = String(item.plannedOwnerStaffName || "");
        const currentId = String(item.assignedStaffId || item.responseOwnerUid || plannedId || "");
        const currentName = String(item.assignedStaffName || item.responseOwner || plannedName || "");

        const plannedActual = actualFor(plannedId, plannedName);
        const currentActual = actualFor(currentId, currentName);

        const plannedClockIn = Number(plannedActual?.clockInSec || 0);
        const plannedClockOut = Number(
          plannedActual?.approvedActualClockOutSec ||
          plannedActual?.clockOutSec ||
          0
        );
        const currentClockIn = Number(currentActual?.clockInSec || 0);
        const currentClockOut = Number(
          currentActual?.approvedActualClockOutSec ||
          currentActual?.clockOutSec ||
          0
        );

        const currentOnShift = !!currentClockIn && !currentClockOut;
        const plannedOnShift = !!plannedClockIn && !plannedClockOut;
        const dueSec = Math.floor(dueMs / 1000);

        let attendanceStatus = "unknown";
        let attendanceReason = "No attendance evidence yet";

        if (item.ownershipResolutionStatus === "reassigned" && currentOnShift) {
          attendanceStatus = "covered_by_replacement";
          attendanceReason = `${currentName || "Replacement"} is on shift and owns this duty`;
        } else if (currentOnShift) {
          attendanceStatus = "owner_on_shift";
          attendanceReason = `${currentName || "Duty owner"} is currently clocked in`;
        } else if (currentClockIn && currentClockOut) {
          attendanceStatus = currentClockOut < dueSec ? "owner_left_before_due" : "owner_shift_concluded";
          attendanceReason =
            currentClockOut < dueSec
              ? `${currentName || "Duty owner"} clocked out before this duty was due`
              : `${currentName || "Duty owner"} has concluded the shift`;
        } else if (plannedId || plannedName) {
          if (nowSec >= dueSec) {
            attendanceStatus = "expected_owner_not_clocked_in";
            attendanceReason = `${plannedName || "Expected owner"} was scheduled but is not clocked in`;
          } else {
            attendanceStatus = "expected_owner_not_arrived_yet";
            attendanceReason = `${plannedName || "Expected owner"} is scheduled but has not clocked in yet`;
          }
        }

        const next = {
          attendanceStatus,
          attendanceReason,
          attendanceCheckedAtSec: nowSec,
          attendanceExpectedStaffId: plannedId,
          attendanceExpectedStaffName: plannedName,
          attendanceCurrentOwnerStaffId: currentId,
          attendanceCurrentOwnerStaffName: currentName,
          attendanceOwnerClockInSec: currentClockIn || null,
          attendanceOwnerClockOutSec: currentClockOut || null,
          attendanceOwnerOnShift: currentOnShift,
          attendanceExpectedOwnerOnShift: plannedOnShift,
          attendanceReplacementActive:
            item.ownershipResolutionStatus === "reassigned" && currentOnShift,
          attendanceActualShiftId: currentActual?.id || "",
        };

        const unchanged =
          String(item.attendanceStatus || "") === attendanceStatus &&
          String(item.attendanceCurrentOwnerStaffId || "") === currentId &&
          Number(item.attendanceOwnerClockInSec || 0) === currentClockIn &&
          Number(item.attendanceOwnerClockOutSec || 0) === currentClockOut &&
          Boolean(item.attendanceOwnerOnShift) === currentOnShift;

        if (unchanged) return;

        batch.set(
          doc(db, "orgs", orgId, "neoDutyQueue", item.id),
          { ...next, attendanceCorrelatedAt: serverTimestamp(), updatedAt: serverTimestamp() },
          { merge: true }
        );
        writes += 1;
      });

      if (!cancelled && writes) await batch.commit();
    };

    correlateDutyAttendance().catch((error) => {
      console.error("S7.9 duty attendance correlation failed", error);
    });

    return () => {
      cancelled = true;
    };
  }, [orgId, dutyQueueRows, todayActualShifts, todayKey, todayNow]);

  // S7.10 — Operational Silence / Execution Assurance.
  // Persist a single contract that tells the rest of Neo whether a human manager
  // is actually needed. Routine reminders, healthy attendance and successful
  // reassignments remain visible for audit but do not create management noise.
  useEffect(() => {
    if (!orgId || !dutyQueueRows.length) return undefined;

    let cancelled = false;

    const syncExecutionAssurance = async () => {
      const batch = writeBatch(db);
      let writes = 0;

      dutyQueueRows.forEach((item) => {
        if (item.active === false || item.archived === true || item.deleted === true) return;

        const completed =
          item.completed === true ||
          item.status === "completed" ||
          item.lifecycleStatus === "completed" ||
          !!item.completedAt ||
          item.resolved === true ||
          !!item.resolvedAt;

        const lifecycle = String(item.lifecycleStatus || "");
        const attendance = String(item.attendanceStatus || "");
        const replacementActive = item.attendanceReplacementActive === true;

        let assuranceStatus = "healthy";
        let assuranceReason = "Neo is handling this duty normally";
        let managerAttentionRequired = false;
        let managerAttentionType = "";

        if (completed) {
          assuranceStatus = "resolved";
          assuranceReason = "Duty completed";
        } else if (
          attendance === "expected_owner_not_clocked_in" &&
          !replacementActive &&
          !["reassigned", "completed"].includes(lifecycle)
        ) {
          assuranceStatus = "attention_required";
          assuranceReason = item.attendanceReason || "Expected duty owner is not on shift";
          managerAttentionRequired = true;
          managerAttentionType = "attendance_gap";
        } else if (
          attendance === "owner_left_before_due" &&
          !replacementActive
        ) {
          assuranceStatus = "attention_required";
          assuranceReason = item.attendanceReason || "Duty owner left before duty time";
          managerAttentionRequired = true;
          managerAttentionType = "coverage_gap";
        } else if (
          lifecycle === "owner_unavailable" ||
          lifecycle === "awaiting_replacement"
        ) {
          assuranceStatus = "attention_required";
          assuranceReason = item.lifecycleReason || "No available duty owner";
          managerAttentionRequired = true;
          managerAttentionType = "missing_owner";
        } else if (lifecycle === "escalated") {
          assuranceStatus = "attention_required";
          assuranceReason = item.lifecycleReason || "Duty escalation requires management";
          managerAttentionRequired = true;
          managerAttentionType = "escalated";
        } else if (lifecycle === "overdue") {
          assuranceStatus = "attention_required";
          assuranceReason = item.lifecycleReason || "Duty is overdue";
          managerAttentionRequired = true;
          managerAttentionType = "overdue";
        } else if (
          lifecycle === "reassigned" ||
          attendance === "covered_by_replacement"
        ) {
          assuranceStatus = "handled_by_neo";
          assuranceReason =
            item.attendanceReason ||
            item.lifecycleReason ||
            "Neo restored duty coverage automatically";
        } else if (
          lifecycle === "due" ||
          lifecycle === "reminder_sent" ||
          ["queued", "sending", "sent", "delivered"].includes(
            String(item.staffReminderStatus || item.staffReminder?.status || "")
          )
        ) {
          assuranceStatus = "handled_by_neo";
          assuranceReason =
            item.lifecycleReason ||
            "Staff response is in progress";
        } else if (lifecycle === "upcoming") {
          assuranceStatus = "healthy";
          assuranceReason = "Duty is upcoming with no intervention required";
        }

        if (
          String(item.executionAssuranceStatus || "") === assuranceStatus &&
          String(item.executionAssuranceReason || "") === assuranceReason &&
          item.managerAttentionRequired === managerAttentionRequired &&
          String(item.managerAttentionType || "") === managerAttentionType
        ) return;

        batch.set(
          doc(db, "orgs", orgId, "neoDutyQueue", item.id),
          {
            executionAssuranceStatus: assuranceStatus,
            executionAssuranceReason: assuranceReason,
            managerAttentionRequired,
            managerAttentionType,
            executionAssuranceUpdatedAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
        writes += 1;
      });

      if (!cancelled && writes) await batch.commit();
    };

    syncExecutionAssurance().catch((error) => {
      console.error("S7.10 execution assurance sync failed", error);
    });

    return () => {
      cancelled = true;
    };
  }, [orgId, dutyQueueRows, todayNow]);

  // S7.12 — Notification Discipline & Scenario Assurance.
  // One final policy decides who may be notified and why. This prevents duplicate
  // staff reminders, unnecessary manager noise, and alerts that survive after resolution.
  useEffect(() => {
    if (!orgId || !dutyQueueRows.length) return undefined;

    let cancelled = false;

    const syncNotificationDiscipline = async () => {
      const batch = writeBatch(db);
      let writes = 0;

      dutyQueueRows.forEach((item) => {
        if (item.active === false || item.archived === true || item.deleted === true) return;

        const completed =
          item.completed === true ||
          item.status === "completed" ||
          item.lifecycleStatus === "completed" ||
          !!item.completedAt ||
          item.resolved === true ||
          !!item.resolvedAt;

        const lifecycle = String(item.lifecycleStatus || "");
        const assurance = String(item.executionAssuranceStatus || "");
        const attentionType = String(item.managerAttentionType || "");
        const hasOwner = !!(
          item.assignedStaffId ||
          item.assignedStaffName ||
          item.responseOwnerUid ||
          item.responseOwner
        );

        const staffNotificationAllowed =
          !completed &&
          hasOwner &&
          !["owner_unavailable", "awaiting_replacement", "escalated"].includes(lifecycle) &&
          ["due", "reminder_sent", "overdue", "reassigned"].includes(lifecycle);

        const managerNotificationAllowed =
          !completed &&
          (
            item.managerAttentionRequired === true ||
            assurance === "attention_required"
          ) &&
          [
            "attendance_gap",
            "coverage_gap",
            "missing_owner",
            "overdue",
            "escalated",
          ].includes(attentionType);

        let disciplineState = "silent";
        let disciplineReason = "No notification required";

        if (completed) {
          disciplineState = "resolved_silent";
          disciplineReason = "Duty resolved · suppress further notifications";
        } else if (managerNotificationAllowed) {
          disciplineState = "manager_attention";
          disciplineReason = `Manager attention · ${attentionType || "exception"}`;
        } else if (staffNotificationAllowed) {
          disciplineState = "staff_response";
          disciplineReason =
            lifecycle === "reassigned"
              ? "Replacement owner response"
              : lifecycle === "overdue"
              ? "Overdue staff response"
              : "Routine staff response";
        } else if (assurance === "handled_by_neo") {
          disciplineState = "neo_handling";
          disciplineReason = "Neo handling automatically · manager remains silent";
        } else if (assurance === "healthy" || lifecycle === "upcoming") {
          disciplineState = "healthy_silent";
          disciplineReason = "Operations healthy · no notification";
        }

        const previousManagerKey = String(item.managerNotificationKey || "");
        const managerKey = managerNotificationAllowed
          ? [
              item.id,
              attentionType || lifecycle,
              item.assignedStaffId || item.assignedStaffName || "",
              item.lifecycleUpdatedAt?.seconds || "",
            ].join("|")
          : "";

        if (
          String(item.notificationDisciplineState || "") === disciplineState &&
          String(item.notificationDisciplineReason || "") === disciplineReason &&
          item.staffNotificationAllowed === staffNotificationAllowed &&
          item.managerNotificationAllowed === managerNotificationAllowed &&
          previousManagerKey === managerKey
        ) return;

        batch.set(
          doc(db, "orgs", orgId, "neoDutyQueue", item.id),
          {
            notificationDisciplineState: disciplineState,
            notificationDisciplineReason: disciplineReason,
            staffNotificationAllowed,
            managerNotificationAllowed,
            managerNotificationKey: managerKey,
            notificationDisciplineUpdatedAt: serverTimestamp(),

            // Resolution must clear any lingering notification intent.
            ...(completed
              ? {
                  staffReminderStatus: "resolved",
                  staffReminderError: "",
                  managerAttentionRequired: false,
                  managerNotificationAllowed: false,
                }
              : {}),

            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
        writes += 1;
      });

      if (!cancelled && writes) await batch.commit();
    };

    syncNotificationDiscipline().catch((error) => {
      console.error("S7.12 notification discipline sync failed", error);
    });

    return () => {
      cancelled = true;
    };
  }, [orgId, dutyQueueRows, todayNow]);

  // S7.6 — Smart Reminder Engine.
  // Schedule Intelligence determines WHEN a staff response is needed.
  // The existing server response channel remains responsible for actual WhatsApp delivery.
  useEffect(() => {
    if (!orgId || !dutyQueueRows.length) return undefined;
    if (dutyResponseSettings.whatsappDutyDueEnabled !== true) return undefined;

    let cancelled = false;

    const toMs = (value) => {
      if (!value) return 0;
      if (typeof value?.toMillis === "function") return value.toMillis();
      if (typeof value?.seconds === "number") return value.seconds * 1000;
      const parsed = new Date(value).getTime();
      return Number.isNaN(parsed) ? 0 : parsed;
    };

    const normalizedWhatsapp = (value) => {
      const raw = String(value || "").trim();
      const digits = raw.replace(/\D/g, "");
      if (!digits) return "";
      if (digits.startsWith("27")) return `+${digits}`;
      if (digits.startsWith("0")) return `+27${digits.slice(1)}`;
      return raw.startsWith("+") ? `+${digits}` : `+${digits}`;
    };

    const minutesFromClock = (value) => {
      const match = String(value || "").match(/^(\d{1,2}):(\d{2})$/);
      if (!match) return null;
      return Number(match[1]) * 60 + Number(match[2]);
    };

    const inQuietHours = (now) => {
      const startMinutes = minutesFromClock(dutyResponseSettings.quietHoursStart);
      const endMinutes = minutesFromClock(dutyResponseSettings.quietHoursEnd);
      if (startMinutes == null || endMinutes == null || startMinutes === endMinutes) return false;

      const nowMinutes = now.getHours() * 60 + now.getMinutes();
      return startMinutes < endMinutes
        ? nowMinutes >= startMinutes && nowMinutes < endMinutes
        : nowMinutes >= startMinutes || nowMinutes < endMinutes;
    };

    const deriveReminder = (item, nowMs) => {
      const completed =
        item.completed === true ||
        item.status === "completed" ||
        !!item.completedAt ||
        item.resolved === true ||
        !!item.resolvedAt;
      if (completed) return null;

      const lifecycle = String(item.lifecycleStatus || "");
      if (["escalated", "owner_unavailable", "awaiting_replacement"].includes(lifecycle)) return null;

      const dueMs = toMs(item.dueAt);
      if (!dueMs) return null;

      const rule = item.responseRule || {};
      const remindBefore = Math.max(
        0,
        Number(item.reminderBeforeMins ?? rule.remindBeforeMins ?? 0) || 0
      );
      const repeatEvery = Math.max(
        0,
        Number(
          item.repeatEveryMins ??
          rule.repeatEveryMins ??
          dutyResponseSettings.defaultRepeatEveryMins ??
          0
        ) || 0
      );
      const graceMinutes = Math.max(
        0,
        Number(
          item.gracePeriodMins ??
          item.graceMinutes ??
          rule.gracePeriodMins ??
          rule.graceMinutes ??
          0
        ) || 0
      );

      const reminderStartMs = dueMs - remindBefore * 60000;
      const graceEndMs = dueMs + graceMinutes * 60000;

      // Optional pre-due reminder.
      if (remindBefore > 0 && nowMs >= reminderStartMs && nowMs < dueMs) {
        return {
          phase: "pre_due",
          sequence: 0,
          slotMs: reminderStartMs,
          reason: `${remindBefore}m before due`,
        };
      }

      // Exact due notification.
      if (nowMs >= dueMs && (repeatEvery <= 0 || nowMs < dueMs + repeatEvery * 60000)) {
        return {
          phase: "due",
          sequence: 0,
          slotMs: dueMs,
          reason: "Duty due",
        };
      }

      // After due, repeat only on configured cadence while still incomplete.
      if (repeatEvery > 0 && nowMs >= dueMs + repeatEvery * 60000) {
        const elapsed = nowMs - dueMs;
        const sequence = Math.floor(elapsed / (repeatEvery * 60000));
        const slotMs = dueMs + sequence * repeatEvery * 60000;
        const overdueMinutes = Math.max(1, Math.floor((nowMs - dueMs) / 60000));
        return {
          phase: nowMs > graceEndMs ? "overdue_repeat" : "grace_repeat",
          sequence,
          slotMs,
          reason:
            nowMs > graceEndMs
              ? `${overdueMinutes}m overdue`
              : `Due · grace active`,
        };
      }

      return null;
    };

    const queueSmartReminders = async () => {
      const batch = writeBatch(db);
      let writes = 0;
      const now = new Date();
      const nowMs = now.getTime();
      const quiet = inQuietHours(now);

      dutyQueueRows.forEach((item) => {
        if (item.active === false || item.archived === true || item.deleted === true) return;
        if (item.staffNotificationAllowed === false) return;

        const reminder = deriveReminder(item, nowMs);
        if (!reminder) return;

        const staffId = String(item.assignedStaffId || item.responseOwnerUid || "");
        const staffName = String(item.assignedStaffName || item.responseOwner || "");
        if (!staffId && !staffName) return;

        const person =
          (staffId && staff.find((row) => String(row.id) === staffId)) ||
          staff.find(
            (row) =>
              String(row.name || "").trim().toLowerCase() ===
              staffName.trim().toLowerCase()
          ) ||
          null;

        const responseIdentity = person?.responseIdentity || {};
        const whatsappNumber = normalizedWhatsapp(
          person?.whatsappNumber ||
          responseIdentity.whatsappNumber ||
          responseIdentity.whatsapp ||
          ""
        );
        const preferredChannel = String(
          person?.preferredResponseChannel ||
          responseIdentity.preferredChannel ||
          "whatsapp"
        ).toLowerCase();

        const dueMs = toMs(item.dueAt);
        const dueTime = dueMs
          ? new Date(dueMs).toLocaleTimeString("en-ZA", {
              hour: "2-digit",
              minute: "2-digit",
              hour12: false,
            })
          : "";
        const dutyLabel = String(item.dutyLabel || item.label || item.dutyName || "Duty");
        const storeName = String(item.storeName || item.siteName || "");
        const template = String(dutyResponseSettings.whatsappDutyDueTemplate || "neo_duty_due");
        const language = String(dutyResponseSettings.whatsappDutyDueLanguage || "en");
        const staffUrl = String(dutyResponseSettings.neoStaffAppUrl || "https://neo.matrixos.co.za");

        const fingerprint = [
          "neo_duty_reminder",
          item.id,
          staffId || staffName,
          reminder.phase,
          reminder.sequence,
          reminder.slotMs,
          template,
        ].join("|");

        const previousFingerprints = Array.isArray(item.staffReminderFingerprints)
          ? item.staffReminderFingerprints
          : [];
        const existingFingerprint =
          item.staffReminderFingerprint ||
          item.staffReminder?.fingerprint ||
          "";

        // Never queue the same cadence slot twice, even after refresh.
        if (existingFingerprint === fingerprint || previousFingerprints.includes(fingerprint)) return;

        const recipientReady =
          !!whatsappNumber &&
          (preferredChannel === "whatsapp" || preferredChannel === "both" || !preferredChannel);

        const nextStatus = quiet
          ? "quiet_hours"
          : recipientReady
          ? "queued"
          : "recipient_unavailable";
        const nextError = quiet
          ? "Reminder held during configured quiet hours"
          : recipientReady
          ? ""
          : whatsappNumber
          ? `Staff preferred channel is ${preferredChannel || "not WhatsApp"}`
          : "No verified WhatsApp number found in Staff";

        const history = [...previousFingerprints, fingerprint].slice(-30);

        batch.set(
          doc(db, "orgs", orgId, "neoDutyQueue", item.id),
          {
            staffReminderStatus: nextStatus,
            staffReminderFingerprint: fingerprint,
            staffReminderFingerprints: history,
            staffReminderPhase: reminder.phase,
            staffReminderSequence: reminder.sequence,
            staffReminderSlotMs: reminder.slotMs,
            staffReminderQueuedAt: nextStatus === "queued" ? serverTimestamp() : null,
            staffReminderRecipientId: staffId,
            staffReminderRecipientName: staffName || person?.name || "",
            staffReminderWhatsapp: whatsappNumber,
            staffReminderTemplate: template,
            staffReminderLanguage: language,
            staffReminderError: nextError,
            staffReminder: {
              status: nextStatus,
              fingerprint,
              phase: reminder.phase,
              sequence: reminder.sequence,
              slotMs: reminder.slotMs,
              reason: reminder.reason,
              channel: "whatsapp",
              template,
              language,
              recipientId: staffId,
              recipientName: staffName || person?.name || "",
              whatsappNumber,
              dutyLabel,
              storeName,
              dueTime,
              appUrl: staffUrl,
              error: nextError,
            },
            responseRequestedAt: serverTimestamp(),
            responseRequestedBy: "schedule_intelligence_s7_6",
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
        writes += 1;
      });

      if (!cancelled && writes) await batch.commit();
    };

    queueSmartReminders().catch((error) => {
      console.error("S7.6 smart reminder queue failed", error);
    });

    return () => {
      cancelled = true;
    };
  }, [
    orgId,
    dutyQueueRows,
    staff,
    todayNow,
    dutyResponseSettings.whatsappDutyDueEnabled,
    dutyResponseSettings.whatsappDutyDueTemplate,
    dutyResponseSettings.whatsappDutyDueLanguage,
    dutyResponseSettings.neoStaffAppUrl,
    dutyResponseSettings.defaultRepeatEveryMins,
    dutyResponseSettings.quietHoursStart,
    dutyResponseSettings.quietHoursEnd,
  ]);

  useEffect(() => {
    // C7.4 — History is historical: one read when opened, never a standing listener.
    if (view !== "history") return undefined;
    if (!orgId) {
      setScheduleHistory([]);
      return undefined;
    }
    let cancelled = false;
    const loadScheduleHistory = async () => {
      setHistoryLoading(true);
      setHistoryError("");
      try {
        const [periodStart, periodEnd] = schedulePeriodRange(historyPeriod);
        const historyRef = collection(db, "orgs", orgId, "neo_schedule_history");
        const allowed = Array.from(new Set(siteScope.siteIds || [])).filter(Boolean);
        if (!siteScope.allSites && !allowed.length) {
          if (!cancelled) setScheduleHistory([]);
          return;
        }
        const historyQuery = siteScope.allSites
          ? query(
              historyRef,
              where("createdAt", ">=", Timestamp.fromDate(periodStart)),
              where("createdAt", "<", Timestamp.fromDate(periodEnd)),
              orderBy("createdAt", "desc")
            )
          : query(
              historyRef,
              where("storeId", "in", allowed.slice(0, 30)),
              where("createdAt", ">=", Timestamp.fromDate(periodStart)),
              where("createdAt", "<", Timestamp.fromDate(periodEnd)),
              orderBy("createdAt", "desc")
            );
        const snap = await getDocs(historyQuery);
        if (cancelled) return;
        const rows = snap.docs
          .map((d) => ({ id: d.id, ...(d.data() || {}) }))
          .sort((a, b) => {
            const aMs = a.createdAt?.toMillis?.() || (a.createdAt?.seconds ? a.createdAt.seconds * 1000 : 0);
            const bMs = b.createdAt?.toMillis?.() || (b.createdAt?.seconds ? b.createdAt.seconds * 1000 : 0);
            return bMs - aMs;
          });
        setScheduleHistory(rows);
      } catch (error) {
        if (!cancelled) setHistoryError(error?.message || "Could not load schedule history.");
      } finally {
        if (!cancelled) setHistoryLoading(false);
      }
    };
    loadScheduleHistory();
    return () => { cancelled = true; };
  }, [view, orgId, historyPeriod, siteScope.allSites, siteScope.siteIds]);

  useEffect(() => {
    // C7.4 — Month catalogue is FRESH builder metadata, not LIVE state.
    // The month catalogue powers both Builder (current/future) and History (past rosters).
    // Keep it on-demand, but load it when either surface is opened.
    if (view !== "builder" && view !== "history") return undefined;
    if (!orgId) {
      setScheduleMonths([]);
      return undefined;
    }
    let cancelled = false;
    const loadScheduleMonths = async () => {
      try {
        const monthsRef = collection(db, "orgs", orgId, "neo_schedule_months");
        const allowed = Array.from(new Set(siteScope.siteIds || [])).filter(Boolean);
        if (!siteScope.allSites && !allowed.length) {
          if (!cancelled) setScheduleMonths([]);
          return;
        }
        const monthsQuery = siteScope.allSites
          ? monthsRef
          : query(monthsRef, where("storeId", "in", allowed.slice(0, 30)));
        const snap = await getDocs(monthsQuery);
        if (cancelled) return;
        const rows = snap.docs
          .map((d) => ({ id: d.id, ...(d.data() || {}) }))
          .filter((row) => row.storeId && row.monthKey)
          .sort((a, b) => String(a.monthKey).localeCompare(String(b.monthKey)));
        setScheduleMonths(rows);
      } catch {
        if (!cancelled) setScheduleMonths([]);
      }
    };
    loadScheduleMonths();
    return () => { cancelled = true; };
  }, [view, orgId, siteScope.allSites, siteScope.siteIds]);

  useEffect(() => {
    // C7.3 — remove the duplicate 30-second Firestore polling loop. Entering Today
    // or Exceptions performs one fresh read; the scoped realtime listeners above
    // handle subsequent attendance changes. The clock tick is local-only.
    if (view !== "today" && view !== "exceptions") return undefined;
    loadTodayActuals();
    const tick = setInterval(() => setTodayNow(Date.now()), 30000);
    return () => clearInterval(tick);
  }, [view, loadTodayActuals]);

  const todayIntelligence = useMemo(() => {
    const nowSec = Math.floor(todayNow / 1000);
    const currentDayActualShifts = todayActualShifts.filter((shift) => !shift.carriedOpenShift);
    const previousOpenShifts = todayActualShifts.filter((shift) => shift.carriedOpenShift && !shift.clockOutSec);

    const matchedActualIds = new Set();

    // Match one actual attendance occurrence to one published schedule occurrence.
    // Identity can come from different Neo collections, so a valid normalized name
    // match is accepted even when the stored staff IDs differ. The time intervals
    // must still overlap, which prevents a completed morning shift from satisfying
    // a later evening schedule for the same person.
    const actualForScheduleOccurrence = (schedule, scheduledStartSec, scheduledEndSec) => {
      if (!scheduledStartSec || !scheduledEndSec) return null;
      const staffId = String(schedule?.staffId || "").trim();
      const staffName = String(schedule?.staffName || "").trim().toLowerCase();
      const storeId = String(schedule?.storeId || "").trim();

      const matches = currentDayActualShifts
        .filter((actual) => !matchedActualIds.has(actual.id))
        .filter((actual) => {
          const actualStaffId = String(actual.staffId || "").trim();
          const actualName = String(actual.name || "").trim().toLowerCase();

          const sameById = !!staffId && !!actualStaffId && actualStaffId === staffId;
          const sameByName = !!staffName && !!actualName && actualName === staffName;
          if (!sameById && !sameByName) return false;

          if (storeId && actual.storeId && String(actual.storeId).trim() !== storeId) return false;

          const actualStart = Number(actual.clockInSec || 0);
          const actualEnd = Number(actual.clockOutSec || 0) || Number.POSITIVE_INFINITY;
          if (!actualStart) return false;

          return actualStart <= scheduledEndSec && actualEnd >= scheduledStartSec;
        });

      // A published shift can contain multiple attendance occurrences. The live
      // comparison must represent the employee's CURRENT state, not keep showing
      // the first occurrence closest to roster start. Prefer the newest open
      // occurrence; otherwise show the most recently concluded occurrence.
      const latestOpen = matches
        .filter((actual) => !actual.clockOutSec && !actual.approvedActualClockOutSec)
        .sort((a, b) => Number(b.clockInSec || 0) - Number(a.clockInSec || 0))[0];
      if (latestOpen) return latestOpen;

      return matches
        .sort((a, b) => Number(b.clockInSec || 0) - Number(a.clockInSec || 0))[0] || null;
    };

    const approvedSwapByExpectedId = new Map();
    const approvedSwapByExpectedName = new Map();
    currentDayActualShifts.forEach((shift) => {
      if (
        shift.scheduleVarianceApproved === true &&
        shift.scheduleVarianceType === "swap"
      ) {
        if (shift.swapExpectedStaffId) {
          approvedSwapByExpectedId.set(String(shift.swapExpectedStaffId), shift);
        }
        const expectedName = String(shift.swapExpectedStaffName || "").trim().toLowerCase();
        if (expectedName) approvedSwapByExpectedName.set(expectedName, shift);
      }
    });

    const plannedRows = todaySchedules.map((schedule) => {
      const attendanceTypeId = schedule.attendanceTypeId || "";
      const isAttendance = schedule.isAttendance === true || !!attendanceTypeId;
      const isOff = schedule.off === true || schedule.publishedShift === "OFF";
      const expectsClock = !isAttendance && !isOff && !!schedule.startTime && !!schedule.endTime;

      const scheduledStartSec = secondsForLocalDateTime(todayKey, schedule.startTime);
      let scheduledEndSec = secondsForLocalDateTime(todayKey, schedule.endTime);
      if (scheduledStartSec && scheduledEndSec && scheduledEndSec < scheduledStartSec) {
        scheduledEndSec += 86400;
      }

      const actual = actualForScheduleOccurrence(schedule, scheduledStartSec, scheduledEndSec);

      // A rostered shift may legitimately contain multiple attendance occurrences
      // (for example: early clock-out, manager calls staff, staff returns and
      // clocks in again). All overlapping occurrences belong to this published
      // shift and must not be emitted again as "unscheduled" rows.
      const scheduleActualOccurrences = expectsClock
        ? currentDayActualShifts.filter((candidate) => {
            const candidateStaffId = String(candidate.staffId || "").trim();
            const candidateName = String(candidate.name || "").trim().toLowerCase();
            const scheduleStaffId = String(schedule.staffId || "").trim();
            const scheduleName = String(schedule.staffName || "").trim().toLowerCase();
            const samePerson =
              (!!scheduleStaffId && !!candidateStaffId && scheduleStaffId === candidateStaffId) ||
              (!!scheduleName && !!candidateName && scheduleName === candidateName);
            if (!samePerson) return false;
            if (schedule.storeId && candidate.storeId && String(schedule.storeId) !== String(candidate.storeId)) return false;
            const candidateStart = Number(candidate.clockInSec || 0);
            const candidateEnd = Number(candidate.clockOutSec || 0) || Number.POSITIVE_INFINITY;
            return !!candidateStart && candidateStart <= scheduledEndSec && candidateEnd >= scheduledStartSec;
          })
        : [];

      const approvedSwapCover =
        (schedule.staffId && approvedSwapByExpectedId.get(String(schedule.staffId))) ||
        approvedSwapByExpectedName.get(String(schedule.staffName || "").trim().toLowerCase()) ||
        null;

      scheduleActualOccurrences.forEach((occurrence) => matchedActualIds.add(occurrence.id));
      if (actual) matchedActualIds.add(actual.id);
      if (approvedSwapCover) matchedActualIds.add(approvedSwapCover.id);

      let status = "scheduled";
      let tone = "blue";
      let detail = "Scheduled";
      let varianceMinutes = 0;

      if (isAttendance) {
        status = "attendance";
        tone = "slate";
        detail = schedule.attendanceLabel || "Leave / attendance";
      } else if (isOff) {
        status = "off";
        tone = "slate";
        detail = "Off day";
      } else if (actual) {
        if (scheduledStartSec) {
          varianceMinutes = Math.round((actual.clockInSec - scheduledStartSec) / 60);
        }

        // MS3.5: persisted manager decisions are authoritative.
        // Labor already consumes these fields. Today's Schedule Intelligence must
        // not reconstruct an approved/rejected concluded shift as a fresh exception.
        const varianceResolved =
          actual.scheduleVarianceApproved === true ||
          actual.scheduleVarianceRejected === true;

        if (varianceResolved && actual.clockOutSec) {
          status = actual.scheduleVarianceApproved === true ? "approved" : "rejected";
          tone = actual.scheduleVarianceApproved === true ? "green" : "slate";
          detail =
            actual.scheduleVarianceApproved === true
              ? "Overtime approved"
              : "Overtime rejected";
        } else if (varianceMinutes < -todayGraceMinutes) {
          // The same attendance grace applies on BOTH sides of the published
          // start time. Example: with a 10m grace, 13:50-14:10 is considered
          // on time for a 14:00 shift. Earlier than 13:50 remains potential OT.
          status = "overtime";
          tone = "blue";
          detail = `${compactDuration(Math.abs(varianceMinutes))} early overtime · approval required`;
        } else if (varianceMinutes > todayGraceMinutes) {
          status = "late";
          tone = "amber";
          detail = `${compactDuration(varianceMinutes)} late`;
        } else {
          status = "on-time";
          tone = "green";
          detail =
            varianceMinutes !== 0 && todayGraceMinutes > 0
              ? `On time · ${compactDuration(Math.abs(varianceMinutes))} ${varianceMinutes < 0 ? "early" : "late"} within grace`
              : "On time";
        }

        if (
          actual.clockOutSec &&
          scheduledEndSec &&
          actual.scheduleVarianceApproved !== true &&
          actual.scheduleVarianceRejected !== true
        ) {
          const outVariance = Math.round((actual.clockOutSec - scheduledEndSec) / 60);
          if (outVariance > 0) {
            status = "overtime";
            tone = "blue";
            detail = `${compactDuration(outVariance)} overtime`;
          } else if (outVariance < 0) {
            status = "early-out";
            tone = "amber";
            detail = `${compactDuration(Math.abs(outVariance))} early clock-out`;
          }
        } else if (!actual.clockOutSec && scheduledEndSec && nowSec > scheduledEndSec) {
          const overtimeMinutes = Math.round((nowSec - scheduledEndSec) / 60);
          status = "overtime";
          tone = "blue";
          detail = `${compactDuration(overtimeMinutes)} overtime · still clocked in`;
        }
      } else if (expectsClock && approvedSwapCover) {
        status = "covered";
        tone = "green";
        detail = `Covered by ${approvedSwapCover.name || approvedSwapCover.swapReplacementStaffName || "replacement"} · approved swap`;
      } else if (
        expectsClock &&
        scheduledStartSec &&
        nowSec > scheduledStartSec + (todayGraceMinutes * 60)
      ) {
        const missedMinutes = Math.round((nowSec - scheduledStartSec) / 60);
        status = "no-show";
        tone = "rose";
        detail =
          scheduledEndSec && nowSec >= scheduledEndSec
            ? "Not clocked in"
            : `${compactDuration(missedMinutes)} not clocked in`;
      } else if (expectsClock) {
        const untilStart = scheduledStartSec ? Math.round((scheduledStartSec - nowSec) / 60) : 0;
        const graceRemaining =
          todayGraceMinutes > 0 && scheduledStartSec && nowSec > scheduledStartSec
            ? Math.max(0, todayGraceMinutes - Math.round((nowSec - scheduledStartSec) / 60))
            : 0;
        status = "upcoming";
        tone = "blue";
        detail =
          graceRemaining > 0
            ? `Grace window · ${compactDuration(graceRemaining)} remaining`
            : untilStart > 0
            ? `Starts in ${compactDuration(untilStart)}`
            : "Scheduled";
      }

      const activeShiftRoleName = String(actual?.role || actual?.roleName || "").trim();
      const activeShiftRoleId = String(actual?.roleId || "").trim();

      return {
        ...schedule,
        // Today is an operational view. Once the employee clocks in, the active
        // shift role becomes the displayed/working role. The published roster role
        // remains planning context only.
        roleId:
          activeShiftRoleId ||
          schedule.roleId ||
          roleIdForStaff(schedule.staffId, schedule.staffName) ||
          "",
        roleName:
          activeShiftRoleName ||
          schedule.roleName ||
          schedule.role ||
          roleNameForStaff(schedule.staffId, schedule.staffName) ||
          "",
        actual,
        approvedSwapCover,
        expectsClock,
        scheduledStartSec,
        scheduledEndSec,
        status,
        tone,
        detail,
      };
    });

    const unscheduledRows = currentDayActualShifts
      .filter((shift) => !matchedActualIds.has(shift.id))
      .filter(
        (shift) =>
          !(
            (shift.scheduleVarianceApproved === true && shift.scheduleVarianceType === "swap") ||
            (shift.scheduleVarianceRejected === true && shift.scheduleVarianceType === "swap_rejected")
          )
      )
      .map((actual) => ({
        id: `unscheduled-${actual.id}`,
        staffId: actual.staffId,
        staffName: actual.name || "Unknown staff",
        roleName: actual.role || actual.roleName || roleNameForStaff(actual.staffId, actual.name) || "",
        storeId: actual.storeId || activeStore?.id || "",
        storeName: actual.storeName || stores.find((store) => store.id === actual.storeId)?.name || activeStore?.name || "",
        actual,
        status: "unscheduled",
        tone: "amber",
        detail: "Clocked in without a published shift",
        expectsClock: false,
      }));

    const previousShiftRows = previousOpenShifts.map((actual) => {
      const nextSchedule =
        plannedRows.find((row) =>
          (actual.staffId && row.staffId && String(row.staffId) === String(actual.staffId)) ||
          String(row.staffName || "").trim().toLowerCase() === String(actual.name || "").trim().toLowerCase()
        ) || null;

      const openMinutes = Math.max(0, Math.floor((nowSec - actual.clockInSec) / 60));
      const clockedInDate = new Date(actual.clockInSec * 1000);
      const sinceLabel = clockedInDate.toLocaleString("en-ZA", {
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });

      let nextShiftMessage = "";
      if (nextSchedule?.expectsClock && nextSchedule?.startTime) {
        const untilNext = nextSchedule.scheduledStartSec
          ? Math.round((nextSchedule.scheduledStartSec - nowSec) / 60)
          : null;
        nextShiftMessage =
          untilNext != null && untilNext > 0
            ? ` · next shift ${nextSchedule.startTime} starts in ${compactDuration(untilNext)}`
            : ` · today's scheduled shift is ${nextSchedule.startTime}–${nextSchedule.endTime}`;
      }

      return {
        id: `previous-open-${actual.id}`,
        staffId: actual.staffId || "",
        staffName: actual.name || "Unknown staff",
        roleName: actual.role || actual.roleName || roleNameForStaff(actual.staffId, actual.name) || "",
        storeId: actual.storeId || activeStore?.id || "",
        storeName:
          actual.storeName ||
          stores.find((store) => store.id === actual.storeId)?.name ||
          activeStore?.name ||
          "",
        actual,
        status: "previous-shift-open",
        tone: "rose",
        detail: `Still clocked in from previous shift since ${sinceLabel} · ${compactDuration(openMinutes)} open${nextShiftMessage}`,
        expectsClock: false,
        previousShift: true,
        nextSchedule,
      };
    });

    const allRows = [...plannedRows, ...unscheduledRows, ...previousShiftRows];
    const workingScheduled = plannedRows.filter((row) => row.expectsClock);
    return {
      rows: allRows,
      scheduled: workingScheduled.length,
      // Arrival punctuality is independent from how the shift later ended.
      // Someone who clocked in within grace still counts as On Time even if they
      // later clock out early or move into overtime.
      onTime: plannedRows.filter((row) => {
        if (!row.expectsClock || !row.actual?.clockInSec) return false;
        const scheduledStartSec = Number(row.scheduledStartSec || 0);
        if (!Number.isFinite(scheduledStartSec) || scheduledStartSec <= 0) return false;
        return (
          row.actual.clockInSec >= scheduledStartSec - (todayGraceMinutes * 60) &&
          row.actual.clockInSec <= scheduledStartSec + (todayGraceMinutes * 60)
        );
      }).length,
      late: plannedRows.filter((row) => row.status === "late").length,
      noShow: plannedRows.filter((row) => row.status === "no-show").length,
      covered: plannedRows.filter((row) => row.status === "covered").length,
      overtime: plannedRows.filter((row) => row.status === "overtime").length,
      earlyOut: plannedRows.filter((row) => row.status === "early-out").length,
      unscheduled: unscheduledRows.length,
      previousOpen: previousShiftRows.length,
      attendance: plannedRows.filter((row) => row.status === "attendance").length,
    };
  }, [todaySchedules, todayActualShifts, todayNow, todayKey, todayGraceMinutes, activeStore?.id, activeStore?.name, stores, roleNameForStaff, roleIdForStaff]);

  const exceptionIntelligence = useMemo(() => {
    const relevantRows = todayIntelligence.rows.filter((row) =>
      allStoresSelected || row.storeId === activeStore?.id
    );

    // Detect a likely same-store shift replacement before treating the two signals
    // as unrelated "no-show" + "unscheduled" exceptions. This intentionally uses
    // operational coverage: one expected person is absent while one OFF/unscheduled
    // person is actively covering at the same store.
    const noShowRows = relevantRows.filter(
      (row) => row.status === "no-show" && !row.approvedSwapCover
    );
    const replacementRows = relevantRows.filter((row) =>
      (row.status === "unscheduled" || row.status === "off") &&
      row.actual?.clockInSec &&
      row.actual?.scheduleVarianceApproved !== true &&
      row.actual?.scheduleVarianceRejected !== true
    );
    const usedReplacementIds = new Set();
    const swapRows = noShowRows.map((missing) => {
      const replacement = replacementRows.find((candidate) => {
        if (usedReplacementIds.has(candidate.id)) return false;
        const sameStore =
          candidate.storeId === missing.storeId ||
          (!!candidate.storeName && candidate.storeName === missing.storeName);
        return sameStore && !!candidate.actual?.clockInSec;
      });
      if (!replacement) return null;
      usedReplacementIds.add(replacement.id);
      return {
        id: `swap-${missing.id}-${replacement.id}`,
        type: "swap",
        tone: "amber",
        title: `${missing.staffName || "Scheduled staff"} was expected, but ${replacement.staffName || "replacement staff"} showed up instead`,
        detail: `${missing.staffName || "Scheduled staff"} was scheduled to work. ${replacement.staffName || "Replacement staff"} was not expected today but is clocked in and covering the shift.`,
        storeName: missing.storeName || replacement.storeName || replacement.actual?.storeName || "",
        roleName: missing.roleName || replacement.roleName || replacement.actual?.role || "",
        source: "attendance",
        missingRow: missing,
        row: replacement,
      };
    }).filter(Boolean);

    const pairedNoShowIds = new Set(swapRows.map((item) => item.missingRow.id));
    const attendanceExceptions = relevantRows
      .filter((row) => ["late", "no-show", "overtime", "early-out", "unscheduled", "previous-shift-open"].includes(row.status))
      .filter((row) => !(row.status === "no-show" && pairedNoShowIds.has(row.id)))
      .filter((row) => !(row.status === "unscheduled" && usedReplacementIds.has(row.id)))
      .map((row) => ({
        id: `attendance-${row.id}`,
        type: row.status,
        tone: row.tone,
        title:
          row.status === "no-show"
            ? `${row.staffName || "Staff member"} has not clocked in`
            : row.status === "unscheduled"
            ? `${row.staffName || "Staff member"} clocked in unscheduled`
            : row.status === "late"
            ? `${row.staffName || "Staff member"} arrived late`
            : row.status === "early-out"
            ? `${row.staffName || "Staff member"} clocked out early`
            : row.status === "previous-shift-open"
            ? `${row.staffName || "Staff member"} is still clocked in from a previous shift`
            : `${row.staffName || "Staff member"} is in overtime`,
        detail: row.detail,
        storeName: row.storeName || row.actual?.storeName || "",
        roleName: row.roleName || row.actual?.role || "",
        source: "attendance",
        row,
      }));

    // Overtime is an approval dimension, not the row's final attendance status.
    // Example: someone clocks in 2h early and clocks out 1h early. Today should
    // correctly display "early out", while the 2h worked before the published
    // shift still requires an overtime approve/reject decision in Exceptions.
    const boundaryOvertimeExceptions = relevantRows
      .filter((row) => {
        const actual = row.actual;
        if (!row.expectsClock || !actual?.clockInSec) return false;
        if (actual.scheduleVarianceApproved === true || actual.scheduleVarianceRejected === true) return false;

        const scheduledStartSec = Number(row.scheduledStartSec || 0);
        const scheduledEndSec = Number(row.scheduledEndSec || 0);
        if (!scheduledStartSec || !scheduledEndSec) return false;

        const actualEndSec = Number(actual.clockOutSec || 0);
        const earlySecondsRaw = Math.max(
          0,
          Math.min(actualEndSec || scheduledStartSec, scheduledStartSec) - Number(actual.clockInSec || 0)
        );
        const earlyGraceSeconds = Math.max(0, Number(todayGraceMinutes || 0)) * 60;
        const earlySeconds = Math.max(0, earlySecondsRaw - earlyGraceSeconds);
        const lateSeconds =
          actualEndSec > 0
            ? Math.max(0, actualEndSec - Math.max(Number(actual.clockInSec || 0), scheduledEndSec))
            : Math.max(0, Math.floor(todayNow / 1000) - scheduledEndSec);

        return earlySeconds + lateSeconds > 0;
      })
      .map((row) => {
        const actual = row.actual;
        const scheduledStartSec = Number(row.scheduledStartSec || 0);
        const scheduledEndSec = Number(row.scheduledEndSec || 0);
        const actualEndSec = Number(actual.clockOutSec || 0);
        const earlySecondsRaw = Math.max(
          0,
          Math.min(actualEndSec || scheduledStartSec, scheduledStartSec) - Number(actual.clockInSec || 0)
        );
        const earlyGraceSeconds = Math.max(0, Number(todayGraceMinutes || 0)) * 60;
        const earlySeconds = Math.max(0, earlySecondsRaw - earlyGraceSeconds);
        const lateSeconds =
          actualEndSec > 0
            ? Math.max(0, actualEndSec - Math.max(Number(actual.clockInSec || 0), scheduledEndSec))
            : Math.max(0, Math.floor(todayNow / 1000) - scheduledEndSec);
        const pendingMinutes = Math.max(1, Math.round((earlySeconds + lateSeconds) / 60));

        return {
          id: `boundary-overtime-${row.id}`,
          type: "overtime",
          tone: "blue",
          title: `${row.staffName || "Staff member"} has overtime pending`,
          detail: `${compactDuration(pendingMinutes)} outside the published ${row.startTime || ""}–${row.endTime || ""} shift`,
          storeName: row.storeName || actual.storeName || "",
          roleName: row.roleName || actual.role || "",
          source: "attendance",
          row: {
            ...row,
            status: "overtime",
            tone: "blue",
            detail: `${compactDuration(pendingMinutes)} overtime pending`,
          },
        };
      });

    // If Today already rendered the same row as overtime, don't add it twice.
    const normalOvertimeActualIds = new Set(
      attendanceExceptions
        .filter((item) => item.type === "overtime")
        .map((item) => item.row?.actual?.id)
        .filter(Boolean)
    );
    const supplementalBoundaryOvertime = boundaryOvertimeExceptions.filter(
      (item) => !normalOvertimeActualIds.has(item.row?.actual?.id)
    );

    const now = Date.now();
    const queueExceptions = dutyQueueRows
      .filter((item) => {
        const storeMatches =
          allStoresSelected ||
          !activeStore?.id ||
          item.storeId === activeStore.id ||
          item.storeName === activeStore.name ||
          item.siteName === activeStore.name;
        if (!storeMatches) return false;

        const dueMs =
          item.dueAt?.toMillis?.() ||
          (item.dueAt?.seconds ? item.dueAt.seconds * 1000 : 0) ||
          (item.dueAt ? new Date(item.dueAt).getTime() : 0);
        const dueToday = !dueMs || localTodayKey() === isoDate(new Date(dueMs));
        return dueToday;
      })
      .filter((item) => {
        const roleRequired =
          item.ownershipMode === "scheduled_role" ||
          !!item.ownerRoleId ||
          !!item.ownerRoleName;
        const assigned =
          item.assignedStaffId ||
          item.assignedStaffName ||
          item.assignedToUid ||
          item.assignedTo ||
          item.ownerUid ||
          item.ownerName ||
          item.responseOwnerUid ||
          item.responseOwner;
        const completed =
          item.status === "completed" ||
          item.completed === true ||
          item.completedAt ||
          item.resolved === true ||
          item.resolvedAt;
        const lifecycleRequiresAttention =
          ["owner_unavailable", "awaiting_replacement", "overdue", "escalated"].includes(
            String(item.lifecycleStatus || "")
          );
        return roleRequired && !assigned && !completed && lifecycleRequiresAttention;
      })
      .map((item) => ({
        id: `duty-${item.id}`,
        type: "missing-owner",
        tone: "rose",
        title: `${item.dutyLabel || item.label || item.dutyName || "Duty"} has no available owner`,
        detail: item.ownerRoleName
          ? `${item.ownerRoleName} is required, but Neo could not resolve an available staff member from the published roster or live attendance.`
          : "Neo could not resolve an available staff member for this duty.",
        storeName: item.storeName || item.siteName || "",
        roleName: item.ownerRoleName || "",
        source: "duty",
        queueItem: item,
      }));

    const currentActualKeys = new Set(attendanceExceptions.map((item) => {
      const actual = item?.row?.actual;
      return actual?.clockInSec ? `${String(actual.staffId || actual.name || "").toLowerCase()}|${actual.clockInSec}|${String(actual.storeId || "")}` : "";
    }).filter(Boolean));
    const historicalOvertimeRows = historicalPendingOvertime.filter((item) => {
      const actual = item?.row?.actual;
      if (!actual?.clockInSec) return false;
      const key = `${String(actual.staffId || actual.name || "").toLowerCase()}|${actual.clockInSec}|${String(actual.storeId || "")}`;
      return !currentActualKeys.has(key);
    });
    const includeLiveToday = ["today", "this-week", "this-month"].includes(exceptionPeriod);
    const all = [
      ...(includeLiveToday ? queueExceptions : []),
      ...(includeLiveToday ? swapRows : []),
      ...(includeLiveToday ? attendanceExceptions : []),
      ...(includeLiveToday ? supplementalBoundaryOvertime : []),
      ...historicalOvertimeRows,
    ];
    return {
      rows: all,
      late: all.filter((row) => row.type === "late").length,
      noShow: all.filter((row) => row.type === "no-show").length,
      unscheduled: all.filter((row) => row.type === "unscheduled").length,
      overtime: all.filter((row) => row.type === "overtime").length,
      earlyOut: all.filter((row) => row.type === "early-out").length,
      previousOpen: all.filter((row) => row.type === "previous-shift-open").length,
      missingOwner: all.filter((row) => row.type === "missing-owner").length,
      critical: all.filter((row) => ["no-show", "missing-owner", "swap", "previous-shift-open"].includes(row.type)).length,
      total: all.length, generatedAt: now,
    };
  }, [todayIntelligence, dutyQueueRows, allStoresSelected, activeStore?.id, activeStore?.name, historicalPendingOvertime, exceptionPeriod]);

  const decideShiftSwap = useCallback(async (exceptionItem, approved) => {
    const replacement = exceptionItem?.row;
    const actual = replacement?.actual;
    const missing = exceptionItem?.missingRow;
    if (!orgId || !actual?.clockInSec || !actual?.storeId) return;

    const replacementName = replacement.staffName || actual.name || "Replacement staff";
    const expectedName = missing?.staffName || "Scheduled staff";
    const storeId = replacement.storeId || actual.storeId || missing?.storeId || "";
    const storeName =
      replacement.storeName ||
      actual.storeName ||
      missing?.storeName ||
      stores.find((store) => store.id === storeId)?.name ||
      "";
    const decisionType = approved ? "swap" : "swap_rejected";

    setSwapDecisionState((current) => ({ ...current, [exceptionItem.id]: "saving" }));
    try {
      const batch = writeBatch(db);
      const shiftPatch = {
        scheduleVarianceApproved: approved === true,
        scheduleVarianceRejected: approved !== true,
        scheduleVarianceDecisionAt: serverTimestamp(),
        scheduleVarianceApprovedAt: approved ? serverTimestamp() : null,
        scheduleVarianceApprovedByUid: approved ? (actor?.uid || "") : "",
        scheduleVarianceApprovedByEmail: approved ? (actor?.email || "") : "",
        scheduleVarianceApprovedByName: approved ? (actor?.name || "") : "",
        scheduleVarianceRejectedAt: approved ? null : serverTimestamp(),
        scheduleVarianceRejectedByUid: approved ? "" : (actor?.uid || ""),
        scheduleVarianceRejectedByEmail: approved ? "" : (actor?.email || ""),
        scheduleVarianceRejectedByName: approved ? "" : (actor?.name || ""),
        scheduleVarianceType: decisionType,
        swapExpectedStaffId: missing?.staffId || "",
        swapExpectedStaffName: expectedName,
        swapReplacementStaffId: replacement.staffId || actual.staffId || "",
        swapReplacementStaffName: replacementName,
        approvedActualClockInSec: approved ? actual.clockInSec : null,
        approvedActualClockOutSec: approved && actual.clockOutSec ? actual.clockOutSec : null,
        payrollZeroHours: approved ? false : true,
        updatedAt: serverTimestamp(),
      };

      if (actual.source === "nested" && actual.storeId) {
        batch.set(
          doc(db, "orgs", orgId, "stores", actual.storeId, "shifts", String(actual.id).replace(/^nested:/, "")),
          shiftPatch,
          { merge: true }
        );
      } else if (actual.source === "legacy") {
        batch.set(
          doc(db, "cashup_shifts", String(actual.id).replace(/^legacy:/, "")),
          shiftPatch,
          { merge: true }
        );
      }

      const historyRef = doc(collection(db, "orgs", orgId, "neo_schedule_history"));
      batch.set(historyRef, {
        orgId,
        storeId,
        storeName,
        monthKey: localTodayKey().slice(0, 7),
        action: approved ? "approve_shift_swap" : "reject_shift_swap",
        changeCount: 1,
        changes: [{
          staffId: replacement.staffId || actual.staffId || "",
          staffName: replacementName,
          date: localTodayKey(),
          before: `Expected: ${expectedName}`,
          after: approved ? `Covered by: ${replacementName}` : `Rejected replacement: ${replacementName}`,
          varianceType: decisionType,
          approvalNote: approved
            ? `Approved swap · ${expectedName} replaced by ${replacementName}`
            : `Swap rejected · ${replacementName} allocated zero payable hours`,
        }],
        createdAt: serverTimestamp(),
        createdByUid: actor?.uid || "",
        createdByEmail: actor?.email || "",
        createdByName: actor?.name || "",
      });

      await batch.commit();
      setSwapDecisionState((current) => ({ ...current, [exceptionItem.id]: approved ? "approved" : "rejected" }));
      await loadTodayActuals();
      setTodayNow(Date.now());
    } catch (error) {
      console.error("Could not save shift swap decision", error);
      setSwapDecisionState((current) => ({ ...current, [exceptionItem.id]: "error" }));
    }
  }, [orgId, stores, actor?.uid, actor?.email, actor?.name, loadTodayActuals, historyStaffName]);

  const approveActualShift = useCallback(async (exceptionItem) => {
    const row = exceptionItem?.row;
    const actual = row?.actual;
    if (!orgId || !actual?.clockInSec || !actual?.clockOutSec) return;

    const rawStaffName = String(
      row.staffName || actual.name || actual.staffName || actual.staffNameSnapshot || ""
    ).trim();
    const matchedStaff = staff.find((person) =>
      String(person.name || "").trim().toLowerCase() === rawStaffName.toLowerCase()
    ) || null;
    const staffId = row.staffId || actual.staffId || matchedStaff?.id || "";
    const staffName = historyStaffName(staffId, rawStaffName);
    const storeId = row.storeId || actual.storeId || "";
    const storeName =
      row.storeName ||
      actual.storeName ||
      stores.find((store) => store.id === storeId)?.name ||
      "";
    if (!storeId || !staffId) {
      console.error("Could not approve overtime: missing resolvable staff/store identity", {
        exceptionItem,
        rawStaffName,
        storeId,
        staffId,
      });
      setVarianceApprovalState((current) => ({ ...current, [exceptionItem.id]: "error" }));
      return;
    }

    const dateKey = row.date || isoDate(new Date(actual.clockInSec * 1000));
    const startTime = formatClock(actual.clockInSec);
    const endTime = formatClock(actual.clockOutSec);
    const approvedShift = `${startTime}-${endTime}`;
    const scheduleEntryId = row.historical || String(row.id || "").startsWith("historical-")
      ? `${storeId}__${staffId}__${dateKey}`
      : row.id && !String(row.id).startsWith("unscheduled-")
      ? row.id
      : `${storeId}__${staffId}__${dateKey}`;
    const previousShift = row.publishedShift || row.shift || "";
    const overtimeAmount = row.status === "overtime"
      ? String(row.detail || "").split(/\s+overtime/i)[0].trim()
      : "";

    setVarianceApprovalState((current) => ({ ...current, [exceptionItem.id]: "saving" }));

    try {
      const batch = writeBatch(db);
      const scheduleRef = doc(db, "orgs", orgId, "neo_schedule_entries", scheduleEntryId);

      batch.set(
        scheduleRef,
        {
          orgId,
          storeId,
          storeName,
          staffId,
          staffName,
          roleId: row.roleId || "",
          roleName: row.roleName || actual.role || "",
          date: dateKey,
          monthKey: dateKey.slice(0, 7),

          // Phase 3: approving overtime/actual variance must NOT rewrite
          // the published roster. The published schedule remains the
          // regular-time boundary used by Labor.
          varianceApproved: true,
          varianceApprovalType: row.status || "actual_shift",
          varianceApprovedAt: serverTimestamp(),
          varianceApprovedByUid: actor?.uid || "",
          varianceApprovedByEmail: actor?.email || "",
          varianceApprovedByName: actor?.name || "",
          originalPublishedShift: previousShift,
          approvedVarianceActualShift: approvedShift,
          actualClockInSec: actual.clockInSec,
          actualClockOutSec: actual.clockOutSec,
          updatedAt: serverTimestamp(),
          source: "schedule-variance-approval",
        },
        { merge: true }
      );

      const shiftPatch = {
        scheduleVarianceApproved: true,
        scheduleVarianceApprovedAt: serverTimestamp(),
        scheduleVarianceApprovedByUid: actor?.uid || "",
        scheduleVarianceApprovedByEmail: actor?.email || "",
        scheduleVarianceApprovedByName: actor?.name || "",
        approvedScheduleEntryId: scheduleEntryId,
        approvedScheduleShift: approvedShift,
        originalScheduledShift: previousShift,
        scheduleVarianceType: row.status || "actual_shift",

        // Accepted actual working period. Labor remains the source used by Payroll;
        // these fields make the manager approval explicit and auditable.
        approvedActualClockInSec: actual.clockInSec,
        approvedActualClockOutSec: actual.clockOutSec,
        approvedActualStartTime: startTime,
        approvedActualEndTime: endTime,
        updatedAt: serverTimestamp(),
      };

      if (actual.source === "nested" && actual.storeId) {
        batch.set(
          doc(db, "orgs", orgId, "stores", actual.storeId, "shifts", String(actual.id).replace(/^nested:/, "")),
          shiftPatch,
          { merge: true }
        );
      } else if (actual.source === "legacy") {
        batch.set(
          doc(db, "cashup_shifts", String(actual.id).replace(/^legacy:/, "")),
          shiftPatch,
          { merge: true }
        );
      }

      const historyRef = doc(collection(db, "orgs", orgId, "neo_schedule_history"));
      batch.set(historyRef, {
        orgId,
        storeId,
        storeName,
        monthKey: dateKey.slice(0, 7),
        action: row.status === "overtime" ? "approve_overtime" : "approve_actual_shift",
        changeCount: 1,
        changes: [{
          staffId,
          staffName,
          date: dateKey,
          before: previousShift,
          after: approvedShift,
          varianceType: row.status || "actual_shift",
          approvalNote: row.status === "overtime"
            ? `Approved overtime${overtimeAmount ? ` · ${overtimeAmount}` : ""}`
            : "Approved actual shift",
        }],
        createdAt: serverTimestamp(),
        createdByUid: actor?.uid || "",
        createdByEmail: actor?.email || "",
        createdByName: actor?.name || "",
      });

      await batch.commit();
      setVarianceApprovalState((current) => ({ ...current, [exceptionItem.id]: "approved" }));
      await loadTodayActuals();
      setHistoricalExceptionRefreshKey((current) => current + 1);
    } catch (error) {
      console.error("Could not approve actual shift", {
        code: error?.code || "unknown",
        message: error?.message || String(error),
        source: actual?.source || "unknown",
        storeId,
        date: dateKey,
        scheduleEntryId,
      });
      if (error?.code === "permission-denied") {
        await diagnoseNeoVarianceBatch({
          orgId, storeId, scheduleEntryId, actual,
          historyRef: doc(db, "orgs", orgId, "neo_schedule_history", "neo-read-only-authorization-probe"),
          action: "approve",
        }).catch((diagnosticError) => console.warn("Neo schedule diagnostic unavailable", diagnosticError));
      }
      setVarianceApprovalState((current) => ({ ...current, [exceptionItem.id]: "error" }));
    }
  }, [orgId, stores, staff, actor?.uid, actor?.email, actor?.name, loadTodayActuals, historyStaffName]);

  const rejectActualVariance = useCallback(async (exceptionItem) => {
    const row = exceptionItem?.row;
    const actual = row?.actual;
    if (!orgId || !actual?.clockInSec || !actual?.clockOutSec) return;

    const rawStaffName = String(
      row.staffName || actual.name || actual.staffName || actual.staffNameSnapshot || ""
    ).trim();
    const matchedStaff = staff.find((person) =>
      String(person.name || "").trim().toLowerCase() === rawStaffName.toLowerCase()
    ) || null;
    const staffId = row.staffId || actual.staffId || matchedStaff?.id || "";
    const staffName = historyStaffName(staffId, rawStaffName);
    const storeId = row.storeId || actual.storeId || "";
    const storeName = row.storeName || actual.storeName || stores.find((store) => store.id === storeId)?.name || "";
    if (!storeId) {
      console.error("Could not reject shift variance: missing store identity", { exceptionItem });
      setVarianceApprovalState((current) => ({ ...current, [exceptionItem.id]: "error" }));
      return;
    }

    const dateKey = row.date || isoDate(new Date(actual.clockInSec * 1000));
    const previousShift = row.publishedShift || row.shift || "";
    const scheduleEntryId = staffId
      ? (
          row.historical || String(row.id || "").startsWith("historical-")
            ? `${storeId}__${staffId}__${dateKey}`
            : row.id && !String(row.id).startsWith("unscheduled-")
            ? row.id
            : `${storeId}__${staffId}__${dateKey}`
        )
      : "";
    const isUnscheduled = row.status === "unscheduled" || exceptionItem.type === "unscheduled";
    const rejectionType = isUnscheduled ? "unscheduled_rejected" : "overtime_rejected";
    const rejectionAction = isUnscheduled ? "reject_unscheduled_shift" : "reject_overtime";
    const overtimeAmount = String(row.detail || exceptionItem.detail || "").split(/\s+overtime/i)[0].trim();

    setVarianceApprovalState((current) => ({ ...current, [exceptionItem.id]: "saving" }));
    try {
      const batch = writeBatch(db);
      if (scheduleEntryId) {
        batch.set(
          doc(db, "orgs", orgId, "neo_schedule_entries", scheduleEntryId),
          {
            // Historical exceptions may have no existing schedule entry.
            // A merge into a missing document is a CREATE, and the Firestore
            // neo_schedule_entries create rule requires a valid storeId.
            // Include identity fields without rewriting the published roster.
            orgId,
            storeId,
            storeName,
            staffId,
            staffName,
            roleId: row.roleId || "",
            roleName: row.roleName || actual.role || "",
            date: dateKey,
            monthKey: dateKey.slice(0, 7),
            varianceApproved: false,
            varianceRejected: true,
            varianceApprovalType: rejectionType,
            varianceRejectedAt: serverTimestamp(),
            varianceRejectedByUid: actor?.uid || "",
            varianceRejectedByEmail: actor?.email || "",
            varianceRejectedByName: actor?.name || "",
            originalPublishedShift: previousShift,
            actualClockInSec: actual.clockInSec,
            actualClockOutSec: actual.clockOutSec,
            updatedAt: serverTimestamp(),
            source: "schedule-variance-rejection",
          },
          { merge: true }
        );
      }

      const shiftPatch = {
        scheduleVarianceApproved: false,
        scheduleVarianceRejected: true,
        scheduleVarianceType: rejectionType,
        scheduleVarianceRejectedAt: serverTimestamp(),
        scheduleVarianceRejectedByUid: actor?.uid || "",
        scheduleVarianceRejectedByEmail: actor?.email || "",
        scheduleVarianceRejectedByName: actor?.name || "",
        originalScheduledShift: previousShift,
        updatedAt: serverTimestamp(),
      };

      if (actual.source === "nested") {
        const nestedStoreId = actual.storeId || storeId;
        const nestedShiftId = String(actual.id || "").replace(/^nested:/, "");
        if (!nestedStoreId || !nestedShiftId) {
          throw new Error("Historical overtime shift is missing its nested Firestore identity.");
        }
        batch.set(
          doc(db, "orgs", orgId, "stores", nestedStoreId, "shifts", nestedShiftId),
          shiftPatch,
          { merge: true }
        );
      } else if (actual.source === "legacy") {
        const legacyShiftId = String(actual.id || "").replace(/^legacy:/, "");
        if (!legacyShiftId) throw new Error("Historical overtime shift is missing its legacy Firestore identity.");
        batch.set(doc(db, "cashup_shifts", legacyShiftId), shiftPatch, { merge: true });
      } else {
        throw new Error(`Unsupported overtime shift source: ${actual.source || "unknown"}`);
      }

      const historyRef = doc(collection(db, "orgs", orgId, "neo_schedule_history"));
      batch.set(historyRef, {
        orgId,
        storeId,
        storeName,
        monthKey: dateKey.slice(0, 7),
        action: rejectionAction,
        changeCount: 1,
        changes: [{
          staffId,
          staffName,
          date: dateKey,
          before: previousShift,
          after: `${formatClock(actual.clockInSec)}-${formatClock(actual.clockOutSec)}`,
          varianceType: rejectionType,
          approvalNote: isUnscheduled
            ? "Rejected unscheduled shift"
            : `Rejected overtime${overtimeAmount ? ` · ${overtimeAmount}` : ""}${exceptionItem.autoClockedOut || row.autoClockedOut ? " · Auto clocked out" : ""}`,
        }],
        createdAt: serverTimestamp(),
        createdByUid: actor?.uid || "",
        createdByEmail: actor?.email || "",
        createdByName: actor?.name || "",
      });

      await batch.commit();
      setVarianceApprovalState((current) => ({ ...current, [exceptionItem.id]: "rejected" }));
      await loadTodayActuals();
      setHistoricalExceptionRefreshKey((current) => current + 1);
    } catch (error) {
      console.error("Could not reject shift variance", {
        code: error?.code || "unknown",
        message: error?.message || String(error),
        source: actual?.source || "unknown",
        storeId,
        date: dateKey,
        scheduleEntryId: scheduleEntryId || "none",
      });
      if (error?.code === "permission-denied") {
        await diagnoseNeoVarianceBatch({
          orgId, storeId, scheduleEntryId, actual,
          historyRef: doc(db, "orgs", orgId, "neo_schedule_history", "neo-read-only-authorization-probe"),
          action: "reject",
        }).catch((diagnosticError) => console.warn("Neo schedule diagnostic unavailable", diagnosticError));
      }
      setVarianceApprovalState((current) => ({ ...current, [exceptionItem.id]: "error" }));
    }
  }, [orgId, stores, staff, actor?.uid, actor?.email, actor?.name, loadTodayActuals, historyStaffName]);

  const visibleScheduleHistory = useMemo(() => {
    const periodRange = schedulePeriodRange(historyPeriod);
    return scheduleHistory.filter((item) => {
      const storeMatch = historyStoreId === "ALL" || item.storeId === historyStoreId;
      const created = item.createdAt?.toDate?.();
      const periodMatch = created instanceof Date && !Number.isNaN(created.getTime()) && created >= periodRange[0] && created < periodRange[1];
      return storeMatch && periodMatch;
    });
  }, [scheduleHistory, historyStoreId, historyPeriod]);


  const exportScheduleHistoryPdf = () => {
    if (!visibleScheduleHistory.length) return;

    const selectedStore =
      historyStoreId === "ALL"
        ? "All stores"
        : stores.find((store) => store.id === historyStoreId)?.name || "Selected store";
    const periodLabel = {
      today: "Today",
      "this-week": "This week",
      "last-week": "Last week",
      "this-month": "This month",
      "last-month": "Last month",
    }[historyPeriod] || historyPeriod;

    const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(18);
    pdf.text("Neo Schedule & Leave History", 14, 16);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.text(`Store: ${selectedStore}  |  Period: ${periodLabel}  |  Exported: ${new Date().toLocaleString("en-ZA")}`, 14, 23);

    const rows = [];
    visibleScheduleHistory.forEach((item) => {
      const created = item.createdAt?.toDate?.();
      const publication = created
        ? created.toLocaleString("en-ZA", { dateStyle: "medium", timeStyle: "short" })
        : "Pending";
      const manager = item.createdByName || item.createdByEmail || "—";
      const changes = Array.isArray(item.changes) ? item.changes : [];

      if (!changes.length) {
        rows.push([
          publication,
          item.storeName || "—",
          manager,
          "—",
          "—",
          "—",
          "—",
          item.revisionReason || "",
        ]);
        return;
      }

      changes.forEach((change) => {
        const resolvedStaffId = change.staffId || item.staffId || "";
        const resolvedStaffName = change.staffName || item.staffName || "";
        const staffName = historyStaffName(resolvedStaffId, resolvedStaffName);
        const roleName = roleNameForStaff(resolvedStaffId, staffName) || "—";
        rows.push([
          publication,
          item.storeName || "—",
          manager,
          staffName,
          roleName,
          change.date || "—",
          `${change.before || "—"} -> ${change.after || "—"}`,
          change.reason || item.revisionReason || change.approvalNote || "",
        ]);
      });
    });

    autoTable(pdf, {
      startY: 29,
      head: [["Event time", "Store", "Manager / reviewer", "Staff", "Role", "Date", "Change / decision", "Reason / note"]],
      body: rows,
      styles: { fontSize: 7.5, cellPadding: 2, overflow: "linebreak" },
      headStyles: { fontStyle: "bold" },
      columnStyles: {
        0: { cellWidth: 31 },
        1: { cellWidth: 27 },
        2: { cellWidth: 27 },
        3: { cellWidth: 25 },
        4: { cellWidth: 24 },
        5: { cellWidth: 22 },
        6: { cellWidth: 46 },
        7: { cellWidth: 62 },
      },
      margin: { left: 10, right: 10 },
    });

    const safeStore = selectedStore.replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "").toLowerCase() || "all-stores";
    pdf.save(`neo-schedule-leave-history-${safeStore}-${historyPeriod}.pdf`);
  };

  const scheduleCurrentMonthKey = monthKeyFor(new Date(todayNow));
  const nextScheduleMonthKey = addMonthsToKey(scheduleCurrentMonthKey, 1);
  const storeScheduleMonths = scheduleMonths.filter((row) => row.storeId === activeStore?.id);
  const currentScheduleMonth = storeScheduleMonths.find((row) => row.monthKey === scheduleCurrentMonthKey) || null;
  const plannedScheduleMonths = storeScheduleMonths.filter((row) => row.monthKey > scheduleCurrentMonthKey);
  const previousScheduleMonths = scheduleMonths
    .filter((row) => siteScope.allSites || (siteScope.siteIds || []).includes(row.storeId))
    .filter((row) => row.monthKey < scheduleCurrentMonthKey)
    .sort((a, b) => String(b.monthKey).localeCompare(String(a.monthKey)));

  const openScheduleBuilder = (monthKey, { readOnly = false } = {}) => {
    if (!activeStore?.id) return;
    setBuilderStoreId(activeStore.id);
    setBuilderMonthKey(monthKey || scheduleCurrentMonthKey);
    setBuilderReadOnly(readOnly);
    setBuilderOpen(true);
  };

  const startNewSchedule = () => {
    if (!activeStore?.id || !newScheduleMonthKey || newScheduleMonthKey <= scheduleCurrentMonthKey) return;
    setBuilderStoreId(activeStore.id);
    setBuilderMonthKey(newScheduleMonthKey);
    setBuilderReadOnly(false);
    setNewScheduleOpen(false);
    setBuilderOpen(true);
  };

  const viewContent = useMemo(() => {
    if (view === "leave") {
      return (
        <div className="space-y-4">
          <div className="rounded-2xl border p-5 md:p-6" style={{ ...card, borderColor: "rgba(167,139,250,.28)" }}>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="max-w-3xl">
                <div className="text-xs font-bold uppercase tracking-[.14em] text-violet-300">Leave Intelligence</div>
                <h3 className="mt-2 text-2xl font-black text-white">Leave approvals</h3>
                <p className="mt-2 text-sm" style={muted}>
                  Review staff leave requests here. Approved leave is converted into the published schedule automatically so Neo does not create a false no-show.
                </p>
              </div>
              <Pill tone={pendingLeaveRequests.length ? "amber" : "green"}>
                {pendingLeaveRequests.length ? `${pendingLeaveRequests.length} pending` : "No leave waiting"}
              </Pill>
            </div>

            <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-3">
              <div className="rounded-xl border border-white/10 p-4">
                <div className="text-xs uppercase tracking-wider text-white/45">Pending</div>
                <div className="mt-2 text-3xl font-black text-white">{pendingLeaveRequests.length}</div>
                <div className="mt-1 text-xs" style={muted}>Requests waiting for a decision.</div>
              </div>
              <div className="rounded-xl border border-white/10 p-4">
                <div className="text-xs uppercase tracking-wider text-white/45">Approved</div>
                <div className="mt-2 text-3xl font-black text-emerald-300">{resolvedLeaveRequests.filter((row) => String(row.status).toLowerCase() === "approved").length}</div>
                <div className="mt-1 text-xs" style={muted}>Approved for the selected scope.</div>
              </div>
              <div className="rounded-xl border border-white/10 p-4">
                <div className="text-xs uppercase tracking-wider text-white/45">Rejected</div>
                <div className="mt-2 text-3xl font-black text-rose-300">{resolvedLeaveRequests.filter((row) => String(row.status).toLowerCase() === "rejected").length}</div>
                <div className="mt-1 text-xs" style={muted}>Rejected requests remain auditable.</div>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border overflow-hidden" style={{ ...card, borderColor: "rgba(34,211,238,.24)" }}>
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 p-5">
              <div>
                <div className="text-xs font-bold uppercase tracking-[.14em] text-cyan-300">Leave balance intelligence</div>
                <h3 className="mt-1 text-xl font-black text-white">Leave balances & ledger</h3>
                <p className="mt-1 max-w-2xl text-sm" style={muted}>Neo separates opening balance, accrued entitlement, adjustments, booked, used and available leave while keeping the immutable ledger as the audit trail.</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <select
                  value={builderStoreId}
                  onChange={(e) => setBuilderStoreId(e.target.value)}
                  className="rounded-xl border border-white/10 bg-slate-950/70 px-3 py-2.5 text-sm text-white"
                  aria-label="Leave store filter"
                >
                  <option value="ALL">All stores</option>
                  {stores.map((store) => <option key={store.id} value={store.id}>{store.name}</option>)}
                </select>
                <select
                  value={leavePeriod}
                  onChange={(e) => setLeavePeriod(e.target.value)}
                  className="rounded-xl border border-white/10 bg-slate-950/70 px-3 py-2.5 text-sm text-white"
                  aria-label="Leave period filter"
                >
                  <option value="today">Today</option>
                  <option value="this-week">This week</option>
                  <option value="last-week">Last week</option>
                  <option value="this-month">This month</option>
                  <option value="last-month">Last month</option>
                </select>
                {builderStoreId && builderStoreId !== "ALL" ? (
                  <button
                    type="button"
                    onClick={() => loadLeaveBalances(builderStoreId, true)}
                    disabled={leaveBalanceLoading}
                    className="rounded-xl border border-cyan-400/25 bg-cyan-400/10 px-3 py-2.5 text-xs font-black text-cyan-100 disabled:opacity-40"
                  >
                    {leaveBalanceLoading ? "Refreshing…" : "Refresh balances"}
                  </button>
                ) : null}
              </div>
            </div>

            {builderStoreId && builderStoreId !== "ALL" ? (
              <div className="border-b border-white/10 px-5 py-4">
                <label className="block">
                  <span className="text-[11px] font-bold uppercase tracking-[.14em] text-white/40">Search staff</span>
                  <div className="relative mt-2">
                    <input
                      type="search"
                      value={leaveBalanceSearch}
                      onChange={(e) => setLeaveBalanceSearch(e.target.value)}
                      placeholder="Search by staff name or role…"
                      className="w-full rounded-xl border border-white/10 bg-slate-950/55 px-4 py-3 pr-10 text-sm text-white outline-none placeholder:text-white/30 focus:border-cyan-300/35"
                      aria-label="Search staff leave balances"
                    />
                    {leaveBalanceSearch ? (
                      <button
                        type="button"
                        onClick={() => setLeaveBalanceSearch("")}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-lg font-black text-white/40 hover:text-white/75"
                        aria-label="Clear staff search"
                      >
                        ×
                      </button>
                    ) : null}
                  </div>
                </label>
              </div>
            ) : null}

            {builderStoreId === "ALL" || !builderStoreId ? (
              <div className="p-5">
                <div className="rounded-xl border border-dashed border-cyan-300/20 bg-cyan-400/5 p-5">
                  <div className="font-black text-cyan-100">Choose one store to manage balances</div>
                  <div className="mt-2 text-sm text-white/55">Neo deliberately does not download every employee balance across the organisation here. Select a store above to keep this workspace fast and Firestore reads small.</div>
                </div>
              </div>
            ) : leaveBalanceLoading ? (
              <div className="p-8 text-center text-sm text-white/55">Loading staff leave balances…</div>
            ) : leaveBalanceError ? (
              <div className="p-5 text-sm text-rose-200">{leaveBalanceError}</div>
            ) : (
              <div className="p-4">
                <div className="grid grid-cols-1 gap-3">
                  {visibleLeaveBalanceStaff.map((person) => {
                    const summary = balanceDocForStaff(person.id);
                    const balances = summary?.balances || {};
                    const expanded = expandedLeaveBalanceStaff.has(person.id);
                    return (
                      <div key={person.id} className="overflow-hidden rounded-xl border border-white/10 bg-slate-950/25">
                        <button
                          type="button"
                          onClick={() => toggleLeaveBalanceStaff(person.id)}
                          className="flex w-full items-center justify-between gap-4 p-4 text-left transition hover:bg-white/[.025]"
                          aria-expanded={expanded}
                        >
                          <div className="min-w-0">
                            <div className="truncate text-lg font-black text-white">{person.name || "Staff member"}</div>
                            <div className="mt-1 truncate text-xs text-white/45">{roleNameForStaff(person.id, person.name) || "Staff"}</div>
                          </div>
                          <div className="flex shrink-0 items-center gap-3">
                            <span className="hidden text-[11px] font-bold uppercase tracking-wider text-white/35 sm:inline">{expanded ? "Collapse" : "View balances"}</span>
                            <span className={`text-xl font-black text-cyan-200 transition-transform ${expanded ? "rotate-180" : ""}`}>⌄</span>
                          </div>
                        </button>

                        {expanded ? (
                          <div className="border-t border-white/10 p-4">
                            <div className="flex flex-wrap justify-end gap-2">
                              <button type="button" onClick={() => openLeaveLedger(person)} className="rounded-lg border border-white/10 px-3 py-2 text-xs font-bold text-white/70 hover:bg-white/5">View ledger</button>
                              <button type="button" onClick={() => openLeaveBalanceEditor(person, "adjustment")} className="rounded-lg border border-violet-400/25 bg-violet-400/10 px-3 py-2 text-xs font-black text-violet-100">Adjust balance</button>
                              <button type="button" onClick={() => openLeaveBalanceEditor(person, "opening")} className="rounded-lg border border-cyan-400/25 bg-cyan-400/10 px-3 py-2 text-xs font-black text-cyan-100">Set opening balance</button>
                            </div>
                            <div className="mt-4 grid grid-cols-2 gap-2 md:grid-cols-3 xl:grid-cols-5">
                              {managedLeaveTypes.map((type) => {
                                const item = normaliseLeaveBalanceEntry(balances[type.id] || {});
                                return (
                                  <div key={type.id} className="rounded-lg border border-white/10 bg-white/[.025] p-3">
                                    <div className="text-[11px] font-bold uppercase tracking-wider text-white/40">{type.label}</div>
                                    <div className="mt-1 text-lg font-black text-white">{formatLeaveDays(item.availableDays)}</div>
                                    <div className="mt-3 grid grid-cols-2 gap-2 text-[10px] text-white/40 sm:grid-cols-3 xl:grid-cols-2 2xl:grid-cols-3">
                                      <div className="min-w-0 rounded-md bg-white/[.025] px-2 py-1.5"><span className="block truncate uppercase tracking-wide">Opening</span><span className="mt-0.5 block font-bold text-cyan-100/75">{formatLeaveDays(item.openingBalanceDays)}</span></div>
                                      <div className="min-w-0 rounded-md bg-white/[.025] px-2 py-1.5"><span className="block truncate uppercase tracking-wide">Accrued</span><span className="mt-0.5 block font-bold text-emerald-200/80">{formatLeaveDays(item.accruedDays)}</span></div>
                                      <div className="min-w-0 rounded-md bg-white/[.025] px-2 py-1.5"><span className="block truncate uppercase tracking-wide">Adjustments</span><span className="mt-0.5 block font-bold text-white/65">{item.manualAdjustmentDays > 0 ? "+" : ""}{formatLeaveDays(item.manualAdjustmentDays)}</span></div>
                                      <div className="min-w-0 rounded-md bg-white/[.025] px-2 py-1.5"><span className="block truncate uppercase tracking-wide">Booked</span><span className="mt-0.5 block font-bold text-amber-200/80">{formatLeaveDays(item.bookedDays)}</span></div>
                                      <div className="min-w-0 rounded-md bg-white/[.025] px-2 py-1.5"><span className="block truncate uppercase tracking-wide">Used</span><span className="mt-0.5 block font-bold text-violet-200/80">{formatLeaveDays(item.usedDays)}</span></div>
                                    </div>
                                    <div className="mt-2 text-[11px] text-white/30">{item.openingBalanceSet ? "Opening balance recorded" : "Opening not set"}</div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                  {!staff.some((person) => String(person.storeId || "") === String(builderStoreId)) ? (
                    <div className="rounded-xl border border-dashed border-white/10 p-8 text-center text-sm text-white/45">No active staff found for this store.</div>
                  ) : visibleLeaveBalanceStaff.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-white/10 p-8 text-center text-sm text-white/45">No staff match “{leaveBalanceSearch}”.</div>
                  ) : null}
                </div>
              </div>
            )}
          </div>

          <div className="rounded-2xl border overflow-hidden" style={card}>
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 p-5">
              <div>
                <div className="text-xs font-bold uppercase tracking-[.14em] text-violet-300">Pending manager decisions</div>
                <h3 className="mt-1 text-xl font-black text-white">Requests requiring review</h3>
              </div>

            </div>

            {leaveLoading ? (
              <div className="p-8 text-center text-sm text-white/55">Loading leave requests…</div>
            ) : !pendingLeaveRequests.length ? (
              <div className="p-8">
                <div className="rounded-xl border border-dashed border-white/10 p-8 text-center">
                  <div className="font-black text-white">No leave decisions waiting.</div>
                  <div className="mt-2 text-sm" style={muted}>New staff leave requests will appear here automatically.</div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3 p-4 md:grid-cols-2 xl:grid-cols-3">
                {pendingLeaveRequests.map((request) => (
                  <button
                    key={`${request.storeId}:${request.id}`}
                    type="button"
                    onClick={() => {
                      setSelectedLeaveId(request.id);
                      setLeaveDecisionNote(request.managerNote || "");
                      setLeaveDecisionState((current) => {
                        if (!current[request.id] || current[request.id] === "saving") return current;
                        const next = { ...current };
                        delete next[request.id];
                        return next;
                      });
                    }}
                    className="rounded-xl border border-violet-300/15 bg-violet-400/5 p-4 text-left transition hover:border-violet-300/40 hover:bg-violet-400/10"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-lg font-black text-white">{request.staffName || "Staff member"}</div>
                        <div className="mt-1 text-xs text-white/45">{request.staffRole || "Staff"} · {request.storeName || "Store"}</div>
                      </div>
                      <Pill tone="amber">Needs decision</Pill>
                    </div>
                    <div className="mt-4 font-bold text-violet-200">{leaveTypeLabel(request.leaveType)}</div>
                    <div className="mt-1 text-sm text-slate-300">{request.startDate || "—"}{request.endDate && request.endDate !== request.startDate ? ` → ${request.endDate}` : ""}</div>
                    {request.reason ? <div className="mt-3 line-clamp-2 text-sm text-white/55">{request.reason}</div> : null}
                    <div className="mt-4 text-xs font-black text-violet-200">Review request →</div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {resolvedLeaveRequests.length ? (
            <div className="rounded-2xl border overflow-hidden" style={card}>
              <div className="border-b border-white/10 p-5">
                <div className="text-xs font-bold uppercase tracking-[.14em] text-cyan-300">Recent leave decisions</div>
                <h3 className="mt-1 text-xl font-black text-white">Approved & rejected</h3>
              </div>
              <div className="grid grid-cols-1 gap-2 p-4">
                {resolvedLeaveRequests.slice(0, 12).map((request) => {
                  const approved = String(request.status || "").toLowerCase() === "approved";
                  const syncKey = `${request.storeId}:${request.id}`;
                  const syncState = leaveSyncState[syncKey] || request.scheduleSyncStatus || "";
                  return (
                    <button
                      key={syncKey}
                      type="button"
                      onClick={() => { setSelectedLeaveId(request.id); setLeaveDecisionNote(request.managerNote || ""); }}
                      className="flex w-full flex-wrap items-center justify-between gap-3 rounded-xl border border-white/10 bg-slate-950/25 p-4 text-left hover:border-violet-300/25"
                    >
                      <div>
                        <div className="font-black text-white">{request.staffName || "Staff member"} · {leaveTypeLabel(request.leaveType)}</div>
                        <div className="mt-1 text-xs text-white/45">{request.startDate || "—"}{request.endDate && request.endDate !== request.startDate ? ` → ${request.endDate}` : ""} · {request.storeName || "Store"}</div>
                        <div className="mt-1 text-xs text-white/50">
                          {approved ? "Approved" : "Rejected"} by {request.reviewedBy || request.reviewedByName || request.reviewedByEmail || "Manager"}
                          {leaveTsDate(request.reviewedAt) ? ` · ${leaveTsDate(request.reviewedAt).toLocaleString("en-ZA", { dateStyle: "medium", timeStyle: "short" })}` : ""}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {approved ? <Pill tone={syncState === "synced" ? "green" : "amber"}>{syncState === "synced" ? "Schedule synced" : "Syncing schedule"}</Pill> : null}
                        <Pill tone={approved ? "green" : "rose"}>{approved ? "Approved" : "Rejected"}</Pill>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}

          {leaveBalanceEditor ? (() => {
            const existing = balanceDocForStaff(leaveBalanceEditor.id)?.balances?.[leaveBalanceTypeId] || {};
            const selectedType = managedLeaveTypes.find((row) => row.id === leaveBalanceTypeId);
            const openingAlreadySet = existing.openingBalanceSet === true;
            const ready = Math.abs(numericDays(leaveBalanceAmount)) > 0 && String(leaveBalanceReason || "").trim() && selectedType && !(leaveBalanceMode === "opening" && openingAlreadySet);
            return (
              <div className="fixed inset-0 z-[3400] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm" onMouseDown={() => setLeaveBalanceEditor(null)}>
                <div className="w-full max-w-xl rounded-2xl border border-cyan-400/20 bg-[#0b1b30] p-5 shadow-2xl" onMouseDown={(e) => e.stopPropagation()}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-xs font-bold uppercase tracking-[.14em] text-cyan-300">{leaveBalanceMode === "opening" ? "Opening balance" : "Manual adjustment"}</div>
                      <h3 className="mt-1 text-xl font-black text-white">{leaveBalanceEditor.name || "Staff member"}</h3>
                      <div className="mt-1 text-sm text-white/50">Current {selectedType?.label || "leave"}: {formatLeaveDays(existing.availableDays || 0)}</div>
                    </div>
                    <button type="button" onClick={() => setLeaveBalanceEditor(null)} className="rounded-lg border border-white/10 px-3 py-1.5 text-white/60">✕</button>
                  </div>

                  <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <label>
                      <span className="text-xs font-bold uppercase tracking-wider text-white/50">Leave type</span>
                      <select value={leaveBalanceTypeId} onChange={(e) => setLeaveBalanceTypeId(e.target.value)} className="mt-1 w-full rounded-xl border border-white/10 bg-slate-950/70 px-3 py-3 text-sm text-white">
                        {managedLeaveTypes.map((type) => <option key={type.id} value={type.id}>{type.label}</option>)}
                      </select>
                    </label>
                    <label>
                      <span className="text-xs font-bold uppercase tracking-wider text-white/50">Effective date</span>
                      <input type="date" value={leaveBalanceEffectiveDate} onChange={(e) => setLeaveBalanceEffectiveDate(e.target.value)} className="mt-1 w-full rounded-xl border border-white/10 bg-slate-950/70 px-3 py-3 text-sm text-white" />
                    </label>
                  </div>

                  {leaveBalanceMode === "adjustment" ? (
                    <div className="mt-3 grid grid-cols-2 gap-2">
                      <button type="button" onClick={() => setLeaveBalanceDirection("add")} className={`rounded-xl border px-3 py-3 text-sm font-black ${leaveBalanceDirection === "add" ? "border-emerald-400/35 bg-emerald-400/10 text-emerald-100" : "border-white/10 text-white/55"}`}>Add days</button>
                      <button type="button" onClick={() => setLeaveBalanceDirection("deduct")} className={`rounded-xl border px-3 py-3 text-sm font-black ${leaveBalanceDirection === "deduct" ? "border-rose-400/35 bg-rose-400/10 text-rose-100" : "border-white/10 text-white/55"}`}>Deduct days</button>
                    </div>
                  ) : null}

                  <label className="mt-3 block">
                    <span className="text-xs font-bold uppercase tracking-wider text-white/50">Days</span>
                    <input type="number" min="0" step="0.25" value={leaveBalanceAmount} onChange={(e) => setLeaveBalanceAmount(e.target.value)} placeholder="e.g. 7.5" className="mt-1 w-full rounded-xl border border-white/10 bg-slate-950/70 px-3 py-3 text-white" />
                  </label>
                  <label className="mt-3 block">
                    <span className="text-xs font-bold uppercase tracking-wider text-white/50">Reason / source</span>
                    <textarea rows="3" value={leaveBalanceReason} onChange={(e) => setLeaveBalanceReason(e.target.value)} placeholder={leaveBalanceMode === "opening" ? "e.g. Balance brought forward from previous payroll system" : "Why is this balance being corrected?"} className="mt-1 w-full rounded-xl border border-white/10 bg-slate-950/70 px-3 py-3 text-sm text-white" />
                  </label>

                  {leaveBalanceMode === "opening" && openingAlreadySet ? (
                    <div className="mt-3 rounded-xl border border-amber-400/25 bg-amber-400/10 p-3 text-sm text-amber-100">An opening balance has already been recorded for {selectedType?.label}. Use <b>Adjust balance</b> so the correction stays auditable.</div>
                  ) : null}
                  {leaveBalanceSaveState !== "idle" && leaveBalanceSaveState !== "saving" && leaveBalanceSaveState !== "saved" ? (
                    <div className="mt-3 text-sm text-rose-200">{leaveBalanceSaveState}</div>
                  ) : null}

                  <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                    <button type="button" onClick={() => setLeaveBalanceEditor(null)} className="rounded-xl border border-white/10 px-4 py-2.5 text-sm font-bold text-white/65">Cancel</button>
                    <button type="button" disabled={!ready || leaveBalanceSaveState === "saving"} onClick={saveLeaveBalanceTransaction} className="rounded-xl border border-cyan-400/30 bg-cyan-400/10 px-4 py-2.5 text-sm font-black text-cyan-100 disabled:opacity-35">
                      {leaveBalanceSaveState === "saving" ? "Saving…" : leaveBalanceSaveState === "saved" ? "Recorded" : leaveBalanceMode === "opening" ? "Record opening balance" : "Record adjustment"}
                    </button>
                  </div>
                </div>
              </div>
            );
          })() : null}

          {leaveLedgerStaff ? (
            <div className="fixed inset-0 z-[3390] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm" onMouseDown={() => setLeaveLedgerStaff(null)}>
              <div className="w-full max-w-3xl rounded-2xl border border-violet-400/20 bg-[#0b1b30] p-5 shadow-2xl" onMouseDown={(e) => e.stopPropagation()}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-xs font-bold uppercase tracking-[.14em] text-violet-300">Leave balance ledger</div>
                    <h3 className="mt-1 text-xl font-black text-white">{leaveLedgerStaff.name || "Staff member"}</h3>
                    <div className="mt-1 text-sm text-white/50">Latest 100 balance transactions · loaded only when requested.</div>
                  </div>
                  <button type="button" onClick={() => setLeaveLedgerStaff(null)} className="rounded-lg border border-white/10 px-3 py-1.5 text-white/60">✕</button>
                </div>
                <div className="mt-5 max-h-[60vh] space-y-2 overflow-y-auto pr-1">
                  {leaveLedgerLoading ? <div className="p-6 text-center text-sm text-white/50">Loading ledger…</div> : leaveLedgerRows.length ? leaveLedgerRows.map((row) => {
                    const created = leaveTsDate(row.createdAt);
                    return (
                      <div key={row.id} className="rounded-xl border border-white/10 bg-slate-950/25 p-4">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <div className="font-black text-white">{row.leaveTypeLabel || row.leaveTypeId || "Leave"}</div>
                            <div className="mt-1 text-xs text-white/45">{row.type === "opening_balance" ? "Opening balance" : row.type === "automatic_accrual" ? "Automatic accrual" : "Manual adjustment"} · Effective {row.effectiveDate || "—"}</div>
                          </div>
                          <Pill tone={Number(row.deltaDays || 0) >= 0 ? "green" : "rose"}>{Number(row.deltaDays || 0) >= 0 ? "+" : ""}{formatLeaveDays(row.deltaDays || 0)}</Pill>
                        </div>
                        <div className="mt-3 text-sm text-white/65">{row.reason || "No reason recorded"}</div>
                        <div className="mt-2 text-xs text-white/40">{formatLeaveDays(row.balanceBeforeDays || 0)} → <span className="font-bold text-white/65">{formatLeaveDays(row.balanceAfterDays || 0)}</span> · {row.createdByName || row.createdByEmail || "Admin"}{created ? ` · ${created.toLocaleString("en-ZA", { dateStyle: "medium", timeStyle: "short" })}` : ""}</div>
                        {(row.openingAfterDays !== undefined || row.accruedAfterDays !== undefined || row.adjustmentAfterDays !== undefined) ? (
                          <div className="mt-2 text-[11px] text-white/30">
                            Opening {formatLeaveDays(row.openingAfterDays || 0)} · Accrued {formatLeaveDays(row.accruedAfterDays || 0)} · Adjustments {Number(row.adjustmentAfterDays || 0) > 0 ? "+" : ""}{formatLeaveDays(row.adjustmentAfterDays || 0)} · Booked {formatLeaveDays(row.bookedAfterDays || 0)} · Used {formatLeaveDays(row.usedAfterDays || 0)}
                          </div>
                        ) : (row.creditedAfterDays !== undefined || row.bookedAfterDays !== undefined || row.usedAfterDays !== undefined) ? (
                          <div className="mt-2 text-[11px] text-white/30">Credited {formatLeaveDays(row.creditedAfterDays || 0)} · Booked {formatLeaveDays(row.bookedAfterDays || 0)} · Used {formatLeaveDays(row.usedAfterDays || 0)}</div>
                        ) : null}
                      </div>
                    );
                  }) : <div className="rounded-xl border border-dashed border-white/10 p-8 text-center text-sm text-white/45">No balance transactions yet.</div>}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      );
    }

    if (view === "builder") {
      const storeStaffCount = staff.filter((person) => person.storeId === activeStore?.id).length;
      return (
        <div className="space-y-4">
          <div className="rounded-2xl border p-5 md:p-6" style={{ ...card, borderColor: "rgba(16,185,129,.28)" }}>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="max-w-3xl">
                <div className="text-xs font-bold uppercase tracking-[.14em] text-emerald-300">Monthly planning workspace</div>
                <h3 className="mt-2 text-2xl font-black text-white">Schedule workspace</h3>
                <p className="mt-2 text-sm" style={muted}>
                  The current month follows the calendar automatically. Build future rosters ahead of time, save them as drafts, or publish them when the team is ready.
                </p>
              </div>
              <Pill tone="green">MatrixOS-native scheduling</Pill>
            </div>

            <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-[1fr_auto]">
              <label>
                <span className="text-xs font-bold uppercase tracking-wider text-white/60">Store</span>
                <select
                  value={builderStoreId}
                  onChange={(e) => setBuilderStoreId(e.target.value)}
                  className="mt-2 w-full rounded-xl border border-white/10 bg-slate-950/70 px-3 py-3 text-sm text-white"
                >
                  {stores.map((store) => <option key={store.id} value={store.id}>{store.name}</option>)}
                </select>
              </label>
              <div className="flex items-end gap-2">
                <button
                  type="button"
                  disabled={!activeStore}
                  onClick={() => { setBuilderMonthKey(scheduleCurrentMonthKey); setBuilderReadOnly(false); setBuilderOpen(true); }}
                  className="rounded-xl border border-cyan-400/35 bg-cyan-400/10 px-5 py-3 text-sm font-black text-cyan-100 transition hover:bg-cyan-400/15 disabled:opacity-40"
                >
                  Open Current Schedule
                </button>
                <button
                  type="button"
                  disabled={!activeStore}
                  onClick={() => { setNewScheduleMonthKey(nextScheduleMonthKey); setNewScheduleOpen(true); }}
                  className="rounded-xl border border-emerald-400/35 bg-emerald-400/10 px-5 py-3 text-sm font-black text-emerald-100 transition hover:bg-emerald-400/15 disabled:opacity-40"
                >
                  + New Schedule
                </button>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
              <div className="rounded-xl border border-white/10 p-4">
                <div className="text-xs uppercase tracking-wider text-white/45">Current month</div>
                <div className="mt-2 font-bold text-white">{formatMonth(scheduleCurrentMonthKey)}</div>
                <div className="mt-1 text-xs" style={muted}>
                  {currentScheduleMonth ? `${currentScheduleMonth.status === "published" ? "Published" : "Draft"} schedule detected.` : "No saved schedule yet."}
                </div>
              </div>
              <div className="rounded-xl border border-white/10 p-4">
                <div className="text-xs uppercase tracking-wider text-white/45">Staff source</div>
                <div className="mt-2 font-bold text-white">{storeStaffCount} active staff</div>
                <div className="mt-1 text-xs" style={muted}>Automatically absorbed from Staff.</div>
              </div>
              <div className="rounded-xl border border-white/10 p-4">
                <div className="text-xs uppercase tracking-wider text-white/45">Future planning</div>
                <div className="mt-2 font-bold text-white">{plannedScheduleMonths.length} built ahead</div>
                <div className="mt-1 text-xs" style={muted}>Draft and published future months stay ready here.</div>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border overflow-hidden" style={card}>
            <div className="flex flex-wrap items-start justify-between gap-3 border-b border-white/10 p-5">
              <div>
                <div className="text-xs font-bold uppercase tracking-[.14em] text-cyan-300">Built schedules</div>
                <h3 className="mt-1 text-xl font-black text-white">Upcoming rosters</h3>
                <p className="mt-1 text-sm" style={muted}>Build several months in advance. When the calendar changes, Neo automatically promotes that month to Current.</p>
              </div>
              <Pill tone={plannedScheduleMonths.length ? "blue" : "slate"}>{plannedScheduleMonths.length} upcoming</Pill>
            </div>
            {!plannedScheduleMonths.length ? (
              <div className="p-6">
                <div className="rounded-xl border border-dashed border-white/10 p-6 text-center">
                  <div className="font-black text-white">No future schedules built yet.</div>
                  <div className="mt-2 text-sm" style={muted}>Use New Schedule to start next month or plan several months ahead.</div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3 p-4 md:grid-cols-2 xl:grid-cols-3">
                {plannedScheduleMonths.map((row) => (
                  <button
                    type="button"
                    key={row.id}
                    onClick={() => { setBuilderStoreId(row.storeId); setBuilderMonthKey(row.monthKey); setBuilderReadOnly(false); setBuilderOpen(true); }}
                    className="rounded-xl border border-white/10 bg-slate-950/25 p-4 text-left transition hover:border-cyan-400/35 hover:bg-cyan-400/5"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-lg font-black text-white">{formatMonth(row.monthKey)}</div>
                        <div className="mt-1 text-xs text-white/45">{row.storeName || activeStore?.name || "Store"}</div>
                      </div>
                      <Pill tone={row.status === "published" ? "green" : "amber"}>{row.status === "published" ? "Published" : "Draft"}</Pill>
                    </div>
                    <div className="mt-4 text-xs font-bold text-cyan-200">Open schedule →</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      );
    }

    if (view === "history") {
      return (
        <div className="space-y-4">
          <div className="rounded-2xl border overflow-hidden" style={card}>
            <div className="flex flex-wrap items-start justify-between gap-3 border-b border-white/10 p-5">
              <div>
                <div className="text-xs font-bold uppercase tracking-[.14em] text-violet-200">Previous schedules</div>
                <h3 className="mt-1 text-xl font-black text-white">Past monthly rosters</h3>
                <p className="mt-1 text-sm" style={muted}>When a month ends, its roster moves here automatically. Previous schedules are view-only.</p>
              </div>
              <Pill tone={previousScheduleMonths.length ? "blue" : "slate"}>{previousScheduleMonths.length} previous</Pill>
            </div>
            {!previousScheduleMonths.length ? (
              <div className="p-6 text-center text-sm text-white/50">No previous monthly schedules yet.</div>
            ) : (
              <div className="grid grid-cols-1 gap-3 p-4 md:grid-cols-2 xl:grid-cols-3">
                {previousScheduleMonths.map((row) => (
                  <button
                    type="button"
                    key={row.id}
                    onClick={() => { setBuilderStoreId(row.storeId); setBuilderMonthKey(row.monthKey); setBuilderReadOnly(true); setBuilderOpen(true); }}
                    className="rounded-xl border border-white/10 bg-slate-950/25 p-4 text-left transition hover:border-violet-400/35 hover:bg-violet-400/5"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-lg font-black text-white">{formatMonth(row.monthKey)}</div>
                        <div className="mt-1 text-xs text-white/45">{row.storeName || "Store"}</div>
                      </div>
                      <Pill tone={row.status === "published" ? "green" : "amber"}>{row.status === "published" ? "Published" : "Draft"}</Pill>
                    </div>
                    <div className="mt-4 text-xs font-bold text-violet-200">View roster →</div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="rounded-2xl border p-4" style={card}>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <label>
                <span className="text-xs font-bold uppercase tracking-wider text-white/50">History · Store</span>
                <select
                  value={historyStoreId}
                  onChange={(e) => setHistoryStoreId(e.target.value)}
                  className="mt-2 w-full rounded-xl border border-white/10 bg-slate-950/70 px-3 py-2.5 text-sm text-white"
                >
                  <option value="ALL">All stores</option>
                  {stores.map((store) => <option key={store.id} value={store.id}>{store.name}</option>)}
                </select>
              </label>
              <label>
                <span className="text-xs font-bold uppercase tracking-wider text-white/50">Filter period</span>
                <select
                  value={historyPeriod}
                  onChange={(e) => setHistoryPeriod(e.target.value)}
                  className="mt-2 w-full rounded-xl border border-white/10 bg-slate-950/70 px-3 py-2.5 text-sm text-white"
                >
                  <option value="today">Today</option>
                  <option value="this-week">This week</option>
                  <option value="last-week">Last week</option>
                  <option value="this-month">This Month</option>
                  <option value="last-month">Last Month</option>
                </select>
              </label>
            </div>
          </div>

          <div className="rounded-2xl border overflow-hidden" style={card}>
            <div className="flex flex-wrap items-start justify-between gap-3 border-b border-white/10 p-5">
              <div>
                <div className="text-xs font-bold uppercase tracking-[.14em] text-cyan-300">Schedule history</div>
                <h3 className="mt-1 text-xl font-black text-white">Schedule & leave history</h3>
                <p className="mt-1 text-sm" style={muted}>
                  An immutable operational trail of roster publications and leave decisions, including who approved or rejected each request.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={exportScheduleHistoryPdf}
                  disabled={!visibleScheduleHistory.length}
                  className="rounded-xl border border-cyan-400/25 bg-cyan-400/10 px-3 py-2 text-xs font-black text-cyan-100 hover:bg-cyan-400/15 disabled:cursor-not-allowed disabled:opacity-35"
                >
                  Export PDF
                </button>
                <Pill tone={visibleScheduleHistory.length ? "blue" : "slate"}>
                  {visibleScheduleHistory.length} event{visibleScheduleHistory.length === 1 ? "" : "s"}
                </Pill>
              </div>
            </div>

            {historyLoading ? (
              <div className="p-6 text-sm text-white/60">Loading schedule history…</div>
            ) : historyError ? (
              <div className="p-6">
                <div className="rounded-xl border border-rose-400/20 bg-rose-500/5 p-4 text-sm text-rose-200">{historyError}</div>
              </div>
            ) : !visibleScheduleHistory.length ? (
              <div className="p-6">
                <div className="rounded-xl border border-dashed border-white/10 p-6 text-center">
                  <div className="font-black text-white">No schedule or leave history for this selection.</div>
                  <div className="mt-2 text-sm" style={muted}>History is created automatically when rosters change or leave decisions are made.</div>
                </div>
              </div>
            ) : (
              <div className="divide-y divide-white/5">
                {visibleScheduleHistory.map((item) => {
                  const created = item.createdAt?.toDate?.();
                  const changes = Array.isArray(item.changes) ? item.changes : [];
                  return (
                    <div key={item.id} className="p-4 md:p-5">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <div className="flex flex-wrap items-center gap-2">
                            <div className="font-black text-white">{item.storeName || "Store schedule"}</div>
                            {item.monthKey ? <Pill tone="blue">{formatMonth(item.monthKey)}</Pill> : null}
                            <Pill tone={item.type === "leave_rejected" ? "rose" : "green"}>
                              {item.type === "leave_rejected" ? "Leave rejected" : item.type === "leave_approved" ? "Leave approved" : "Published"}
                            </Pill>
                          </div>
                          <div className="mt-2 text-xs text-white/50">
                            {created ? created.toLocaleString("en-ZA", { dateStyle: "medium", timeStyle: "short" }) : "Event time pending"}
                            {item.createdByName || item.createdByEmail
                              ? ` · ${item.type === "leave_rejected" ? "Rejected by" : item.type === "leave_approved" ? "Approved by" : "By"} ${item.createdByName || item.createdByEmail}`
                              : ""}
                            {item.reviewedVia ? ` · ${String(item.reviewedVia).replaceAll("_", " ")}` : ""}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-black text-white">{item.changeCount ?? changes.length}</div>
                          <div className="text-[10px] font-bold uppercase tracking-wider text-white/40">changes</div>
                        </div>
                      </div>

                      {changes.length ? (
                        <div className="mt-4 grid grid-cols-1 gap-2 lg:grid-cols-2">
                          {changes.map((change, index) => {
                            const displayStaffName = historyStaffName(
                              change.staffId || item.staffId,
                              change.staffName || item.staffName
                            );
                            return (
                              <div key={`${item.id}-${index}`} className="rounded-xl border border-white/10 bg-slate-950/25 p-3">
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                  <div>
                                    <div className="text-sm font-bold text-white">{displayStaffName}</div>
                                    <div className="mt-0.5 text-xs text-white/45">{change.date || "Date unavailable"}</div>
                                  </div>
                                  <div className="text-right text-xs">
                                    <span className="text-white/40">{change.before || "Unscheduled"}</span>
                                    <span className="mx-2 text-cyan-300">→</span>
                                    <span className="font-bold text-white">{attendanceIdFromValue(change.after)
                                      ? `${attendanceTypes.find((row) => row.id === attendanceIdFromValue(change.after))?.label || item.leaveLabel || "Leave"}${attendanceHoursFromValue(change.after, attendanceTypes) ? ` · ${attendanceHoursFromValue(change.after, attendanceTypes)}h` : ""}`
                                      : (change.after || "Unscheduled")}</span>
                                  </div>
                                </div>
                                {change.reason ? (
                                  <div className="mt-2 rounded-lg border border-amber-400/15 bg-amber-400/5 px-2.5 py-2 text-xs text-amber-100">
                                    <span className="font-bold">Reason:</span> {change.reason}
                                  </div>
                                ) : null}
                                {(() => {
                                  const note =
                                    change.approvalNote ||
                                    (item.action === "approve_actual_shift"
                                      ? approvedOvertimeNote(change.before, change.after)
                                      : "");
                                  return note ? (
                                    <div className={`mt-2 text-xs font-semibold ${item.type === "leave_rejected" ? "text-rose-300" : "text-emerald-300"}`}>
                                      {note}
                                    </div>
                                  ) : null;
                                })()}
                                {item.managerNote && item.managerNote !== change.reason ? (
                                  <div className="mt-2 rounded-lg border border-white/10 bg-white/[.03] px-2.5 py-2 text-xs text-white/65">
                                    <span className="font-bold text-white/80">Manager note:</span> {item.managerNote}
                                  </div>
                                ) : null}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="mt-3 text-xs text-white/45">No individual shift changes were stored for this publication.</div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      );
    }

    if (view === "exceptions") {
      const periodLabel = {
        today: "Today",
        "this-week": "This week",
        "last-week": "Last week",
        "this-month": "This month",
        "last-month": "Last month",
      }[exceptionPeriod] || "Today";

      const exceptionTone = (type) => {
        if (["no-show", "missing-owner", "previous-shift-open"].includes(type)) return "rose";
        if (["late", "unscheduled", "swap", "early-out"].includes(type)) return "amber";
        if (type === "overtime") return "blue";
        return "slate";
      };

      return (
        <div className="space-y-4">
          <div className="rounded-2xl border p-4" style={card}>
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div className="grid min-w-[240px] flex-1 grid-cols-1 gap-3 md:grid-cols-[minmax(240px,1fr)_190px]">
                <label>
                  <span className="text-xs font-bold uppercase tracking-wider text-white/50">Exceptions · Store</span>
                  <select
                    value={builderStoreId}
                    onChange={(e) => setBuilderStoreId(e.target.value)}
                    className="mt-2 w-full rounded-xl border border-white/10 bg-slate-950/70 px-3 py-2.5 text-sm text-white"
                  >
                    <option value="ALL">All stores</option>
                    {stores.map((store) => <option key={store.id} value={store.id}>{store.name}</option>)}
                  </select>
                </label>
                <label>
                  <span className="text-xs font-bold uppercase tracking-wider text-white/50">Period</span>
                  <select
                    value={exceptionPeriod}
                    onChange={(e) => setExceptionPeriod(e.target.value)}
                    className="mt-2 w-full rounded-xl border border-white/10 bg-slate-950/70 px-3 py-2.5 text-sm text-white"
                  >
                    <option value="today">Today</option>
                    <option value="this-week">This week</option>
                    <option value="last-week">Last week</option>
                    <option value="this-month">This month</option>
                    <option value="last-month">Last month</option>
                  </select>
                </label>
              </div>
              <div className="flex items-center gap-2">
                <Pill tone={exceptionIntelligence.total ? "amber" : "green"}>
                  {exceptionIntelligence.total ? `${exceptionIntelligence.total} pending` : "Clear"}
                </Pill>
                <button
                  type="button"
                  onClick={refreshTodayAttendance}
                  disabled={attendanceRefreshFeedback === "refreshing"}
                  className="rounded-xl border border-cyan-400/25 bg-cyan-400/5 px-3 py-2 text-xs font-bold text-cyan-100 hover:bg-cyan-400/10 disabled:cursor-wait disabled:opacity-70"
                >
                  {attendanceRefreshFeedback === "refreshing" ? "Refreshing…" : "Refresh"}
                </button>
              </div>
            </div>
          </div>

          <PayrollOperationalVerificationQueue key={`${orgId}/${builderStoreId}`} orgId={orgId} siteId={builderStoreId} />

          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
            {[
              ["Overtime", exceptionIntelligence.overtime, "blue"],
              ["No-show", exceptionIntelligence.noShow, "rose"],
              ["Unscheduled", exceptionIntelligence.unscheduled, "amber"],
              ["Late", exceptionIntelligence.late, "amber"],
              ["Early out", exceptionIntelligence.earlyOut, "amber"],
              ["Missing owner", exceptionIntelligence.missingOwner, "rose"],
            ].map(([title, value, tone]) => (
              <div key={title} className="rounded-2xl border p-4" style={card}>
                <div className="flex items-center justify-between gap-2">
                  <div className="text-[11px] font-bold uppercase tracking-[.12em] text-sky-300">{title}</div>
                  <Pill tone={tone}>{value}</Pill>
                </div>
                <div className="mt-3 text-2xl font-black text-white">{value}</div>
              </div>
            ))}
          </div>

          <div className="rounded-2xl border overflow-hidden" style={card}>
            <div className="flex flex-wrap items-start justify-between gap-3 border-b border-white/10 p-5">
              <div>
                <div className="text-xs font-bold uppercase tracking-[.14em] text-amber-300">Attendance decisions</div>
                <h3 className="mt-1 text-xl font-black text-white">Exceptions requiring review</h3>
                <p className="mt-1 text-sm" style={muted}>
                  Approve or reject overtime and unscheduled work here. Labor keeps the published roster as the regular-time boundary until you decide.
                </p>
              </div>
              <Pill tone={exceptionIntelligence.total ? "amber" : "green"}>{periodLabel}</Pill>
            </div>

            {historicalExceptionLoading ? (
              <div className="p-6 text-sm text-white/60">Loading exceptions…</div>
            ) : !exceptionIntelligence.rows.length ? (
              <div className="p-6">
                <div className="rounded-xl border border-dashed border-white/10 p-6 text-center">
                  <div className="font-black text-white">No pending exceptions for this selection.</div>
                  <div className="mt-2 text-sm" style={muted}>Overtime, unscheduled shifts and other attendance exceptions will appear here when they require review.</div>
                </div>
              </div>
            ) : (
              <div className="divide-y divide-white/5">
                {exceptionIntelligence.rows.map((item) => {
                  const decision = varianceApprovalState[item.id] || swapDecisionState[item.id] || "";
                  const actual = item?.row?.actual;
                  const canDecideVariance = ["overtime", "unscheduled"].includes(item.type) && !!actual?.clockInSec && !!actual?.clockOutSec;
                  const canDecideSwap = item.type === "swap" && !!actual?.clockInSec;
                  return (
                    <div key={item.id} className="p-4 md:p-5">
                      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <div className="font-black text-white">{item.title}</div>
                            <Pill tone={exceptionTone(item.type)}>{String(item.type || "exception").replaceAll("-", " ")}</Pill>
                            {item.row?.historical ? <Pill tone="slate">Historical</Pill> : null}
                          </div>
                          <div className="mt-1 text-xs text-white/50">
                            {[item.storeName, item.roleName].filter(Boolean).join(" · ") || "Operational exception"}
                          </div>
                          <div className="mt-2 text-sm text-white/75">{item.detail}</div>
                          {(() => {
                            const scheduledRow = item.type === "swap" ? item.missingRow : item.row;
                            const scheduledLabel =
                              scheduledRow?.expectsClock && scheduledRow?.startTime && scheduledRow?.endTime
                                ? `${scheduledRow.startTime} → ${scheduledRow.endTime}`
                                : "";
                            if (!scheduledLabel && !actual?.clockInSec) return null;
                            return (
                              <div className={`mt-3 grid gap-2 ${scheduledLabel && actual?.clockInSec ? "md:grid-cols-2" : "grid-cols-1"}`}>
                                {scheduledLabel ? (
                                  <div className="rounded-xl border border-white/10 bg-white/[0.025] px-3 py-2.5">
                                    <div className="text-[10px] font-bold uppercase tracking-[.14em] text-white/45">Published schedule</div>
                                    <div className="mt-1 text-sm font-black text-white">{scheduledLabel}</div>
                                  </div>
                                ) : null}
                                {actual?.clockInSec ? (
                                  <div className="rounded-xl border border-cyan-400/15 bg-cyan-400/5 px-3 py-2.5">
                                    <div className="text-[10px] font-bold uppercase tracking-[.14em] text-sky-300">Actual attendance</div>
                                    <div className="mt-1 flex flex-wrap items-center gap-2 text-sm font-black text-white">
                                      <span>{formatClock(actual.clockInSec)} → {actual.clockOutSec ? formatClock(actual.clockOutSec) : "On shift"}</span>
                                      {item.autoClockedOut ? <Pill tone="amber">Auto clock-out</Pill> : null}
                                    </div>
                                  </div>
                                ) : null}
                              </div>
                            );
                          })()}
                          {decision === "approved" ? <div className="mt-2 text-xs font-bold text-emerald-300">✓ Approved</div> : null}
                          {decision === "rejected" ? <div className="mt-2 text-xs font-bold text-rose-300">✓ Rejected</div> : null}
                          {decision === "error" ? <div className="mt-2 text-xs font-bold text-rose-300">Could not save decision. Please try again.</div> : null}
                        </div>

                        <div className="flex flex-wrap items-center gap-2 lg:justify-end">
                          {canDecideVariance && !["approved", "rejected"].includes(decision) ? (
                            <>
                              <button
                                type="button"
                                disabled={decision === "saving"}
                                onClick={() => setVarianceConfirm({ item, approved: true })}
                                className="rounded-xl border border-emerald-400/35 bg-emerald-400/10 px-4 py-2.5 text-xs font-black text-emerald-100 transition hover:bg-emerald-400/15 disabled:opacity-50"
                              >
                                {decision === "saving" ? "Saving…" : item.type === "unscheduled" ? "Approve shift" : "Approve overtime"}
                              </button>
                              <button
                                type="button"
                                disabled={decision === "saving"}
                                onClick={() => setVarianceConfirm({ item, approved: false })}
                                className="rounded-xl border border-rose-400/35 bg-rose-400/10 px-4 py-2.5 text-xs font-black text-rose-100 transition hover:bg-rose-400/15 disabled:opacity-50"
                              >
                                {item.type === "unscheduled" ? "Reject shift" : "Reject overtime"}
                              </button>
                            </>
                          ) : null}

                          {canDecideSwap && !["approved", "rejected"].includes(decision) ? (
                            <>
                              <button
                                type="button"
                                disabled={decision === "saving"}
                                onClick={() => setSwapConfirm({ item, approved: true })}
                                className="rounded-xl border border-emerald-400/35 bg-emerald-400/10 px-4 py-2.5 text-xs font-black text-emerald-100 transition hover:bg-emerald-400/15 disabled:opacity-50"
                              >
                                Approve replacement
                              </button>
                              <button
                                type="button"
                                disabled={decision === "saving"}
                                onClick={() => setSwapConfirm({ item, approved: false })}
                                className="rounded-xl border border-rose-400/35 bg-rose-400/10 px-4 py-2.5 text-xs font-black text-rose-100 transition hover:bg-rose-400/15 disabled:opacity-50"
                              >
                                Reject replacement
                              </button>
                            </>
                          ) : null}

                          {["overtime", "unscheduled"].includes(item.type) && !canDecideVariance && !["approved", "rejected"].includes(decision) ? (
                            <Pill tone="blue">Decision available after clock-out</Pill>
                          ) : null}
                          {!["overtime", "unscheduled", "swap"].includes(item.type) ? (
                            <Pill tone={exceptionTone(item.type)}>Monitor</Pill>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-3 rounded-2xl border p-4 xl:grid-cols-[minmax(0,1fr)_auto] xl:items-end" style={card}>
          <div className="grid min-w-0 grid-cols-1 gap-3 md:grid-cols-[minmax(220px,1fr)_150px_minmax(210px,.9fr)]">
            <label>
              <span className="text-xs font-bold uppercase tracking-wider text-white/50">Today · Store</span>
              <select
                value={builderStoreId}
                onChange={(e) => setBuilderStoreId(e.target.value)}
                className="mt-2 w-full rounded-xl border border-white/10 bg-slate-950/70 px-3 py-2.5 text-sm text-white"
              >
                <option value="ALL">All stores</option>
                {stores.map((store) => <option key={store.id} value={store.id}>{store.name}</option>)}
              </select>
            </label>

            <label>
              <span className="text-xs font-bold uppercase tracking-wider text-white/50">Grace period</span>
              <select
                value={todayGraceMinutes}
                onChange={(e) => updateTodayGraceMinutes(e.target.value)}
                className="mt-2 w-full rounded-xl border border-white/10 bg-slate-950/70 px-3 py-2.5 text-sm text-white"
              >
                <option value={0}>None</option>
                <option value={5}>5 min</option>
                <option value={10}>10 min</option>
                <option value={15}>15 min</option>
                <option value={30}>30 min</option>
              </select>
            </label>
            <label>
              <span className="text-xs font-bold uppercase tracking-wider text-white/50">Escalation delay</span>
              <div className="mt-2 flex min-w-0 flex-wrap items-center gap-2">
                <div className="flex min-w-0 flex-1 items-center overflow-hidden rounded-xl border border-white/10 bg-slate-950/70">
                  <input
                    ref={todayEscalationInputRef}
                    type="text"
                    inputMode="numeric"
                    defaultValue={todayEscalationMinutes}
                    onInput={(e) => {
                      const clean = String(e.currentTarget.value || "")
                        .replace(/[^0-9]/g, "")
                        .slice(0, 3);
                      if (e.currentTarget.value !== clean) {
                        e.currentTarget.value = clean;
                      }
                    }}
                    onFocus={() => {
                      todayEscalationEditingRef.current = true;
                    }}
                    onBlur={() => {
                      todayEscalationEditingRef.current = false;
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        const raw = String(e.currentTarget.value || "").trim();
                        if (raw !== "") {
                          todayEscalationEditingRef.current = false;
                          updateTodayEscalationMinutes(raw);
                        }
                      }
                      if (e.key === "Escape") {
                        e.currentTarget.value = String(todayEscalationMinutes);
                        todayEscalationEditingRef.current = false;
                        e.currentTarget.blur();
                      }
                    }}
                    className="min-w-0 flex-1 bg-transparent px-3 py-2.5 text-sm text-white outline-none"
                    aria-label="Minutes after the grace reminder before manager escalation"
                  />
                  <span className="border-l border-white/10 px-3 text-xs font-semibold text-white/50">min</span>
                </div>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    const raw = String(todayEscalationInputRef.current?.value || "").trim();
                    if (raw === "") {
                      if (todayEscalationInputRef.current) {
                        todayEscalationInputRef.current.value = String(todayEscalationMinutes);
                      }
                      return;
                    }
                    todayEscalationEditingRef.current = false;
                    updateTodayEscalationMinutes(raw);
                  }}
                  className="rounded-xl border border-cyan-400/30 bg-cyan-500/10 px-3 py-2.5 text-xs font-bold text-cyan-100 transition hover:bg-cyan-500/20"
                >
                  Save
                </button>
                {todayEscalationSaved ? (
                  <span className="whitespace-nowrap text-xs font-bold text-emerald-300">
                    ✓ Saved
                  </span>
                ) : null}
              </div>
            </label>
          </div>
          <div className="flex min-w-0 flex-wrap items-center gap-2 xl:justify-end">
            <Pill tone="slate">{new Date().toLocaleDateString("en-ZA", { weekday: "long", day: "numeric", month: "long" })}</Pill>
            <button
              type="button"
              onClick={refreshTodayAttendance}
              disabled={attendanceRefreshFeedback === "refreshing"}
              className="rounded-xl border border-cyan-400/25 bg-cyan-400/5 px-3 py-2 text-xs font-bold text-cyan-100 hover:bg-cyan-400/10 disabled:cursor-wait disabled:opacity-70"
            >
              {attendanceRefreshFeedback === "refreshing"
                ? "Refreshing…"
                : attendanceRefreshFeedback === "refreshed"
                ? "✓ Refreshed"
                : "Refresh attendance"}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
          {[
            ["scheduled", "Scheduled", todayIntelligence.scheduled, "Published working shifts", "blue"],
            ["on-time", "On time", todayIntelligence.onTime, todayGraceMinutes ? `Within ${todayGraceMinutes}m grace` : "No grace period", "green"],
            ["late", "Late", todayIntelligence.late, todayGraceMinutes ? `Beyond ${todayGraceMinutes}m grace` : "After scheduled start", "amber"],
            ["no-show", "No-show", todayIntelligence.noShow, "Past start · not clocked in", "rose"],
            ["overtime", "Overtime", todayIntelligence.overtime, "Past scheduled end", "blue"],
            ["unscheduled", "Unscheduled", todayIntelligence.unscheduled, "Clock-in without roster", "amber"],
          ].map(([filterKey, title, value, body, tone]) => {
            return (
            <button
              type="button"
              key={title}
              data-today-filter-card={filterKey}
              aria-pressed="false"
              onClick={() => applyInstantCardFilter("today", filterKey)}
              className="cursor-pointer rounded-2xl border p-4 text-left transition-all duration-150 hover:-translate-y-0.5 hover:border-cyan-300/60 hover:shadow-[0_0_22px_rgba(34,211,238,0.22)]"
              style={card}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="text-[11px] font-bold uppercase tracking-[.12em] text-sky-300">{title}</div>
                <Pill tone={tone}>{value}</Pill>
              </div>
              <div className="mt-3 text-2xl font-black text-white">{value}</div>
              <div className="mt-1 text-[11px]" style={muted}>{body}</div>
            </button>
          );
          })}
        </div>

        <div className="rounded-2xl border overflow-hidden" style={card}>
          <div className="flex flex-wrap items-start justify-between gap-3 border-b border-white/10 p-5">
            <div>
              <div className="text-xs font-bold uppercase tracking-[.14em] text-cyan-300">Live schedule comparison</div>
              <h3 className="mt-1 text-xl font-black text-white">Today’s workforce plan</h3>
              <p className="mt-1 text-sm" style={muted}>
                Published schedule versus actual clock-in/out activity for {allStoresSelected ? "all stores" : (activeStore?.name || "the selected store")}.
              </p>
            </div>
            {todayLoading ? (
              <Pill tone="blue">Loading schedule…</Pill>
            ) : todayError ? (
              <Pill tone="rose">Read error</Pill>
            ) : (
              <Pill tone={todayIntelligence.rows.length ? "green" : "slate"}>
                {todayIntelligence.rows.length ? "Live intelligence" : "No activity yet"}
              </Pill>
            )}
          </div>

          {todayError ? (
            <div className="border-b border-rose-400/15 bg-rose-500/5 p-4 text-sm text-rose-200">{todayError}</div>
          ) : null}

          {!todayLoading && !todayIntelligence.rows.length ? (
            <div className="p-6">
              <div className="rounded-xl border border-dashed border-white/10 p-5 text-center">
                <div className="font-bold text-white">No published working shifts or clock-ins found for today.</div>
                <div className="mt-2 text-sm" style={muted}>
                  Publish today’s roster in Schedule Builder. Neo will then compare it against clock activity automatically.
                </div>
              </div>
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {todayIntelligence.rows.map((row) => (
                <div
                  key={row.id}
                  data-today-filter-row={row.status}
                  data-expects-clock={row.expectsClock ? "true" : "false"}
                  data-arrival-on-time={
                    row.expectsClock &&
                    row.actual?.clockInSec &&
                    Number.isFinite(Number(row.scheduledStartSec || 0)) &&
                    Number(row.scheduledStartSec || 0) > 0 &&
                    row.actual.clockInSec >= Number(row.scheduledStartSec) - (todayGraceMinutes * 60) &&
                    row.actual.clockInSec <= Number(row.scheduledStartSec) + (todayGraceMinutes * 60)
                      ? "true"
                      : "false"
                  }
                  className="grid min-w-0 grid-cols-1 gap-4 p-4 lg:grid-cols-2 lg:items-center xl:grid-cols-[minmax(210px,1.25fr)_minmax(135px,.8fr)_minmax(155px,.9fr)_minmax(180px,1fr)]"
                >
                  <div className="min-w-0">
                    <div className="flex min-w-0 flex-wrap items-center gap-2">
                      <div className="font-black text-white">{row.staffName || "Unknown staff"}</div>
                      <Pill tone={row.tone}>{row.status.replace("-", " ")}</Pill>
                    </div>
                    <div className="mt-1 text-xs text-white/50">
                      {row.roleName || row.actual?.role || "Role not set"}
                      {(row.storeName || row.actual?.storeName) ? ` · ${row.storeName || row.actual?.storeName}` : ""}
                      {row.attendanceLabel ? ` · ${row.attendanceLabel}` : ""}
                    </div>
                  </div>

                  <div className="min-w-0">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-white/35">Scheduled</div>
                    <div className="mt-1 text-sm font-semibold text-white">
                      {row.isAttendance
                        ? row.attendanceLabel
                        : row.off
                        ? "OFF"
                        : row.startTime && row.endTime
                        ? `${row.startTime} – ${row.endTime}`
                        : "Not scheduled"}
                    </div>
                  </div>

                  <div className="min-w-0">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-white/35">Actual</div>
                    <div className="mt-1 text-sm font-semibold text-white">
                      {row.actual
                        ? `${formatClock(row.actual.clockInSec)} → ${row.actual.clockOutSec ? formatClock(row.actual.clockOutSec) : "On shift"}`
                        : row.expectsClock
                        ? "Not clocked in"
                        : "—"}
                    </div>
                  </div>

                  <div className="min-w-0 lg:text-right">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-white/35">Neo intelligence</div>
                    <div className={`mt-1 break-words text-sm font-bold ${
                      row.tone === "green"
                        ? "text-emerald-300"
                        : row.tone === "rose"
                        ? "text-rose-300"
                        : row.tone === "amber"
                        ? "text-amber-300"
                        : row.tone === "blue"
                        ? "text-cyan-300"
                        : "text-white/70"
                    }`}>
                      {row.detail}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    );
  }, [
    view,
    stores,
    staff,
    activeStore,
    builderStoreId,
    publicHolidays,
    attendanceTypes,
    lunchBreakMinutes,
    todayIntelligence,
    todayLoading,
    todayError,
    loadTodayActuals,
    allStoresSelected,
    exceptionIntelligence,
    exceptionPeriod,
    historicalExceptionLoading,
    pendingLeaveRequests,
    resolvedLeaveRequests,
    leaveLoading,
    leaveSyncState,
    leavePeriod,
    decideAdminLeave,
    visibleScheduleHistory,
    historyLoading,
    historyError,
    historyStoreId,
    historyPeriod,
    scheduleCurrentMonthKey,
    nextScheduleMonthKey,
    currentScheduleMonth,
    plannedScheduleMonths,
    previousScheduleMonths,
    staff,
    varianceApprovalState,
    swapDecisionState,
    approveActualShift,
    rejectActualVariance,
    decideShiftSwap,
  ]);

  return (
    <div className="space-y-5">
      <section className="rounded-2xl border p-5 md:p-6" style={card}>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="max-w-3xl">
            <div className="text-xs font-bold uppercase tracking-[.18em] text-cyan-300">Workforce Intelligence</div>
            <h1 className="mt-1 text-3xl font-black text-white">Schedule Intelligence</h1>
            <p className="mt-2 text-sm" style={muted}>
              Plan who should work, compare the roster with actual clock-ins, and give Neo the assignment context it needs to route duties to the correct person.
            </p>
          </div>
        </div>

        <div className="mt-5 flex gap-2 overflow-x-auto pb-1">
          {VIEWS.filter((item) => item.id !== "attendance" || canViewAttendancePhotos).map((item) => {
            const active = item.id === view;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => setView(item.id)}
                className="relative whitespace-nowrap rounded-full border px-4 py-2 text-sm font-semibold transition"
                style={{
                  borderColor:
                    item.id === "leave" && pendingLeaveRequests.length > 0
                      ? "rgba(167,139,250,.72)"
                      : item.id === "exceptions" && exceptionIntelligence.total > 0
                      ? "rgba(245,158,11,.72)"
                      : active
                      ? "rgba(34,211,238,.45)"
                      : "rgba(148,163,184,.18)",
                  background: active ? "linear-gradient(135deg, rgba(34,211,238,.22), rgba(59,130,246,.26))" : "rgba(15,23,42,.48)",
                  color: active ? "#ecfeff" : "rgba(226,232,240,.72)",
                  boxShadow:
                    item.id === "leave" && pendingLeaveRequests.length > 0
                      ? "0 0 18px rgba(167,139,250,.30)"
                      : item.id === "exceptions" && exceptionIntelligence.total > 0
                      ? "0 0 18px rgba(245,158,11,.30)"
                      : active
                      ? "0 0 24px rgba(34,211,238,.10)"
                      : "none",
                }}
              >
                {item.label}
                {item.id === "exceptions" && exceptionIntelligence.total > 0 ? (
                  <span
                    className="ml-2 inline-flex min-w-[20px] items-center justify-center rounded-full border px-1.5 py-0.5 text-[10px] font-black"
                    style={{
                      borderColor: "rgba(251,191,36,.75)",
                      background: "rgba(245,158,11,.20)",
                      color: "#fde68a",
                      boxShadow: "0 0 10px rgba(245,158,11,.45)",
                    }}
                  >
                    {exceptionIntelligence.total}
                  </span>
                ) : null}
                {item.id === "leave" && pendingLeaveRequests.length > 0 ? (
                  <span
                    className="ml-2 inline-flex min-w-[20px] items-center justify-center rounded-full border px-1.5 py-0.5 text-[10px] font-black"
                    style={{
                      borderColor: "rgba(196,181,253,.75)",
                      background: "rgba(139,92,246,.20)",
                      color: "#ddd6fe",
                      boxShadow: "0 0 10px rgba(139,92,246,.45)",
                    }}
                  >
                    {pendingLeaveRequests.length}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      </section>

      {view === "attendance" && canViewAttendancePhotos
        ? <PhotoEvidenceReview orgId={orgId} stores={stores} />
        : view === "attendance" ? null : viewContent}

      {selectedLeaveRequest ? (
        <div
          className="fixed inset-0 z-[145] flex justify-end bg-black/75 backdrop-blur-sm"
          onClick={() => { setSelectedLeaveId(""); setLeaveDecisionNote(""); }}
        >
          <div
            className="h-full w-full overflow-y-auto border-l border-violet-300/15 bg-[#08182c] p-5 shadow-2xl sm:max-w-xl md:p-7"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-xs font-black uppercase tracking-[.16em] text-violet-300">Leave Intelligence</div>
                <h3 className="mt-2 text-3xl font-black text-white">{selectedLeaveRequest.staffName || "Staff member"}</h3>
                <div className="mt-1 text-sm text-white/50">{selectedLeaveRequest.staffRole || "Staff"} · {selectedLeaveRequest.storeName || "Store"}</div>
              </div>
              <button
                type="button"
                onClick={() => { setSelectedLeaveId(""); setLeaveDecisionNote(""); }}
                className="h-11 w-11 rounded-xl border border-white/10 text-xl font-black text-white hover:bg-white/5"
              >
                ×
              </button>
            </div>

            <div className="mt-6 rounded-2xl border border-violet-300/20 bg-violet-400/5 p-5">
              <div className="text-xs font-bold uppercase tracking-wider text-violet-300">Leave request</div>
              <div className="mt-2 text-2xl font-black text-violet-100">{leaveTypeLabel(selectedLeaveRequest.leaveType)}</div>
              <div className="mt-2 font-bold text-white">{selectedLeaveRequest.startDate || "—"}{selectedLeaveRequest.endDate && selectedLeaveRequest.endDate !== selectedLeaveRequest.startDate ? ` → ${selectedLeaveRequest.endDate}` : ""}</div>
            </div>

            <div className="mt-4 rounded-2xl border border-white/10 bg-slate-950/25 p-5">
              <div className="text-xs font-bold uppercase tracking-wider text-white/45">Staff note</div>
              <div className="mt-2 text-sm leading-6 text-slate-200">{selectedLeaveRequest.reason || "No reason supplied."}</div>
            </div>

            <div className="mt-4 rounded-2xl border border-white/10 bg-slate-950/25 p-5">
              <div className="text-xs font-bold uppercase tracking-wider text-white/45">Supporting evidence</div>
              {selectedLeaveRequest.evidence?.url ? (
                <>
                  <a href={selectedLeaveRequest.evidence.url} target="_blank" rel="noreferrer" className="mt-3 inline-flex font-black text-cyan-300 hover:text-cyan-200">
                    Open evidence ↗
                  </a>
                  <div className="mt-1 text-xs text-white/45">{selectedLeaveRequest.evidence.name || "Supporting document"}</div>
                </>
              ) : (
                <div className="mt-2 text-sm text-white/50">No supporting document attached.</div>
              )}
            </div>

            {String(selectedLeaveRequest.status || "pending").toLowerCase() === "pending" ? (
              <>
                {leaveDecisionState[selectedLeaveRequest.id] && leaveDecisionState[selectedLeaveRequest.id] !== "saving" && !["approved", "rejected"].includes(leaveDecisionState[selectedLeaveRequest.id]) ? (
                  <div className="mt-5 rounded-xl border border-amber-400/30 bg-amber-400/10 p-4 text-sm leading-6 text-amber-100">
                    <div className="font-black">Leave cannot be approved yet</div>
                    <div className="mt-1">{leaveDecisionState[selectedLeaveRequest.id]}</div>
                  </div>
                ) : null}
                <label className="mt-5 block">
                  <span className="text-xs font-bold text-white/60">Manager note (optional)</span>
                  <textarea
                    value={leaveDecisionNote}
                    onChange={(e) => setLeaveDecisionNote(e.target.value)}
                    placeholder="Add a note for the staff member"
                    className="mt-2 min-h-[100px] w-full resize-y rounded-xl border border-white/10 bg-slate-950/60 px-3 py-3 text-sm text-white outline-none focus:border-violet-300/35"
                  />
                </label>
                <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <button
                    type="button"
                    disabled={leaveDecisionState[selectedLeaveRequest.id] === "saving"}
                    onClick={() => decideAdminLeave(selectedLeaveRequest, true)}
                    className="rounded-xl border border-emerald-400/30 bg-emerald-400/10 px-4 py-3 font-black text-emerald-200 hover:bg-emerald-400/15 disabled:opacity-50"
                  >
                    {leaveDecisionState[selectedLeaveRequest.id] === "saving" ? "Saving…" : "Approve leave"}
                  </button>
                  <button
                    type="button"
                    disabled={leaveDecisionState[selectedLeaveRequest.id] === "saving"}
                    onClick={() => decideAdminLeave(selectedLeaveRequest, false)}
                    className="rounded-xl border border-rose-400/30 bg-rose-400/10 px-4 py-3 font-black text-rose-200 hover:bg-rose-400/15 disabled:opacity-50"
                  >
                    {leaveDecisionState[selectedLeaveRequest.id] === "saving" ? "Saving…" : "Reject leave"}
                  </button>
                </div>
              </>
            ) : (
              <div className="mt-5 rounded-2xl border border-white/10 bg-slate-950/25 p-5">
                <div className="text-xs font-bold uppercase tracking-wider text-white/45">Decision</div>
                <div className={`mt-2 text-xl font-black ${String(selectedLeaveRequest.status).toLowerCase() === "approved" ? "text-emerald-300" : "text-rose-300"}`}>
                  {String(selectedLeaveRequest.status).toLowerCase() === "approved" ? "Approved" : "Rejected"}
                </div>
                {selectedLeaveRequest.managerNote ? <div className="mt-3 text-sm leading-6 text-slate-200">{selectedLeaveRequest.managerNote}</div> : null}
                {String(selectedLeaveRequest.status).toLowerCase() === "approved" ? (
                  <div className="mt-3 text-xs font-bold text-cyan-300">
                    {selectedLeaveRequest.scheduleSyncStatus === "synced" ? "✓ Schedule synced" : "Schedule sync pending…"}
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </div>
      ) : null}

      {swapConfirm ? (
        <div className="fixed inset-0 z-[130] flex items-center justify-center bg-black/75 p-4 backdrop-blur-sm">
          <div className="w-full max-w-lg rounded-2xl border border-cyan-400/20 bg-[#0d223d] p-5 shadow-2xl">
            <div className="text-xs font-bold uppercase tracking-[.16em] text-cyan-300">Confirm shift swap decision</div>
            <h3 className="mt-2 text-2xl font-black text-white">
              {swapConfirm.approved ? "Approve replacement shift?" : "Reject replacement shift?"}
            </h3>
            <p className="mt-3 text-sm leading-6 text-slate-300">
              <span className="font-bold text-white">{swapConfirm.item?.missingRow?.staffName || "Scheduled staff"}</span>{" "}
              was expected to work, but{" "}
              <span className="font-bold text-white">{swapConfirm.item?.row?.staffName || "replacement staff"}</span>{" "}
              clocked in instead.
            </p>
            <div className={`mt-4 rounded-xl border p-4 text-sm leading-6 ${
              swapConfirm.approved
                ? "border-emerald-400/30 bg-emerald-400/10 text-emerald-100"
                : "border-rose-400/30 bg-rose-400/10 text-rose-100"
            }`}>
              {swapConfirm.approved
                ? "This will mark the shift as Approved swap in Labor. Finalized replacement hours can flow to Payroll after clock-out."
                : "This will mark the shift as Swap rejected in Labor and allocate zero payable hours to the replacement shift."}
            </div>
            <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button type="button" onClick={() => setSwapConfirm(null)}
                className="rounded-xl border border-white/10 bg-slate-950/50 px-4 py-2.5 text-sm font-bold text-slate-200 hover:bg-slate-900">
                Cancel
              </button>
              <button
                type="button"
                onClick={async () => {
                  const pending = swapConfirm;
                  setSwapConfirm(null);
                  await decideShiftSwap(pending.item, pending.approved);
                }}
                className={`rounded-xl border px-4 py-2.5 text-sm font-black ${
                  swapConfirm.approved
                    ? "border-emerald-400/35 bg-emerald-500/15 text-emerald-100 hover:bg-emerald-500/20"
                    : "border-rose-400/35 bg-rose-500/15 text-rose-100 hover:bg-rose-500/20"
                }`}
              >
                {swapConfirm.approved ? "Yes, approve swap" : "Yes, reject swap"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {varianceConfirm ? (
        <div className="fixed inset-0 z-[131] flex items-center justify-center bg-black/75 p-4 backdrop-blur-sm">
          <div className="w-full max-w-lg rounded-2xl border border-cyan-400/20 bg-[#0d223d] p-5 shadow-2xl">
            <div className="text-xs font-bold uppercase tracking-[.16em] text-cyan-300">Confirm shift decision</div>
            <h3 className="mt-2 text-2xl font-black text-white">
              {varianceConfirm.item?.type === "unscheduled"
                ? varianceConfirm.approved ? "Approve unscheduled shift?" : "Reject unscheduled shift?"
                : varianceConfirm.approved ? "Approve overtime?" : "Reject overtime?"}
            </h3>
            <p className="mt-3 text-sm leading-6 text-slate-300">
              <span className="font-bold text-white">{varianceConfirm.item?.row?.staffName || "Staff member"}</span>{" "}
              {varianceConfirm.item?.type === "unscheduled"
                ? "worked without a published shift. The manager is deciding whether this actual shift is accepted for Labor and Payroll."
                : <>
                    worked outside the published shift.
                    {varianceConfirm.item?.autoClockedOut || varianceConfirm.item?.row?.autoClockedOut
                      ? " Neo auto clocked this shift out, so the manager is deciding whether the outside-schedule time is payable."
                      : " The manager is deciding whether the outside-schedule time is payable."}
                  </>}
            </p>
            <div className={`mt-4 rounded-xl border p-4 text-sm leading-6 ${
              varianceConfirm.approved
                ? "border-emerald-400/30 bg-emerald-400/10 text-emerald-100"
                : "border-rose-400/30 bg-rose-400/10 text-rose-100"
            }`}>
              {varianceConfirm.item?.type === "unscheduled"
                ? varianceConfirm.approved
                  ? "The full concluded unscheduled shift will become an approved actual shift in Labor and can flow to Payroll."
                  : "The unscheduled shift will remain recorded as clock evidence but will carry zero accepted/payable hours."
                : varianceConfirm.approved
                ? "Approved outside-schedule time will move from Pending OT to payable Overtime in Labor and Payroll."
                : "Rejected outside-schedule time will remain recorded as actual clock evidence but will not become payable overtime."}
            </div>
            <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button type="button" onClick={() => setVarianceConfirm(null)}
                className="rounded-xl border border-white/10 bg-slate-950/50 px-4 py-2.5 text-sm font-bold text-slate-200 hover:bg-slate-900">
                Cancel
              </button>
              <button
                type="button"
                onClick={async () => {
                  const pending = varianceConfirm;
                  setVarianceConfirm(null);
                  if (pending.approved) await approveActualShift(pending.item);
                  else await rejectActualVariance(pending.item);
                }}
                className={`rounded-xl border px-4 py-2.5 text-sm font-black ${
                  varianceConfirm.approved
                    ? "border-emerald-400/35 bg-emerald-500/15 text-emerald-100 hover:bg-emerald-500/20"
                    : "border-rose-400/35 bg-rose-500/15 text-rose-100 hover:bg-rose-500/20"
                }`}
              >
                {varianceConfirm.item?.type === "unscheduled"
                  ? varianceConfirm.approved ? "Yes, approve shift" : "Yes, reject shift"
                  : varianceConfirm.approved ? "Yes, approve overtime" : "Yes, reject overtime"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {newScheduleOpen ? (
        <div className="fixed inset-0 z-[3250] flex items-center justify-center bg-black/75 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl border border-emerald-400/25 bg-[#0b1b30] p-5 shadow-2xl">
            <div className="text-xs font-bold uppercase tracking-[.16em] text-emerald-300">New schedule</div>
            <h3 className="mt-2 text-2xl font-black text-white">Choose a future month</h3>
            <p className="mt-2 text-sm leading-6 text-white/60">The current month cannot be created as a new schedule. Choose next month or any later month and start building immediately.</p>
            <label className="mt-5 block">
              <span className="text-xs font-bold uppercase tracking-wider text-white/50">Schedule month</span>
              <input
                type="month"
                min={nextScheduleMonthKey}
                value={newScheduleMonthKey}
                onChange={(e) => setNewScheduleMonthKey(e.target.value)}
                className="mt-2 w-full rounded-xl border border-white/10 bg-slate-950/80 px-3 py-3 text-sm text-white"
                style={{ colorScheme: "dark" }}
              />
            </label>
            {plannedScheduleMonths.some((row) => row.monthKey === newScheduleMonthKey) ? (
              <div className="mt-3 rounded-xl border border-cyan-400/20 bg-cyan-400/5 p-3 text-xs text-cyan-100">A schedule already exists for this month. Continue will open the existing {plannedScheduleMonths.find((row) => row.monthKey === newScheduleMonthKey)?.status || "draft"} schedule.</div>
            ) : null}
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setNewScheduleOpen(false)} className="rounded-xl border border-white/10 px-4 py-2.5 text-sm font-bold text-white">Cancel</button>
              <button
                type="button"
                disabled={!newScheduleMonthKey || newScheduleMonthKey <= scheduleCurrentMonthKey}
                onClick={startNewSchedule}
                className="rounded-xl border border-emerald-400/35 bg-emerald-500/15 px-4 py-2.5 text-sm font-black text-emerald-100 disabled:opacity-35"
              >
                {plannedScheduleMonths.some((row) => row.monthKey === newScheduleMonthKey) ? "Open Schedule" : "Start Building"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <ScheduleBuilderModal
        open={builderOpen}
        onClose={() => setBuilderOpen(false)}
        stores={stores}
        staff={staff}
        roles={roles}
        publicHolidays={publicHolidays}
        attendanceTypes={attendanceTypes}
        lunchBreakMinutes={lunchBreakMinutes}
        initialStoreId={builderStoreId}
        initialMonthKey={builderMonthKey}
        readOnly={builderReadOnly}
        orgId={orgId}
        actor={actor}
        onSchedulePublished={() =>
          setHistoricalExceptionRefreshKey((current) => current + 1)
        }
      />
    </div>
  );
}
